import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import React from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useMoneyFmt, useDateFmt } from "@/lib/company";
import { useCompany } from "@/lib/company";
import { useLabV3ReviewCount, LAB_V3_REVIEW_ROUTE } from "@/lib/labV3Review";
import { Link } from "react-router-dom";
import { useAiFocus } from "@/lib/aiFocus";
import { useIsMobile } from "@/lib/useIsMobile";
import MobileTxnCards from "@/components/MobileTxnCards";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Check, Wand2, Split, Link as LinkIcon, RotateCw, Plus, X, Trash2, AlertTriangle, ShieldCheck,
  ChevronLeft, ChevronRight, Search, Calendar, XCircle, Tag, Sparkles, MoreHorizontal,
  List as ListIcon, LayoutGrid, ArrowLeftRight, HelpCircle, Pencil, User as UserIcon,
  SlidersHorizontal, Paperclip, FileText, Loader2, Eye, MessageSquareWarning, Lightbulb, PartyPopper, Link2, Camera,
} from "lucide-react";
import ChatReviewTour from "@/components/tour/ChatReviewTour";
import { buildSampleTxns, filterSample, sampleProgress, SAMPLE_INVOICES, SAMPLE_BILLS } from "@/tours/transactionsSample";
import { TXN_BEATS, TXN_CHAPTERS } from "@/tours/transactionsBeats";
import ReclassifyPicker from "@/components/ReclassifyPicker";
import ContactPickerModal from "@/components/ContactPickerModal";
import BulkConfirmModal from "@/components/BulkConfirmModal";
import BulkUpdateModal from "@/components/BulkUpdateModal";
import { SimilarApproveModal } from "@/components/SimilarApproveModal";
import ContactPicker from "@/components/ContactPicker";
import { CreateRuleModal } from "@/pages/Rules";
import CleanupCopilot, { NextStepCard } from "@/components/CleanupCopilot";
import AccountPicker from "@/components/AccountPicker";
import SearchableAccountPicker from "@/components/SearchableAccountPicker";
import { MatchDot } from "@/components/MatchDot";
import MonthCloseBreadcrumb from "@/components/MonthCloseBreadcrumb";
import AskClientButton from "@/components/AskClientButton";
import AskAccountantModal from "@/components/AskAccountantModal";
import { canUseCockpit } from "@/lib/cockpitAccess";
import { AccountInfoTooltip } from "@/components/AccountInfoTooltip";
import { ProvenanceDot } from "@/components/ProvenanceDot";
import { ContactBadge } from "@/components/ContactBadge";
import { emitAction, useActionListener } from "@/lib/createBus";
import { useLetsReviewNav } from "@/pages/LetsReview";
import { useNoContactReviewNav, NoContactReviewListToggle, ListModeView } from "@/pages/NoContactReview";
import Step2Tour, { hasSeenStep2Tour } from "@/components/Step2Tour";
import Step3BTour, { hasSeenStep3BTour } from "@/components/Step3BTour";
import ReceiptMatchPicker from "@/components/ReceiptMatchPicker";
import { useAuth } from "@/lib/auth";
import { useFeature } from "@/lib/entitlements";

const UNCAT_CODES = new Set(["9999", "6999", "4999"]);
const isUncategorizedTxn = (t) =>
  !t?.category_account_id || UNCAT_CODES.has(String(t.category_account_code || "")) || /uncategorized/i.test(t?.category_account_name || "");

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

/** "New" dropdown that surfaces both the quick-modal manual entry
 * (kept for one-off Plaid-style categorization) and the five full-
 * page editors (Expense, Sales Receipt, Deposit, Credit Memo, Refund
 * Receipt). In Simple accounting mode, only the Quick manual entry
 * is shown — regular business owners don't need the QBO-shaped
 * editors and a shorter menu is less overwhelming. */
/**
 * LinkedDocChip — rich clickable pill that replaces the flat
 * "Linked to invoice" text. Shows `INV-1001 · $110 · partial` when the
 * txn's amount is less than the invoice total, or `INV-1001 · $110`
 * when fully paid. Clicking opens LinkedDocPreview (a lightweight
 * in-place modal) so pros stay in the transactions view.
 *
 * Backend serializes the four fields we render (see
 * routes/transactions.py::list_transactions Mar 1 2026 enrichment).
 */
function LinkedDocChip({ t, onOpen }) {
  const isInvoice = !!t.linked_invoice_id;
  const num = isInvoice ? t.linked_invoice_number : t.linked_bill_number;
  const total = isInvoice ? t.linked_invoice_total : t.linked_bill_total;
  const status = isInvoice ? t.linked_invoice_status : t.linked_bill_status;
  // Multi-app payment metadata (set by backend enrichment when this
  // txn's linked payment applies to more than one invoice — Mar 2026).
  const multiCount = Number(t.linked_applications_count || 0);
  const multiTotal = Number(t.linked_applications_total || 0);
  const txnAmt = Math.abs(Number(t.amount || 0));
  const docTotal = Number(total || 0);
  const isPartial = docTotal > 0.01 && txnAmt + 0.01 < docTotal;
  if (multiCount > 1) {
    // "2 invoices · $500" (deposit) or "2 bills · $500" (withdrawal).
    // Clicking opens the preview modal, which renders the apps list.
    const noun = isInvoice ? "invoice" : "bill";
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpen({ kind: "multi", subKind: noun, id: t.linked_payment_id, txn: t }); }}
        className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-md
                    text-[10px] font-medium border transition-colors
                    ${isInvoice
                      ? "text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
                      : "text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100"}`}
        data-testid={`txn-linked-chip-${t.id}`}
        title={`Click to see ${multiCount} applications`}
      >
        <span>{multiCount} {noun}{multiCount > 1 ? "s" : ""} · ${multiTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </button>
    );
  }
  if (!num) {
    return (
      <div className="text-[10px] text-slate-400 mt-0.5 italic">
        Linked to a deleted {isInvoice ? "invoice" : "bill"}
      </div>
    );
  }
  const label = docTotal > 0
    ? `${num} · $${docTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${isPartial ? " · partial" : ""}`
    : num;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen({ kind: isInvoice ? "invoice" : "bill", id: t.linked_invoice_id || t.linked_bill_id, txn: t }); }}
      className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-md
                    text-[10px] font-medium border transition-colors
                    ${isInvoice
                      ? "text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
                      : "text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100"}`}
      data-testid={`txn-linked-chip-${t.id}`}
      title={`Click to preview ${isInvoice ? "invoice" : "bill"} ${num}${status ? " · " + status : ""}`}
    >
      <span>{label}</span>
    </button>
  );
}


/**
 * LinkedDocPreview — modal that fetches + renders the linked invoice
 * or bill inline. Read-only. "Open full editor" button escapes to
 * /invoices/{id} / /bills/{id} when the pro needs to make changes.
 */
function LinkedDocPreview({ preview, onClose, currentId }) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!preview) return;
    let cancelled = false;
    setLoading(true);
    // "multi" kind fetches the payment itself, so we can render the
    // applications list (Mar 2026 multi-invoice Receive Payment).
    const url = preview.kind === "invoice"
      ? `/companies/${currentId}/invoices/${preview.id}`
      : preview.kind === "bill"
      ? `/companies/${currentId}/bills/${preview.id}`
      : `/companies/${currentId}/payments/${preview.id}`;
    api.get(url)
      .then(r => { if (!cancelled) setDoc(r.data[preview.kind] || r.data.payment || r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [preview, currentId]);
  if (!preview) return null;
  const isInvoice = preview.kind === "invoice";
  const isMulti = preview.kind === "multi";
  const num = doc?.number || "";

  // Multi-app view — render the applications table.
  if (isMulti) {
    const apps = doc?.applications || [];
    const subKind = preview.subKind || (apps[0]?.bill_id ? "bill" : "invoice");
    const isMultiInvoice = subKind === "invoice";
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={onClose} data-testid="linked-doc-preview">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}>
          <div className={`flex items-center justify-between px-5 py-3 border-b ${
            isMultiInvoice ? "bg-emerald-50" : "bg-amber-50"
          }`}>
            <div>
              <div className={`text-[10px] uppercase tracking-wider ${
                isMultiInvoice ? "text-emerald-700" : "text-amber-700"
              }`}>
                {isMultiInvoice ? "Multi-invoice Receive Payment" : "Multi-bill Payment"}
              </div>
              <h3 className="font-heading font-semibold text-lg">
                {doc ? `${apps.length} ${subKind}${apps.length > 1 ? "s" : ""} · $${Number(doc.amount || 0).toFixed(2)}` : "Loading…"}
              </h3>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"
                     data-testid="linked-doc-preview-close">×</button>
          </div>
          <div className="flex-1 overflow-auto p-5 text-sm space-y-3">
            {loading && <div className="text-slate-400 text-center py-10">Loading…</div>}
            {!loading && doc && (
              <>
                <div className="grid grid-cols-3 gap-3 pb-3 border-b">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">{isMultiInvoice ? "Customer" : "Vendor"}</div>
                    <div className="font-medium">{doc.contact_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Date</div>
                    <div>{doc.date}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Total</div>
                    <div className="font-mono tabular-nums">${Number(doc.amount || 0).toFixed(2)}</div>
                  </div>
                </div>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-1.5">{isMultiInvoice ? "Invoice" : "Bill"}</th>
                        <th className="text-right px-3 py-1.5">Applied</th>
                        <th className="text-right px-3 py-1.5">New Balance</th>
                        <th className="w-24"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {apps.map((a, i) => {
                        const docId = isMultiInvoice ? a.invoice_id : a.bill_id;
                        const docNum = (isMultiInvoice ? a.invoice_number : a.bill_number)
                                        || docId?.slice(0, 8);
                        return (
                          <tr key={i}>
                            <td className="px-3 py-1.5 font-mono">{docNum}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">${Number(a.amount || 0).toFixed(2)}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-600">${Number(a.new_balance_due || 0).toFixed(2)}</td>
                            <td className="px-3 py-1.5">
                              <button
                                onClick={() => navigate(isMultiInvoice ? `/invoices/${docId}/edit` : `/bills/${docId}/edit`)}
                                className="text-xs text-indigo-600 hover:underline">Open →</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {doc.memo && (
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Memo</div>
                    <div className="text-slate-700">{doc.memo}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={onClose} data-testid="linked-doc-preview">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {isInvoice ? "Invoice" : "Bill"}
            </div>
            <h3 className="font-heading font-semibold text-lg">
              {num || "Loading…"}
              {doc?.status && (
                <span className="ml-2 text-xs font-normal text-slate-500">
                  · {doc.status}
                </span>
              )}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(isInvoice ? `/invoices/${preview.id}/edit` : `/bills/${preview.id}/edit`)}
              className="text-sm text-indigo-600 hover:underline"
              data-testid="linked-doc-preview-open-full"
            >Open full editor →</button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"
                     data-testid="linked-doc-preview-close">×</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-5 text-sm">
          {loading && <div className="text-slate-400 text-center py-10">Loading…</div>}
          {!loading && !doc && <div className="text-slate-400 text-center py-10">Not found.</div>}
          {!loading && doc && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase text-slate-500">{isInvoice ? "Customer" : "Vendor"}</div>
                  <div className="font-medium">{doc.contact_name || doc.vendor_name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Date</div>
                  <div>{doc.issue_date || doc.date || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Total</div>
                  <div className="font-mono tabular-nums">${Number(doc.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Balance due</div>
                  <div className="font-mono tabular-nums">${Number(doc.balance_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </div>
              </div>
              {(doc.line_items || []).length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-1.5">Description</th>
                        <th className="text-right px-3 py-1.5">Qty</th>
                        <th className="text-right px-3 py-1.5">Rate</th>
                        <th className="text-right px-3 py-1.5">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {doc.line_items.map((l, i) => (
                        <tr key={i}>
                          <td className="px-3 py-1.5">{l.description || l.item_name || "—"}</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">{l.quantity}</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">${Number(l.rate || 0).toFixed(2)}</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">${Number(l.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {doc.notes && (
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Notes</div>
                  <div className="text-slate-700 whitespace-pre-wrap">{doc.notes}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function NewTransactionMenu({ onQuick, advanced }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const items = [
    { label: "Quick manual entry",  desc: "Single-line categorization",
      onClick: onQuick,
      testId: TID.txnAddBtn },
    ...(advanced ? [
      { divider: true, label: "QBO-shaped entries" },
      { label: "Expense",         desc: "Pay a vendor from a bank account",
        onClick: () => navigate("/purchases/new"),
        testId: "txn-new-purchase" },
      { label: "Sales Receipt",   desc: "Customer paid at time of sale",
        onClick: () => navigate("/sales-receipts/new"),
        testId: "txn-new-sales-receipt" },
      { label: "Bank Deposit",    desc: "Inflow with no customer (interest, rebates, owner)",
        onClick: () => navigate("/deposits/new"),
        testId: "txn-new-deposit" },
      { label: "Credit Memo",     desc: "Reduce A/R without a cash refund",
        onClick: () => navigate("/credit-memos/new"),
        testId: "txn-new-credit-memo" },
      { label: "Refund Receipt",  desc: "Cash refund back to a customer",
        onClick: () => navigate("/refund-receipts/new"),
        testId: "txn-new-refund-receipt" },
    ] : []),
  ];
  // Simple mode: single item, no dropdown needed.
  if (!advanced) {
    return (
      <button
        data-testid={TID.txnAddBtn}
        onClick={onQuick}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs"
      >
        <Plus size={13} /> Manual Transaction
      </button>
    );
  }
  return (
    <div ref={rootRef} className="relative">
      <button
        data-testid="txn-new-menu-btn"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs"
      >
        <Plus size={13} /> New transaction
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-lg z-30 py-1"
          data-testid="txn-new-menu"
        >
          {items.map((it, i) => it.divider ? (
            <div key={i} className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400 border-t border-slate-100 mt-1 first:border-t-0 first:mt-0">
              {it.label}
            </div>
          ) : (
            <button
              key={i}
              onClick={() => { setOpen(false); it.onClick && it.onClick(); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex flex-col gap-0.5"
              data-testid={it.testId}
            >
              <span className="text-sm font-medium text-slate-800">{it.label}</span>
              <span className="text-[11px] text-slate-500">{it.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// Per-row "More" dropdown for the actions we don't want cluttering the row:
// AI re-categorize, Split, and Link-to-invoice/bill. Opens on click, closes
// on outside click or Escape. Renders into `document.body` via a portal
// with `position: fixed` coords derived from the trigger's client rect —
// this way the menu escapes any parent `overflow: hidden` clipping,
// including the transactions container that used to swallow it when
// there were only a few rows. Auto-flips up when there's less than
// 200px of headroom below the trigger. Mar 2026.
// Card-style renderer used when the transactions wrapper is too narrow
// (AI panel open on a laptop, etc.) to fit the full 8-column table on
// one row without forcing a horizontal scrollbar. Each row becomes a
// stacked card with label · value pairs. Labels sit to the LEFT of
// their data when there's room; when the value would overflow the
// remaining width, flex-wrap drops the value onto the next line.
function NarrowFieldRow({ label, children, align = "left" }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mt-1">
      <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold w-32 shrink-0">
        {label}
      </span>
      <div
        className={`min-w-0 ${align === "right" ? "ml-auto text-right" : "flex-1"}`}
      >
        {children}
      </div>
    </div>
  );
}

function ReceiptPopup({ cid, rid, txn, onClose, onChanged, onSnapNew }) {
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [cands, setCands] = useState(null);
  const [pane, setPane] = useState("receipt");
  const suggested = txn?.receipt_match_status === "suggested";
  const isSample = String(rid || "").startsWith("sample-");
  const act = async (fn, okMsg) => {
    if (isSample) { toast.message("Sample data — nothing is saved", { duration: 1800 }); onClose(); return; }
    setBusy(true);
    try { await fn(); toast.success(okMsg); onChanged?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(false); }
  };
  const verify = () => act(() => api.post(`/companies/${cid}/transactions/${txn.id}/receipt/verify`), "Match verified");
  const unlink = () => act(() => api.post(`/companies/${cid}/transactions/${txn.id}/receipt/unlink`), "Receipt unlinked — transaction is back to uncategorized");
  const rematch = (receiptId) => act(() => api.post(`/companies/${cid}/transactions/${txn.id}/receipt/rematch`, { transaction_id: receiptId }), "Receipt swapped");
  const openPicker = async () => {
    setPicking(true);
    try { const res = await api.get(`/companies/${cid}/transactions/${txn.id}/receipt-candidates`); setCands(res.data?.candidates || res.data || []); }
    catch { setCands([]); }
  };
  useEffect(() => {
    if (!rid) { setErr(true); return; }
    if (isSample) {
      const acct = { account_code: txn?.category_account_code, account_name: txn?.category_account_name };
      setR({ merchant: txn?.merchant || "The Home Depot", date: txn?.date, amount: Math.abs(Number(txn?.amount || 0)), attachment_filename: "sample-receipt.png", attachment_data_url: "/sample-receipt.png",
             notes: "Sample receipt — on your real data the photo, line items and categories show here.",
             line_items: [{ description: "4x4x8 PT POST", amount: 119.88, ...acct }, { description: "QUIKRETE 80LB CONCRETE", amount: 69.8, ...acct }, { description: "CYPRESS MULCH 2CF", amount: 59.76, ...acct }] });
      return;
    }
    api.get(`/companies/${cid}/receipts/${rid}`).then((res) => setR(res.data)).catch(() => setErr(true));
  }, [cid, rid, isSample]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const url = r?.attachment_data_url;
  const isPdf = url?.startsWith("data:application/pdf");
  const money = (n) => `$${Math.abs(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onMouseDown={onClose} data-testid="receipt-popup">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="receipt-popup-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-50 text-orange-600 border border-orange-200"><Paperclip size={12} /></span>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate">{r?.merchant || r?.contact_name || txn?.merchant || txn?.description || "Receipt"}</div>
              <div className="text-[11px] text-slate-500">
                {r?.date || txn?.date} · {money(r?.amount ?? txn?.amount)}{r?.attachment_filename ? ` · ${r.attachment_filename}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {suggested ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] px-2 py-0.5" data-testid="receipt-popup-status">Approximate match — verify</span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] px-2 py-0.5" data-testid="receipt-popup-status"><Check size={11} /> Verified</span>
            )}
            <Link to="/receipts" className="text-xs text-indigo-700 hover:underline" data-testid="receipt-popup-open-page">Open Receipts</Link>
            <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100" aria-label="Close" data-testid="receipt-popup-close"><X size={16} /></button>
          </div>
        </div>
        <div className="md:hidden flex items-center justify-center px-4 py-2 border-b bg-slate-50" data-testid="receipt-popup-pane-toggle">
          <div className="inline-flex rounded-full border border-slate-300 bg-white p-0.5 text-xs font-medium">
            {[["receipt", "Receipt"], ["categories", "Categories"]].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setPane(k)}
                className={`px-4 py-1.5 rounded-full transition-colors ${pane === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                aria-pressed={pane === k} data-testid={`receipt-popup-pane-${k}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-[1fr_300px] gap-0 overflow-hidden flex-1 min-h-0">
          <div className={`bg-slate-100 items-center justify-center overflow-auto min-h-[320px] ${pane === "receipt" ? "flex" : "hidden md:flex"}`} data-testid="receipt-popup-image-pane">
            {err ? (
              <div className="text-sm text-slate-500 p-6">Couldn't load this receipt.</div>
            ) : !r ? (
              <Loader2 className="animate-spin text-slate-400" size={20} />
            ) : !url ? (
              <div className="text-sm text-slate-500 p-6">No image on this receipt.</div>
            ) : isPdf ? (
              <iframe title="Receipt" src={url} className="w-full h-[70vh]" />
            ) : (
              <img src={url} alt="Receipt" className="max-w-full max-h-[70vh] object-contain" data-testid="receipt-popup-image" />
            )}
          </div>
          <div className={`md:border-l overflow-y-auto p-4 text-xs ${pane === "categories" ? "block" : "hidden md:block"}`} data-testid="receipt-popup-categories-pane">
            {r?.ai_narrative && <p className="text-slate-600 mb-3">{r.ai_narrative}</p>}
            {(r?.line_items || []).length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Line items</div>
                <div className="divide-y divide-slate-100">
                  {r.line_items.map((l, i) => (
                    <div key={i} className="py-1.5 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-slate-800 truncate">{l.description}</div>
                        <div className="text-[10px] text-slate-400 truncate">{l.account_code ? `${l.account_code} · ` : ""}{l.account_name}</div>
                      </div>
                      <div className="tabular-nums text-slate-900 shrink-0">{money(l.amount)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {r?.notes && <div className="mt-3 text-slate-600"><span className="text-slate-400">Note:</span> {r.notes}</div>}
          </div>
        </div>
        <div className="border-t px-4 py-3 flex flex-wrap items-center gap-2 bg-slate-50" data-testid="receipt-popup-actions">
          {suggested && (
            <button type="button" disabled={busy} onClick={verify} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-2 hover:bg-emerald-700 disabled:opacity-50" data-testid="receipt-popup-verify">
              <Check size={13} /> Looks right
            </button>
          )}
          <button type="button" disabled={busy} onClick={unlink} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-700 px-3 py-2 hover:bg-slate-100 disabled:opacity-50" data-testid="receipt-popup-unlink">
            <X size={13} /> Unlink
          </button>
          <button type="button" disabled={busy} onClick={openPicker} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-700 px-3 py-2 hover:bg-slate-100 disabled:opacity-50" data-testid="receipt-popup-rematch">
            <Paperclip size={13} /> Match a different receipt
          </button>
          <button type="button" disabled={busy} onClick={onSnapNew} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-700 px-3 py-2 hover:bg-slate-100 disabled:opacity-50" data-testid="receipt-popup-snap">
            <Camera size={13} /> Snap a new receipt
          </button>
        </div>
        {picking && (
          <div className="border-t px-4 py-3 max-h-56 overflow-y-auto" data-testid="receipt-popup-picker">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Unmatched receipts on file</div>
            {cands === null ? (
              <Loader2 size={14} className="animate-spin text-slate-400" />
            ) : cands.length === 0 ? (
              <div className="text-xs text-slate-500">No unmatched receipts on file — snap a new one instead.</div>
            ) : cands.map((c) => {
              const rec = { ...(c.receipt || c), id: c.receipt_id || (c.receipt || c).id };
              return (
                <button key={rec.id} type="button" disabled={busy} onClick={() => rematch(rec.id)} className="w-full text-left rounded-md border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 px-3 py-2 mb-1.5 flex items-center gap-3 text-xs" data-testid={`receipt-popup-cand-${rec.id}`}>
                  <span className="font-mono text-slate-500 w-20 shrink-0">{rec.date}</span>
                  <span className="flex-1 min-w-0 truncate text-slate-800">{rec.merchant || rec.vendor || rec.contact_name || "Receipt"}</span>
                  <span className="tabular-nums font-medium">{money(rec.amount)}</span>
                  {c.score != null && <span className="text-[10px] text-slate-400">{c.score}%</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function NarrowTxnCardList({
  txns, accts, currentId,
  selected, allChecked, setSelected, toggleSel,
  setFocus, emitAction, fmtDate, fmtMoney,
  toggleApprove, setEditing, recategorize, setSplitting, setLinking,
  del, askClientRef, updateCategory, showProvenance, setLinkedDocPreview,
  onViewReceipt,
}) {
  return (
    <div data-testid="txn-cards-narrow">
      <div className="flex items-center gap-2 px-3 py-2 text-xs uppercase text-slate-500 border-b bg-slate-50">
        <input
          type="checkbox"
          data-testid={TID.txnBulkCheckbox}
          checked={allChecked}
          onChange={(e) => setSelected(e.target.checked ? new Set(txns.map(t => t.id)) : new Set())}
        />
        <span>Transactions</span>
      </div>
      {!txns.length && (
        <div className="px-3 py-8 text-center text-slate-500 text-sm">No transactions.</div>
      )}
      {txns.map(t => (
        <div
          key={t.id}
          data-testid={TID.txnRow}
          data-txn-id={t.id}
          onMouseEnter={() => setFocus({ id: t.id, merchant: t.merchant, amount: t.amount, date: t.date })}
          onMouseLeave={() => setFocus(null)}
          onClick={(e) => {
            // Open the edit modal on row click, but only when the
            // click didn't land on an interactive control (checkbox,
            // action buttons, category picker, split pill, linked-doc
            // chip, or anything inside the more-menu). That way the
            // row itself feels tappable while inline actions still
            // work as expected.
            if (e.target.closest("button, input, select, textarea, a, [role='menu'], [role='menuitem']")) return;
            setEditing(t);
          }}
          className="border-b hover:bg-slate-50 transition-colors px-3 py-3 cursor-pointer"
        >
          {/* Top strip: row checkbox on the left, action icons on the right */}
          <div className="flex items-center justify-between">
            <input
              type="checkbox"
              data-testid={TID.txnRowCheckbox}
              checked={selected.has(t.id)}
              onChange={() => toggleSel(t.id)}
            />
            <div className="flex items-center gap-1 shrink-0">
              <button
                title={t.human_reviewed ? "Unapprove" : isUncategorizedTxn(t) ? "Pick a category before approving" : "Approve"}
                data-testid={TID.txnApprove}
                onClick={(e) => toggleApprove(t, e)}
                className={
                  t.human_reviewed
                    ? "p-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    : isUncategorizedTxn(t)
                      ? "p-1 rounded text-slate-300 hover:bg-slate-100 cursor-not-allowed"
                      : "p-1 rounded hover:bg-emerald-100 text-emerald-600"
                }
              >
                <Check size={14} />
              </button>
              <button
                title="Ask AI about this transaction"
                data-testid={`txn-ai-${t.id}`}
                onClick={() => {
                  setFocus(
                    { id: t.id, merchant: t.merchant, amount: t.amount, date: t.date },
                    { pin: true }
                  );
                  emitAction("ai-open");
                  emitAction("ai-tell-me-about", {
                    txn: {
                      id: t.id,
                      merchant: t.merchant,
                      description: t.description,
                      contact_name: t.contact_name,
                      amount: t.amount,
                      date: t.date,
                    },
                  });
                }}
                className="p-1 rounded hover:bg-sky-100 text-sky-500"
              >
                <Sparkles size={14} />
              </button>
              <RowMoreMenu
                t={t}
                onEdit={() => setEditing(t)}
                onRecategorize={() => recategorize(t.id)}
                onSplit={() => setSplitting(t)}
                onLink={() => setLinking(t)}
                onDelete={() => del(t.id)}
                onAskClient={() => askClientRef.current?.(t)}
              />
            </div>
          </div>

          <NarrowFieldRow label="Date">
            <span className="text-sm text-slate-800 font-mono-num">{fmtDate(t.date)}</span>
          </NarrowFieldRow>

          <NarrowFieldRow label="Contact">
            <div className="flex items-center gap-2 min-w-0" title={t.contact_name || ""}>
              <ContactBadge
                contact={{ name: t.contact_name, logo_url: t.contact_logo_url }}
                size={22}
              />
              <span className="text-sm text-slate-800 font-medium truncate">
                {t.contact_name || <span className="text-slate-300 font-normal">—</span>}
              </span>
            </div>
          </NarrowFieldRow>

          <NarrowFieldRow label="Description">
            <div className="flex items-start gap-2 min-w-0">
              <div className="text-sm text-slate-800 break-words break-all whitespace-normal leading-snug flex-1 min-w-0">
                {t.merchant || t.description}
              </div>
              {(t.matched_receipt_id || t.receipt_id || t.veryfi_receipt_id) && (
                <button type="button" onClick={(e) => { e.stopPropagation(); onViewReceipt?.(t); }} className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full border ${t.receipt_match_status === "suggested" ? "bg-amber-50 text-amber-700 border-amber-300 ring-2 ring-amber-200" : "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"}`} title="View receipt" data-testid={`txn-receipt-badge-card-${t.id}`}>
                  <Paperclip size={11} />
                </button>
              )}
              {["SalesReceipt", "Deposit", "Purchase", "CreditMemo", "RefundReceipt"].includes(t.txn_type) && (
                <MatchDot row={t} mode="compact" />
              )}
            </div>
            {t.splits?.length > 0 && (
              <div className="text-[10px] text-indigo-600 mt-0.5">Split into {t.splits.length}</div>
            )}
            {(t.linked_invoice_id || t.linked_bill_id) && (
              <LinkedDocChip t={t} onOpen={setLinkedDocPreview} />
            )}
          </NarrowFieldRow>

          <NarrowFieldRow label="Category">
            {t.splits?.length > 0 ? (
              <button
                type="button"
                onClick={() => setEditing(t)}
                data-testid={`txn-cat-split-pill-${t.id}`}
                title={`Split across ${t.splits.length} accounts — click to view`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100"
              >
                <Split size={12} />
                — Split ({t.splits.length}) —
              </button>
            ) : (
              <div className="inline-flex items-center gap-1 max-w-full">
                {showProvenance && (
                  <ProvenanceDot
                    source={t.categorization_source || t.ai_source}
                    matched={t.rule_matched}
                    semantic={t.rule_semantic}
                    confidence={t.rule_confidence ?? t.ai_confidence}
                    bucket={t.bucket}
                  />
                )}
                <div className="min-w-0 flex-1" data-testid={TID.txnEditCategory}>
                  <AccountPicker
                    value={t.category_account_id || ""}
                    accounts={accts}
                    onChange={(id) => updateCategory(t.id, id)}
                    companyId={currentId}
                    testId={`txn-cat-picker-${t.id}`}
                  />
                </div>
                <AccountInfoTooltip
                  account={accts.find(a => a.id === t.category_account_id)}
                />
              </div>
            )}
          </NarrowFieldRow>

          <NarrowFieldRow label="Amount" align="right">
            <span
              className={`font-mono-num text-base font-semibold ${t.amount < 0 ? "text-red-600" : "text-emerald-600"}`}
            >
              {fmtMoney(t.amount)}
            </span>
          </NarrowFieldRow>
        </div>
      ))}
    </div>
  );
}


export function RowMoreMenu({ t, onEdit, onRecategorize, onSplit, onLink, onDelete, onAskClient }) {
  const { user: _menuUser } = useAuth();
  const isFirm = canUseCockpit(_menuUser);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null); // {top, left, flipUp}
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Position the menu on open + whenever the window scrolls/resizes.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = btnRef.current;
      if (!b) return;
      const r = b.getBoundingClientRect();
      const menuHeight = 230; // approx height of the 6-item menu
      const menuWidth  = 208; // matches w-52
      const spaceBelow = window.innerHeight - r.bottom;
      const flipUp = spaceBelow < menuHeight && r.top > menuHeight;
      setCoords({
        top: flipUp ? r.top - menuHeight - 4 : r.bottom + 4,
        // Right-align to the button, staying inside the viewport.
        left: Math.max(8, r.right - menuWidth),
        flipUp,
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      // Guided tour drives this menu with synthetic clicks — a mousedown on
      // the tour card (Next / Back) must not dismiss it mid-demo.
      if (e.target?.closest?.('[data-testid="chat-review-tour-v2"]')) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const item = "flex items-center justify-between gap-3 w-full px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50";
  const handle = (fn) => () => { setOpen(false); fn(); };

  return (
    <>
      <button
        ref={btnRef}
        title="More actions"
        data-testid={`txn-more-${t.id}`}
        onClick={() => setOpen(v => !v)}
        className={`p-1 rounded hover:bg-slate-100 ${open ? "bg-slate-100 text-slate-900" : "text-slate-500"}`}
      >
        <MoreHorizontal size={14} />
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          data-testid={`txn-more-menu-${t.id}`}
          style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 1000 }}
          className="w-52 rounded-md border border-slate-200 bg-white shadow-lg py-1"
        >
          <button data-testid={`txn-edit-${t.id}`} onClick={handle(onEdit)} className={item}>
            <span>Edit transaction</span>
            <Pencil size={13} className="text-slate-700" />
          </button>
          <button data-testid={TID.txnSplit} onClick={handle(onSplit)} className={item}>
            <span>Split</span>
            <Split size={13} className="text-violet-600" />
          </button>
          <button data-testid={TID.txnLink} onClick={handle(onLink)} className={item}>
            <span>Link to invoice / bill</span>
            <LinkIcon size={13} className="text-blue-600" />
          </button>
          <button
            data-testid={`txn-ask-client-${t.id}`}
            onClick={handle(onAskClient)}
            className={item}
          >
            <span>{isFirm ? "Ask client about this" : "Ask my accountant about this"}</span>
            <HelpCircle size={13} className="text-cyan-600" />
          </button>
          <div className="my-1 border-t border-slate-100" />
          <button data-testid={TID.deleteBtn} onClick={handle(onDelete)} className={`${item} text-red-600 hover:bg-red-50`}>
            <span>Delete</span>
            <Trash2 size={13} className="text-red-500" />
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

// Single modal instance shared across all rows. The row menu calls
// `registerRef.current(txn)` to pop it open with that transaction — cheaper
// than mounting one AskClientButton per row for large lists.
function AskClientLauncher({ registerRef, onAsked }) {
  const [txn, setTxn] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    registerRef.current = (t) => setTxn(t);
    return () => { registerRef.current = null; };
  }, [registerRef]);
  if (!canUseCockpit(user)) {
    return <AskAccountantModal open={Boolean(txn)} txn={txn} onClose={() => setTxn(null)} />;
  }
  return (
    <AskClientButton
      txn={txn}
      open={Boolean(txn)}
      onClose={() => setTxn(null)}
      onAsked={() => { onAsked?.(); setTxn(null); }}
    />
  );
}

// A tiny "client answered → AI proposes X" pill that renders below the
// confidence chip when a client's magic-link reply produced a categorization
// proposal. Accept applies the category with one click; dismiss drops the
// proposal but keeps the answer text on the row.
function ProposalPill({ proposal, onAccept, onDismiss }) {
  const conf = Number(proposal?.confidence || 0);
  const confColor = conf >= 0.8 ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : conf >= 0.5 ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div
      className={`mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${confColor}`}
      title={proposal.reasoning}
      data-testid="proposal-pill"
    >
      <Sparkles size={9} />
      <span>Client → <b>{proposal.account_name || proposal.account_code}</b></span>
      <button
        onClick={(e) => { e.stopPropagation(); onAccept(); }}
        data-testid="proposal-accept"
        title="Accept — apply this category"
        className="ml-1 inline-flex items-center px-1 rounded hover:bg-white/60"
      >
        <Check size={10} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        data-testid="proposal-dismiss"
        title="Dismiss proposal"
        className="inline-flex items-center px-1 rounded hover:bg-white/60"
      >
        <X size={10} />
      </button>
    </div>
  );
}

function ConfidenceChip({ conf, needs_review, human_reviewed, tx = null, currentId = null }) {
  const v = Number(conf || 0);
  // Needs-review always renders in an attention color regardless of the raw
  // confidence value. Some rows (transfers auto-routed to Uncategorized) have
  // conf=0.95 by design — the chip must not go green on them because the row
  // still requires an accountant to reclassify.
  let cls, label;
  if (human_reviewed) {
    // Green "Reviewed" chip — human (or QBO books-close ratchet) has
    // signed off. Confidence is display-only at this point. Feb 28 2026.
    cls = "confidence-high";
    label = "Reviewed";
  } else if (needs_review) {
    cls = v < 0.70 ? "confidence-low" : "confidence-med";  // rose vs amber
    label = v < 0.70 ? "Low" : "Needs review";
  } else {
    cls = v >= 0.85 ? "confidence-high" : v >= 0.70 ? "confidence-med" : "confidence-low";
    label = v >= 0.85 ? "High" : v >= 0.70 ? "Medium" : "Low";
  }
  const [open, setOpen] = useState(false);
  const [audit, setAudit] = useState(null);       // {winner, runners_up}
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAlts, setShowAlts] = useState(false);
  const clickable = !!tx;
  const chipInner = (
    <>
      {(human_reviewed || !needs_review) ? <ShieldCheck size={10} /> : <AlertTriangle size={10} />}
      {label} · {(v * 100).toFixed(0)}%
    </>
  );
  if (!clickable) {
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>
        {chipInner}
      </span>
    );
  }
  // Friendly label for ai_source (mirrors ProvenanceDot's SOURCE_META).
  const sourceLabel = {
    user_rule:         "Your custom rule",
    rule:              "Your custom rule",
    rules_miner:       "Learned from prior corrections",
    memory:            "Merchant memory (cache)",
    contact_learning:  "Learned from this contact's approved history",
    directory:         "Global Contact Directory",
    standard_plus_directory: "Standard+ Global Directory",
    standard_plus_rule:      "Standard+ Global Vendor Rule",
    standard_plus_pfc:       "Plaid PFC (Standard+ fallback)",
    pfc_business:      "Plaid PFC (business)",
    pfc_personal:      "Plaid PFC (personal)",
    pfc:               "Plaid PFC",
    ai:                "AI (LLM cascade)",
    llm:               "AI (LLM cascade)",
    manual_bulk:       "Manual bulk reclassify",
    manual:            "Manual reclassify",
    uncategorized:     "Uncategorized bucket (low confidence)",
  }[tx.ai_source] || tx.ai_source || "Unknown";
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        data-testid="confidence-chip-btn"
        title="Click to see how AI decided this"
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium cursor-help ${cls} hover:ring-1 hover:ring-slate-300`}
      >
        {chipInner}
      </button>
      {open && (
        <>
          {/* Click-outside catcher */}
          <div
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40"
          />
          <div
            data-testid="confidence-chip-popover"
            className="absolute z-50 mt-1 right-0 w-80 rounded-lg border border-slate-200 bg-white shadow-xl p-3 text-[11px] text-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold uppercase tracking-wider text-[10px] text-slate-500">
                How AI decided
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              ><X size={12} /></button>
            </div>
            <div className="space-y-1.5">
              <div>
                <span className="text-[10px] uppercase text-slate-500">Winner:</span>{" "}
                <b className="text-slate-900">{sourceLabel}</b>
                <span className="ml-1 text-slate-500">· {(v * 100).toFixed(0)}%</span>
              </div>
              {tx.ai_reasoning && (
                <div className="text-slate-600 leading-snug">
                  <span className="text-[10px] uppercase text-slate-500">Reasoning:</span>{" "}
                  {tx.ai_reasoning}
                </div>
              )}
              {tx.category_account_code && (
                <div>
                  <span className="text-[10px] uppercase text-slate-500">Posted to:</span>{" "}
                  <span className="font-mono-num">{tx.category_account_code}</span>{" "}
                  <span>{tx.category_account_name}</span>
                </div>
              )}
              {tx.needs_review && (
                <div className="text-amber-700 text-[10px]">
                  This row is flagged for review — confidence below the auto-post threshold.
                </div>
              )}
              {/* ── Alternates section ─────────────────────────────── */}
              <div className="pt-2 border-t border-slate-100">
                {!showAlts ? (
                  <button
                    type="button"
                    data-testid="confidence-chip-show-alts"
                    onClick={async () => {
                      setShowAlts(true);
                      if (audit || auditLoading) return;
                      setAuditLoading(true);
                      try {
                        const r = await api.get(
                          `/companies/${currentId}/transactions/${tx.id}/categorization-audit`
                        );
                        setAudit(r.data || null);
                      } catch (e) {
                        setAudit({ winner: null, runners_up: [] });
                      } finally {
                        setAuditLoading(false);
                      }
                    }}
                    className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Show alternate categorizations
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Alternate categorizations
                    </div>
                    {auditLoading && (
                      <div className="text-slate-400 text-[11px]">Loading…</div>
                    )}
                    {audit && audit.runners_up.length === 0 && !auditLoading && (
                      <div className="text-slate-400 text-[11px] italic">
                        No alternate categorizations available.
                      </div>
                    )}
                    {audit && audit.runners_up.map((ru, i) => {
                      const layerNames = {
                        user_rule_contact:  "Contact rule",
                        user_rule_merchant: "Merchant rule",
                        contact_learning:   "Contact history",
                        directory:          "Global Directory",
                        pfc:                "Plaid PFC",
                        ai:                 "AI (LLM cascade)",
                      };
                      const layerColors = {
                        user_rule_contact:  "bg-emerald-100 text-emerald-800",
                        user_rule_merchant: "bg-emerald-100 text-emerald-800",
                        contact_learning:   "bg-teal-100 text-teal-800",
                        directory:          "bg-violet-100 text-violet-800",
                        pfc:                "bg-cyan-100 text-cyan-800",
                        ai:                 "bg-amber-100 text-amber-800",
                      };
                      return (
                        <div
                          key={i}
                          data-testid={`confidence-chip-alt-${i}`}
                          className={`rounded border p-2 ${ru.could_have_fired ? "border-slate-200" : "border-slate-100 bg-slate-50"}`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-[10px] font-mono-num text-slate-400">
                              {i + 2}.
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${layerColors[ru.layer] || "bg-slate-100 text-slate-700"}`}>
                              {layerNames[ru.layer] || ru.layer}
                            </span>
                            {ru.account_code && (
                              <span className="text-[11px] text-slate-700">
                                → <b>{ru.account_code}</b> {ru.account_name}
                              </span>
                            )}
                          </div>
                          <div className="text-slate-500 text-[10.5px] leading-snug mb-1">
                            {ru.reason_lost}
                          </div>
                          {ru.could_have_fired && ru.account_code && (
                            <button
                              type="button"
                              data-testid={`confidence-chip-alt-use-${i}`}
                              onClick={async () => {
                                try {
                                  await api.post(
                                    `/companies/${currentId}/transactions/bulk-reclassify`,
                                    {
                                      transaction_ids: [tx.id],
                                      account_code:    ru.account_code,
                                    },
                                  );
                                  toast.success(`Reclassified to ${ru.account_name}`);
                                  setOpen(false);
                                  // Best-effort refresh — the grid re-fetches
                                  // periodically, and the parent will re-render
                                  // this chip with the new source next cycle.
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Failed");
                                }
                              }}
                              className="text-[10px] px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-700 text-white font-medium"
                            >
                              Use this instead
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </span>
  );
}

// Lab v3 · Client Review banner — top of the Transactions page. Fires
// only when the current company is on `categorization_mode == "lab_v3"`
// AND has open review questions. Deep-links to the Review v2 · Lab
// queue. Kept as a small function so we don't pull the counter hook
// into the giant Transactions component below (avoids re-renders on
// unrelated state changes).
function LabV3ReviewBanner({ companyId }) {
  const { data } = useLabV3ReviewCount(companyId);
  if (!data?.is_lab_v3 || !data.questions_left) return null;
  return (
    <div
      className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 flex items-center gap-3 text-sm text-indigo-900"
      data-testid="transactions-labv3-banner"
    >
      <MessageSquareWarning size={14} className="text-indigo-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <b>{data.questions_left.toLocaleString()}</b> transaction{data.questions_left === 1 ? "" : "s"} awaiting your review
        · <span className="font-mono-num">${data.unconfirmed_dollars.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span> unconfirmed
        · <span className="text-indigo-700">{data.pct_confirmed}% of book value already posted</span>
      </div>
      <Link
        to={LAB_V3_REVIEW_ROUTE}
        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-600 text-white text-[12px] font-semibold hover:bg-indigo-700"
        data-testid="transactions-labv3-banner-open"
      >
        Start review →
      </Link>
    </div>
  );
}



export default function Transactions() {
  const { currentId, isAdvancedMode } = useCompany();
  const fmtMoney = useMoneyFmt();
  const fmtDate = useDateFmt();
  const { setFocus } = useAiFocus();
  const isMobile = useIsMobile();
  // Company-level opt-in flag for provenance dots (Settings toggle).
  // Fetched on mount; when true, each txn row shows a colored dot
  // next to its category indicating which tier decided it.
  const [showProvenance, setShowProvenance] = useState(false);
  useEffect(() => {
    if (!currentId) return;
    api.get(`/companies/${currentId}`).then(r => {
      setShowProvenance(!!r.data?.show_categorization_source_badges);
    }).catch(() => {});
  }, [currentId]);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Read the "Let's Review" URL params up-front so `load()` (defined
  // below) can reference them without hitting a TDZ error.
  const isLetsReview = params.get("letsReview") === "1";
  const isReviewDone = params.get("done") === "1";
  const lrContactId = params.get("contact_id") || "";
  const lrContactName = params.get("contact_name") || "";
  const lrIdx = parseInt(params.get("idx") || "1", 10);
  const lrTotal = parseInt(params.get("total") || "1", 10);
  const lrCount = parseInt(params.get("count") || "0", 10);
  const lrTotalAmount = parseFloat(params.get("total_amount") || "0");
  const letsReviewNav = useLetsReviewNav();
  // Declare No-Contact Review mode flags up here (they were previously
  // defined further down) so the bulk-categorize useEffect below can
  // reference them without hitting a Temporal Dead Zone error.
  const isNoContactReview = params.get("noContactReview") === "1";
  const ncrGroupKey = params.get("group_key") || "";
  const ncrLabel = params.get("label") || "";
  const noContactReviewNav = useNoContactReviewNav();
  // In-place list-mode toggle for No-Contact Review. When set, the
  // transactions table is replaced by <ListModeView embedded /> and
  // the "Group X of Y" pill drops its category picker + Prev/Next
  // controls (but keeps its footprint so the surrounding layout
  // doesn't reflow). Powered by ?view=list on the same URL.
  const ncrIsListMode = isNoContactReview && params.get("view") === "list";
  const isReviewMode = isLetsReview || isNoContactReview;

  // Step 2 first-time tour. Fires when the URL carries `?tour=1` AND
  // the client hasn't already seen it for this company. Auto-plays once
  // the contact info box is on-screen. See `firm_glance.py` step2
  // cta_link which appends `&tour=1` to trigger this.
  const { user } = useAuth();
  const { currentId: currentCompanyId } = useCompany();
  const [step2TourOpen, setStep2TourOpen] = useState(false);
  const tourParam = params.get("tour") === "1";
  const replayParam = params.get("replay") === "1";
  useEffect(() => {
    if (!tourParam) return;
    if (!isLetsReview || !lrContactId || !lrContactName) return;
    if (!user?.id || !currentCompanyId) return;
    // `replay=1` bypasses the seen check so the Settings "Replay Step 2
    // tour" button always re-fires the walkthrough.
    if (!replayParam && hasSeenStep2Tour(user.id, currentCompanyId)) return;
    // Wait for the info box + AI panel to mount, then fire.
    const t = setTimeout(() => setStep2TourOpen(true), 900);
    return () => clearTimeout(t);
  }, [tourParam, replayParam, isLetsReview, lrContactId, lrContactName, user?.id, currentCompanyId]);
  const closeStep2Tour = () => setStep2TourOpen(false);
  // Support "Re-play tour" CTA click from the AI panel — restarts the
  // Step 2 tour regardless of the seen flag.
  useActionListener("chat-cta:restart-step2-tour", () => setStep2TourOpen(true));

  // Step 3B first-time tour — same shape as Step 2 but fires when the
  // page is in No-Contact Review mode (`noContactReview=1`). Piggybacks
  // on the same `?tour=1` / `?replay=1` params.
  const [step3bTourOpen, setStep3bTourOpen] = useState(false);
  useEffect(() => {
    if (!tourParam) return;
    if (!isNoContactReview) return;
    if (!user?.id || !currentCompanyId) return;
    if (!replayParam && hasSeenStep3BTour(user.id, currentCompanyId)) return;
    const t = setTimeout(() => setStep3bTourOpen(true), 900);
    return () => clearTimeout(t);
  }, [tourParam, replayParam, isNoContactReview, user?.id, currentCompanyId]);
  const closeStep3BTour = () => setStep3bTourOpen(false);

  // Inline bulk-categorize dropdown in the Let's-Review info card — lets
  // the CPA one-click categorize every currently-visible row for the
  // contact into a chosen GAAP account, bypassing the AI chat entirely.
  // The dropdown now runs in PREVIEW mode — picking a category only
  // updates the on-screen category column for every visible row (via
  // local `txns` state) so the CPA can eyeball the change before
  // committing. Clicking the Approve button next to the header fires
  // the actual bulk-save API call.
  const [bulkCatBusy, setBulkCatBusy] = useState(false);
  const [bulkPreviewAcctId, setBulkPreviewAcctId] = useState("");
  // Snapshot the original category_account_id per row when a preview
  // starts, so we can restore visuals if the user changes their mind
  // (picks a different account or clears the dropdown).
  const bulkPreviewOriginalRef = useRef(null);
  // Reset the preview whenever the current Let's Review contact / No-
  // Contact Review group changes — new stepper page, fresh choice.
  useEffect(() => {
    setBulkPreviewAcctId("");
    bulkPreviewOriginalRef.current = null;
  }, [lrContactId, isLetsReview, ncrGroupKey, isNoContactReview]);
  const previewBulkCategory = (accountId) => {
    // Snapshot originals the first time the CPA touches the dropdown
    // for this contact so we can revert cleanly on re-pick / clear.
    if (bulkPreviewOriginalRef.current === null) {
      bulkPreviewOriginalRef.current = Object.fromEntries(
        (txns || []).map((t) => [t.id, t.category_account_id || ""])
      );
    }
    setBulkPreviewAcctId(accountId || "");
    setTxns((prev) =>
      (prev || []).map((t) => {
        if (!accountId) {
          // Revert to original when cleared.
          const orig = bulkPreviewOriginalRef.current?.[t.id] ?? "";
          return { ...t, category_account_id: orig || null };
        }
        return { ...t, category_account_id: accountId };
      })
    );
  };
  const applyBulkCategoryPreview = async () => {
    const accountId = bulkPreviewAcctId;
    if (!accountId || bulkCatBusy || !currentId) return;
    const acct = accts.find((a) => a.id === accountId);
    // Use the snapshotted row IDs so a mid-flight refresh doesn't shrink
    // the batch. Fall back to the currently-visible rows.
    const ids = bulkPreviewOriginalRef.current
      ? Object.keys(bulkPreviewOriginalRef.current)
      : (txns || []).map((t) => t.id);
    if (ids.length === 0) {
      toast.info("No visible rows to categorize.");
      return;
    }
    setBulkCatBusy(true);
    try {
      const res = await api.post(
        `/companies/${currentId}/transactions/apply-multi-bulk-approve-rule`,
        {
          contact_id: lrContactId || null,
          contact_name: lrContactName || "",
          groups: [{
            txn_ids: ids,
            category_account_id: accountId,
            rule_label: acct ? `${acct.code || ""} ${acct.name || ""}`.trim() : "",
          }],
          // Skip rule creation from the dropdown — it's a one-shot
          // decision the CPA is making without the AI's help; they can
          // always create a persistent rule from Contacts later.
          create_rules: false,
        }
      );
      const updated = res.data?.updated || 0;
      toast.success(
        `Categorized ${updated} ${lrContactName || "row"}${updated === 1 || lrContactName ? "" : "s"} to ${acct?.name || "the selected account"}.`
      );
      // Clear the preview snapshot — the stepper will auto-advance and
      // the freshly-loaded rows should reflect the persisted state.
      bulkPreviewOriginalRef.current = null;
      setBulkPreviewAcctId("");
      // Emit the same cleanup-completed event AiPanel emits after
      // bulk-approve so the Transactions page's stepper listener knows
      // to auto-advance. For Let's Review it fires `contact_in_uncat`
      // (routes back to /accounting/lets-review); for No-Contact Review
      // it fires `no_contact_group` with the group_key so the listener
      // routes to /accounting/no-contact-review instead.
      if (isNoContactReview) {
        emitAction("cleanup-completed", {
          group_key: ncrGroupKey,
          kind: "no_contact_group",
          count: updated,
        });
      } else {
        emitAction("cleanup-completed", {
          contact_id: lrContactId,
          kind: "contact_in_uncat",
          count: updated,
        });
      }
    } catch (e) {
      toast.error("Bulk-categorize failed — try again?");
    } finally {
      setBulkCatBusy(false);
    }
  };
  // No-Contact Review (Step 3) mode flags moved to top of component
  // (see the group above `bulkPreviewAcctId`) so the bulk-categorize
  // useEffect can safely reference them.
  const [txns, setTxns] = useState([]);
  const [sampleMode, setSampleMode] = useState(false);
  const sampleRef = useRef([]);
  const [loadedOnce, setLoadedOnce] = useState(false);
  // Client-side column sort — null means "server/natural order".
  // Clicking a header toggles asc→desc→asc and sets the active column.
  const [sortBy,  setSortBy]  = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("asc"); }
  };
  const [accts, setAccts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [splitting, setSplitting] = useState(null);
  const [linking, setLinking] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reclassOpen, setReclassOpen] = useState(false);
  // Rich linked-invoice/bill chip preview modal — clicking the chip
  // renders the linked doc in-place instead of navigating away.
  const [linkedDocPreview, setLinkedDocPreview] = useState(null);
  // Paperclip click → receipt popup (image + line items) right on this page.
  const [receiptPopup, setReceiptPopup] = useState(null);
  const [verifyCount, setVerifyCount] = useState(0);
  const loadVerifyCount = () => {
    if (!currentId) return;
    api.get(`/companies/${currentId}/receipts/verify-count`).then((r) => setVerifyCount(r.data?.count || 0)).catch(() => {});
  };
  useEffect(() => { loadVerifyCount(); /* eslint-disable-next-line */ }, [currentId, txns]);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [ruleQueue, setRuleQueue] = useState(null);   // guided-rules flow
  const automationsGate = useFeature("automations");
  // { title, body, confirmLabel, variant, exec } — set by any bulk
  // action that needs a second confirm before writing to Mongo.
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [ruleSuggestion, setRuleSuggestion] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, limit: 25 });
  // Toolbar filters
  const [search, setSearch] = useState("");
  // "Show N similar" from the AI panel pins the list to an explicit id set.
  const [similarView, setSimilarView] = useState(null); // { ids, label, count }
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Advanced filter panel — hidden by default, toggled via the
  // "Advanced filter" link next to the date picker.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Responsive table: when the transactions wrapper gets narrow (e.g.
  // the AI panel is open on a laptop), collapse the trailing Amount /
  // Bank Balance / row-actions columns onto a compact second line
  // *below* the primary row instead of forcing a horizontal scrollbar.
  // Threshold is measured on the wrapper itself, so it responds to the
  // AI panel opening/closing without waiting for a viewport change.
  const tableWrapRef = useRef(null);
  const [tableNarrow, setTableNarrow] = useState(false);
  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setTableNarrow(w > 0 && w < 900);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [filterBankAccountId, setFilterBankAccountId] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterContactId, setFilterContactId] = useState("");
  const [filterAmountMin, setFilterAmountMin] = useState("");
  const [filterAmountMax, setFilterAmountMax] = useState("");
  // Withdrawal / Deposit pills — mutually exclusive quick filter.
  // "" (none) | "out" (withdrawal / money out) | "in" (deposit / money in)
  const [directionFilter, setDirectionFilter] = useState("");
  // Entity-type chip strip — orthogonal filter to the status buckets
  // above. Kept separate because the two dimensions are orthogonal:
  // a Purchase can be either "ai" or "reviewed", etc.
  const [txnTypeFilter, setTxnTypeFilter] = useState("");
  // Bank-match pending counts by txn_type. Powers the small amber
  // badge on each entity chip so CPAs know at a glance which types
  // have unreviewed silent matches waiting. Fetched once per mode
  // switch — the counts are stable enough that a few seconds of
  // staleness is acceptable.
  const [pendingByType, setPendingByType] = useState({});
  // "list" (default) or "rollup" — toggled by the two icons in the toolbar.
  const [view, setView] = useState("list");
  const [rollup, setRollup] = useState(null);
  // Imperative handle so the row-menu action can pop the AskClient modal
  // for any transaction without mounting one modal per row.
  const askClientRef = useRef(null);
  const [rollupBusy, setRollupBusy] = useState(false);

  // Debounce free-text search so a fast typist doesn't hammer the API.
  // Single-char searches are almost never useful (returns 20K+ matches) and
  // scan the whole corpus, so we require ≥2 chars before firing.
  useEffect(() => {
    const h = setTimeout(() => {
      const s = search.trim();
      setDebouncedSearch(s.length >= 2 ? s : "");
    }, 300);
    return () => clearTimeout(h);
  }, [search]);

  const loadSeq = useRef(0);
  const load = async () => {
    if (!currentId) return;
    const seq = ++loadSeq.current;
    if (sampleMode) {
      let a = accts;
      if (!a.length) { a = (await api.get(`/companies/${currentId}/accounts`)).data.accounts || []; setAccts(a); }
      if (!sampleRef.current.length) sampleRef.current = buildSampleTxns(a, currentId);
      const rows = filterSample(sampleRef.current, filter);
      setTxns(rows);
      setPagination({ total: rows.length, page: 1, pages: 1, limit: pageSize });
      setInvoices(SAMPLE_INVOICES); setBills(SAMPLE_BILLS); setSelected(new Set());
      return;
    }
    const params = new URLSearchParams();
    if (filter === "review") params.set("needs_review", "true");
    else if (filter === "ai" || filter === "uncategorized" || filter === "unapproved" || filter === "reviewed" || filter === "receipt_verify") {
      params.set("status", filter);
    }
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    // Advanced filter params — only include when set so the URL stays
    // clean for the common "no advanced filters" case.
    if (filterBankAccountId) params.set("bank_account_id", filterBankAccountId);
    if (filterCategoryId) params.set("category_account_id", filterCategoryId);
    if (filterContactId) params.set("contact_id", filterContactId);
    if (filterAmountMin) params.set("amount_min", filterAmountMin);
    if (filterAmountMax) params.set("amount_max", filterAmountMax);
    if (directionFilter) params.set("direction", directionFilter);
    if (txnTypeFilter) {
      params.set("txn_type", txnTypeFilter);
      // When explicitly filtering by an editor-authored entity type,
      // surface the matched rows too — the CPA is asking "show me my
      // Sales Receipts" and would be confused if the matched ones
      // vanished. Only the default (unfiltered) ledger hides them.
      params.set("include_matched", "true");
    }
    // "Let's Review" mode pins the list to a single contact so the
    // stepper walks vendor-by-vendor without the user re-typing filters.
    if (isLetsReview && lrContactId) params.set("contact_id", lrContactId);
    // "No-Contact Review" (Step 3) pins the list to a description-signature
    // group so the CPA walks bank-feed noise one bucket at a time. Also
    // hard-force `status=unapproved` so rows already decisioned (e.g.
    // AI-categorized to 3200 Inter-Account Transfer) drop off the view —
    // the CPA should only see rows still waiting for a call.
    if (isNoContactReview) {
      params.set("no_contact", "1");
      params.set("status", "unapproved");
      if (ncrGroupKey && ncrGroupKey !== "__misc__") {
        params.set("desc_group", ncrGroupKey);
      }
    }
    // Same rule for Let's Review — hide anything already human-reviewed so
    // the vendor stepper only shows work that's still open.
    if (isLetsReview) {
      params.set("status", "unapproved");
    }
    if (similarView?.ids?.length) params.set("ids", similarView.ids.join(","));
    params.set("page", String(page));
    params.set("limit", String(pageSize));
    const qs = `?${params.toString()}`;
    let t, a, i, b;
    try {
      [t, a, i, b] = await Promise.all([
        api.get(`/companies/${currentId}/transactions${qs}`),
        api.get(`/companies/${currentId}/accounts`),
        api.get(`/companies/${currentId}/invoices`),
        api.get(`/companies/${currentId}/bills`),
      ]);
    } catch (e) {
      if (seq === loadSeq.current) toast.error(e?.response?.status === 429 ? "Slow down a sec — too many requests. Retrying shortly." : "Couldn't load transactions.");
      return;
    }
    if (seq !== loadSeq.current) return;
    setLoadedOnce(true);
    setTxns(t.data.transactions || []);
    setPagination(t.data.pagination || { total: (t.data.transactions || []).length, page: 1, pages: 1, limit: pageSize });
    setAccts(a.data.accounts || []);
    setInvoices(i.data.invoices || []);
    setBills(b.data.bills || []);
    if (similarFreshRef.current) {
      similarFreshRef.current = false;
      setSelected(new Set((t.data.transactions || []).map(r => r.id)));
    } else {
      setSelected(new Set());
    }
  };

  // Contact options for the advanced-filter panel. Fetched once per
  // company since contacts change infrequently relative to txns. The
  // Bank account filter is sourced from `accts` (chart of accounts) so
  // it doesn't need its own fetch — all Asset + Liability rows appear
  // in the dropdown, banks, credit cards, loans and A/R/A/P alike.
  const [filterContactOptions, setFilterContactOptions] = useState([]);
  useEffect(() => {
    if (!currentId) return;
    api.get(`/companies/${currentId}/contacts?limit=500`)
      .catch(() => ({ data: {} }))
      .then((contactsRes) => {
        setFilterContactOptions((contactsRes.data?.contacts || []).map((c) => ({
          id: c.id, name: c.name || c.display_name || "—", logo_url: c.logo_url || null,
        })));
      });
  }, [currentId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentId, sampleMode, filter, page, pageSize, debouncedSearch, dateFrom, dateTo, isLetsReview, lrContactId, isNoContactReview, ncrGroupKey, filterBankAccountId, filterCategoryId, filterContactId, filterAmountMin, filterAmountMax, directionFilter, txnTypeFilter, similarView]);

  // "Show N similar" from the AI panel: same-page via action bus, cross-page
  // via sessionStorage (the panel navigates here first, then we hydrate).
  // We snapshot the view the user was looking at so Clear puts them back.
  const similarReturnRef = useRef(null);
  const similarFreshRef = useRef(false);
  const applySimilarView = (p) => {
    if (!p?.ids?.length) return;
    if (!similarReturnRef.current) {
      similarReturnRef.current = {
        filter, search, dateFrom, dateTo, page, view, selected,
        filterBankAccountId, filterCategoryId, filterContactId, filterAmountMin, filterAmountMax, directionFilter,
        returnTo: p.returnTo || null,
      };
    }
    similarFreshRef.current = true;
    setFilter("all"); setSearch(""); setDebouncedSearch(""); setView("list");
    setFilterBankAccountId(""); setFilterCategoryId(""); setFilterContactId("");
    setFilterAmountMin(""); setFilterAmountMax(""); setDirectionFilter("");
    setSimilarView(p); setPage(1);
  };
  const clearSimilarView = () => {
    const snap = similarReturnRef.current;
    similarReturnRef.current = null;
    setSimilarView(null);
    if (snap) {
      setFilter(snap.filter); setSearch(snap.search); setDebouncedSearch(snap.search.trim().length >= 2 ? snap.search.trim() : "");
      setDateFrom(snap.dateFrom); setDateTo(snap.dateTo); setView(snap.view);
      setFilterBankAccountId(snap.filterBankAccountId); setFilterCategoryId(snap.filterCategoryId); setFilterContactId(snap.filterContactId);
      setFilterAmountMin(snap.filterAmountMin); setFilterAmountMax(snap.filterAmountMax); setDirectionFilter(snap.directionFilter);
      setPage(snap.page);
      setTimeout(() => setSelected(snap.selected || new Set()), 0);
    } else {
      setSelected(new Set());
    }
    emitAction("similar-view-cleared");
    if (snap?.returnTo && snap.returnTo !== "/accounting/transactions") navigate(snap.returnTo);
  };
  const applySimilarRef = useRef(applySimilarView);
  const clearSimilarRef = useRef(clearSimilarView);
  useEffect(() => { applySimilarRef.current = applySimilarView; clearSimilarRef.current = clearSimilarView; });
  useActionListener("show-similar-txns", (p) => applySimilarRef.current(p));
  useActionListener("similar-clear", () => clearSimilarRef.current());
  // Voice "yes" on a focused row (AiPanel) approved it and found same-vendor
  // siblings — show the same modal the checkmark click shows.
  useActionListener("open-similar-approve-modal", (p) => {
    if (p?.similar?.count) setSimilarApprove({ similar: p.similar, ruleExists: !!p.rule_exists });
  });

  // Transactions page tour (same engine as Review Chat). Auto-opens once
  // per browser when real rows are on screen; replayable via the Tour
  // button. Snapshots the filter so the "To do" click is undone on close.
  const TXN_TOUR_SEEN_KEY = "transactions-tour-completed-v1";
  const [txnTourOpen, setTxnTourOpen] = useState(false);
  const fromOnboarding = params.get("from") === "onboarding";
  const fromCheckout = params.get("from") === "checkout";
  const [tourInviteOpen, setTourInviteOpen] = useState(false);
  const closeTourInvite = (startIt) => {
    setTourInviteOpen(false);
    try { localStorage.setItem(TXN_TOUR_SEEN_KEY, "1"); } catch (_) { /* ignore */ }
    navigate("/accounting/transactions", { replace: true });
    if (startIt) setTimeout(startTxnTour, 350);
  };
  useEffect(() => {
    if (fromOnboarding && loadedOnce) setTourInviteOpen(true);
  }, [fromOnboarding, loadedOnce]);
  useEffect(() => {
    if (!fromCheckout) return;
    // Returning customers (re-subscribed after a cancel) have already
    // seen or dismissed the tour — don't re-pitch it.
    let seen = false;
    try { seen = localStorage.getItem(TXN_TOUR_SEEN_KEY) === "1"; } catch (_) { /* ignore */ }
    if (seen) { navigate("/accounting/transactions", { replace: true }); return; }
    const t = setTimeout(() => setTourInviteOpen(true), 800);
    return () => clearTimeout(t);
  }, [fromCheckout]);
  const txnTourSnapRef = useRef(null);
  // The tour runs on sample rows rendered through the real table so every
  // beat has a live target even for a brand-new company. Nothing is saved.
  const startTxnTour = () => { txnTourSnapRef.current = { filter, page }; sampleRef.current = []; setSampleMode(true); setTxnTourOpen(true); };
  const closeTxnTour = (res) => {
    setTxnTourOpen(false);
    setSampleMode(false); sampleRef.current = [];
    try { localStorage.setItem(TXN_TOUR_SEEN_KEY, "1"); } catch (_) { /* ignore */ }
    const snap = txnTourSnapRef.current; txnTourSnapRef.current = null;
    if (res?.completed) { setFilter("unapproved"); setPage(1); return; }   // finale: "I'll put you on the To do screen"
    if (snap && snap.filter !== filter) { setFilter(snap.filter); setPage(snap.page || 1); }
  };
  useEffect(() => {
    if (txnTourOpen || tourInviteOpen || fromOnboarding || fromCheckout || isReviewMode || isLetsReview || isNoContactReview || tourParam) return;
    if (!loadedOnce) return;
    let seen = false;
    try { seen = localStorage.getItem(TXN_TOUR_SEEN_KEY) === "1"; } catch (_) { /* ignore */ }
    if (seen) return;
    const t = setTimeout(startTxnTour, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedOnce, isReviewMode, isLetsReview, isNoContactReview, tourParam]);
  useActionListener("chat-cta:restart-transactions-tour", () => startTxnTour());
  // `?txnTour=1` (Step 1 card in AI Transaction Review mode) → filter is
  // already applied by the param hydration; start the tour once data is in.
  const txnTourParam = params.get("txnTour") === "1";
  useEffect(() => {
    if (!txnTourParam || !loadedOnce || txnTourOpen) return;
    const next = new URLSearchParams(params); next.delete("txnTour");
    navigate({ search: next.toString() ? `?${next}` : "" }, { replace: true });
    startTxnTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txnTourParam, loadedOnce]);
  useEffect(() => {
    if (!similarView) return;
    emitAction("similar-selection-changed", { ids: [...selected] });
  }, [selected, similarView]);
  useEffect(() => {
    const raw = sessionStorage.getItem("axiom_similar_view");
    if (!raw) return;
    sessionStorage.removeItem("axiom_similar_view");
    try { applySimilarView(JSON.parse(raw)); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch unreviewed silent-match counts by txn_type — only in
  // Advanced mode where the chip strip actually renders. Cheap: one
  // API call, then group client-side. Re-runs when the company
  // switches so counts follow the active tenant.
  useEffect(() => {
    if (!currentId || !isAdvancedMode) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(
          `/companies/${currentId}/bank-matches`,
          { params: { status: "unconfirmed" } });
        if (cancelled) return;
        const by = {};
        for (const p of (r.data.pairs || [])) {
          const t = p.editor?.txn_type;
          if (t) by[t] = (by[t] || 0) + 1;
        }
        setPendingByType(by);
      } catch {
        if (!cancelled) setPendingByType({});
      }
    })();
    return () => { cancelled = true; };
  }, [currentId, isAdvancedMode]);
  // Reset page when filters narrow/widen.
  useEffect(() => { setPage(p => (p === 1 ? p : 1)); }, [debouncedSearch, dateFrom, dateTo]);

  // Rollup fetch — mirrors the list filters so the two views stay in sync.
  const loadRollup = async () => {
    if (!currentId || view !== "rollup") return;
    setRollupBusy(true);
    try {
      const p = new URLSearchParams();
      if (debouncedSearch) p.set("q", debouncedSearch);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      // Mirror the current status tab so the rollup shows the same slice
      // as the list (esp. important for the AI Categorized tab, which
      // should show ONLY AI-categorized rows grouped by contact).
      if (filter && filter !== "all") p.set("status", filter);
      const r = await api.get(`/companies/${currentId}/transactions/contact-category-rollup?${p.toString()}`);
      setRollup(r.data);
    } finally { setRollupBusy(false); }
  };
  useEffect(() => { loadRollup(); /* eslint-disable-next-line */ }, [currentId, view, filter, debouncedSearch, dateFrom, dateTo]);
  // Any time the user clicks the AI Categorized tab, snap the view to the
  // rollup by default — that's the "contacts + categories" lens the user
  // reaches for on that tab. Clicking any other tab returns to the list.
  useEffect(() => {
    if (filter === "ai") setView("rollup");
    else setView("list");
  }, [filter]);
  // Keep a live ref to `load` so the background sync poller can invoke the
  // CURRENT filter-aware load — not the stale closure from mount. Without
  // this, clicking "Needs Review" briefly shows filtered rows and then the
  // poller (5-15s later) fires the original all-rows load and overwrites
  // them, so the tab stays selected but rows revert to All.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; });
  // AI-panel actions (approve-with-suggestion / bulk-approve-rule) reload us.
  useActionListener("txns:changed", () => { loadRef.current?.(); });

  // Auto-advance the Let's-Review / No-Contact-Review stepper. When the AI
  // Panel finishes bulk-categorizing the current contact/group it emits a
  // `cleanup-completed` event — but our stepper URL params still point at
  // the just-cleared contact, so the info card and the (now-empty) txn list
  // stay stuck. Kick the user forward by re-routing to the parent stepper
  // page, which re-fetches fresh groups and picks up the next uncleared
  // one. Live state ferried through a ref because `useActionListener`
  // binds once (its `[]` deps), so a naive closure would see stale values.
  const reviewCtxRef = useRef({});
  useEffect(() => {
    reviewCtxRef.current = {
      isLetsReview, isNoContactReview, lrContactId, ncrGroupKey, navigate,
      // Ferry the Let's Review navigator so the auto-advance can jump
      // to the NEXT contact (idx+1) after Approve, instead of dropping
      // back to the first uncleared group at idx=0. Aug 24 2026 — the
      // "Contact 4 of 48 → 1 of 48" jump-back bug.
      letsReviewNav,
    };
  }, [isLetsReview, isNoContactReview, lrContactId, ncrGroupKey, navigate, letsReviewNav]);
  useActionListener("cleanup-completed", (payload) => {
    const ctx = reviewCtxRef.current;
    if (!ctx.isLetsReview && !ctx.isNoContactReview) return;
    const completedCid = payload?.contact_id || payload?.contactId;
    if (ctx.isLetsReview && completedCid && completedCid === ctx.lrContactId) {
      // Delay slightly so the toast + "Done" chat message land before the
      // page jumps — matches the 1.2s auto-advance timer in CleanupCopilot.
      setTimeout(() => {
        // Advance to the NEXT contact in the stepper if one exists,
        // otherwise fall back to the base URL (which surfaces "queue
        // complete" state). Fixes the user-reported "Contact 4 of 48 →
        // 1 of 48" regression on Approve. Aug 24 2026.
        if (ctx.letsReviewNav?.next) {
          ctx.letsReviewNav.next();
        } else {
          ctx.navigate("/accounting/lets-review", { replace: true });
        }
      }, 900);
    } else if (ctx.isNoContactReview && (payload?.kind === "no_contact_group" || payload?.group_key)) {
      setTimeout(() => ctx.navigate("/accounting/no-contact-review", { replace: true }), 900);
    }
  });

  // Refs used by the "apply-categorize-proposal" listener below — the
  // useActionListener hook binds its handler once, so we ferry live state
  // (selection, CoA, focused txn) through refs to avoid a stale closure.
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);
  const acctsRef = useRef(accts);
  useEffect(() => { acctsRef.current = accts; }, [accts]);
  const txnsRef = useRef(txns);
  useEffect(() => { txnsRef.current = txns; }, [txns]);

  // AI proposal follow-through — the AI proposed a category via
  // [[PROPOSAL:action=categorize|category=<Name>|scope=<...>]] and the user
  // just said "yes". Match the category to the local CoA and apply it.
  useActionListener("apply-categorize-proposal", async (payload) => {
    if (!payload?.category) return;
    const acctList = acctsRef.current || [];
    const needle = String(payload.category).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    // Prefer exact-name match; fall back to "name contains needle".
    // Feb 2026 — removed the loose "needle contains name" fallback that
    // silently bound "Rental Income" → any account with "Income" in the
    // name (e.g. Interest Income). If no confident match, refuse and
    // toast — the AiPanel now has a create-then-categorize path for
    // truly-missing categories.
    const active = acctList.filter(a => !a.retired_at);
    let match = active.find(a => norm(a.name) === needle);
    if (!match) match = active.find(a => norm(a.name).includes(needle));
    if (!match) {
      toast.error(`No account named "${payload.category}" in your Chart of Accounts. Ask the AI to create one.`);
      return;
    }
    const sel = selectedRef.current;
    if (sel && sel.size > 0) {
      // Route through the existing bulk path.
      try {
        setBusy(true);
        const r = await api.post(`/companies/${currentId}/transactions/bulk-reclassify`, {
          transaction_ids: [...sel],
          category_account_id: match.id,
        });
        toast.success(`Reclassified ${r.data.updated} to ${match.name}.`);
        setSelected(new Set());
        if (r.data.rule_suggestion) setRuleSuggestion(r.data.rule_suggestion);
        loadRef.current?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Reclassify failed");
      } finally {
        setBusy(false);
      }
      return;
    }
    // No multi-selection — fall back to the focused row (hover-set).
    const focusedId = payload.focusedTxnId;
    if (focusedId) {
      try {
        await api.patch(`/companies/${currentId}/transactions/${focusedId}`, {
          category_account_id: match.id,
        });
        toast.success(`Recategorized to ${match.name}.`);
        loadRef.current?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Recategorize failed");
      }
      return;
    }
    toast.error("Select rows or hover a transaction first.");
  });
  // Reset ALL filter state on company switch — otherwise sticky filters from
  // the previous company (e.g. a date range) hide most rows on the new one
  // and users think the sync failed. (Real bug: 400 LLC had 1871 rows but a
  // sticky "last month" date filter from a prior company was masking them.)
  useEffect(() => {
    setSearch(""); setDebouncedSearch("");
    setDateFrom(""); setDateTo("");
    setFilter("all"); setPage(1);
  }, [currentId]);
  // Auto-refresh when webhooks silently backfill new rows in the background:
  //   • poll sync-status every 5s while syncing, 15s while idle, and
  //   • whenever pagination.total ≠ status.total_txns (i.e. new rows landed),
  //     re-fetch the current view so counts + rows update without a manual reload.
  //   • also refetch on tab visibility / focus (mirrors Dashboard behavior).
  const paginationTotalRef = useRef(0);
  useEffect(() => { paginationTotalRef.current = pagination.total || 0; }, [pagination.total]);
  useEffect(() => {
    if (!currentId) return;
    let cancelled = false, timer;
    let lastSyncStatus = null;
    // Track the last observed *company-wide* txn total from /sync-status so
    // "did new rows land?" is a whole-company delta — not a filtered-view
    // delta. Comparing against `paginationTotalRef` broke when a filter
    // was active (filtered total ≠ company total → poller thought rows had
    // changed every tick and clobbered the current view).
    let lastCompanyTotal = null;
    const poll = async () => {
      try {
        const r = await api.get(`/companies/${currentId}/sync-status`);
        if (cancelled) return;
        const s = r.data;
        const companyTotal = s.total_txns || 0;
        const rowsChanged = lastCompanyTotal !== null && companyTotal !== lastCompanyTotal;
        const flippedIdle = lastSyncStatus === "syncing" && s.status !== "syncing";
        if (rowsChanged || flippedIdle) loadRef.current();
        lastCompanyTotal = companyTotal;
        lastSyncStatus = s.status;
      } catch { /* ignore */ }
      const delay = lastSyncStatus === "syncing" ? 5_000 : 15_000;
      timer = setTimeout(poll, delay);
    };
    timer = setTimeout(poll, 5_000);
    const onFocus = () => { if (document.visibilityState === "visible") loadRef.current(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  const clearFilters = () => {
    setSearch(""); setDateFrom(""); setDateTo(""); setFilter("all");
    if (similarView) { similarReturnRef.current = null; setSimilarView(null); emitAction("similar-view-cleared"); }
    setFilterBankAccountId(""); setFilterCategoryId(""); setFilterContactId("");
    setFilterAmountMin(""); setFilterAmountMax(""); setDirectionFilter("");
  };
  const advancedActive = Boolean(
    filterBankAccountId || filterCategoryId || filterContactId ||
    filterAmountMin || filterAmountMax
  );
  const filtersActive = Boolean(debouncedSearch || dateFrom || dateTo || (filter !== "all") || advancedActive || directionFilter || similarView);

  // Voice-command deep-link support: /accounting/transactions?q=Walmart or
  // ?date_from=2026-07-15&date_to=2026-07-15. On mount / URL change, hydrate
  // the toolbar state so the user sees a filtered view immediately.
  const paramsKey = params.toString();
  useEffect(() => {
    const q       = params.get("q") || "";
    const df      = params.get("date_from") || "";
    const dt      = params.get("date_to") || "";
    // Accept either `filter` (voice deep-link convention) OR `status` (what
    // Month Close and other outbound links emit — see load() below which
    // writes `status=` back to the URL). Keeping both keeps every entrypoint
    // working without a rename cascade.
    const flt     = params.get("filter") || params.get("status") || "";
    if (q)   setSearch(q);
    if (df)  setDateFrom(df);
    if (dt)  setDateTo(dt);
    if (flt) setFilter(flt);
    // No else-branch: URL params ADD filters; they don't clear existing ones.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    const hl = params.get("highlight");
    const opn = params.get("open");
    if (!txns.length) return;
    // If the URL only has a search filter (no highlight id), auto-highlight
    // the top row when exactly one transaction matches — a nice UX for
    // "open the July 15th McDonald's transaction".
    if (!hl) {
      const wasVoiceLookup = params.get("q") || params.get("date_from");
      if (wasVoiceLookup && txns.length === 1) {
        const only = txns[0];
        setTimeout(() => {
          const row = document.querySelector(`[data-txn-id="${only.id}"]`);
          row?.scrollIntoView({ behavior: "smooth", block: "center" });
          row?.classList.add("bg-amber-50");
          setTimeout(() => row?.classList.remove("bg-amber-50"), 3000);
        }, 200);
      }
      return;
    }
    const target = txns.find(t => t.id === hl);
    if (target) {
      // Scroll to the row
      setTimeout(() => {
        const row = document.querySelector(`[data-txn-id="${hl}"]`);
        row?.scrollIntoView({ behavior: "smooth", block: "center" });
        row?.classList.add("bg-amber-50");
        setTimeout(() => row?.classList.remove("bg-amber-50"), 3000);
      }, 200);
      if (opn === "split") setSplitting(target);
    }
  }, [params, txns]);

  // Deep-link `?open=<txn_id>` from the Payments page — auto-opens
  // the Edit Transaction modal so pros can review the source txn
  // behind an auto-created payment in one click.
  useEffect(() => {
    const openId = params.get("open");
    if (!openId || openId === "split") return;
    if (!txns.length) return;
    const t = txns.find(x => x.id === openId);
    if (t && !editing) setEditing(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, txns]);

  const acctById = useMemo(() => Object.fromEntries(accts.map(a => [a.id, a])), [accts]);

  // Build the display-ordered list. When sortBy is null, keep the
  // server's natural order (newest-first); otherwise apply client-side
  // sort — numeric for date/amount, locale-compare for text columns.
  const sortedTxns = useMemo(() => {
    if (!sortBy) return txns;
    const dir = sortDir === "desc" ? -1 : 1;
    const txtAt = {
      contact:  (t) => (t.contact_name || "").toLowerCase(),
      merchant: (t) => (t.merchant || t.description || "").toLowerCase(),
      category: (t) => (acctById[t.category_account_id]?.name || t.category_account_name || "").toLowerCase(),
    };
    return [...txns].sort((a, b) => {
      if (sortBy === "date") {
        return dir * (new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
      }
      if (sortBy === "amount") {
        return dir * (Number(a.amount || 0) - Number(b.amount || 0));
      }
      const av = txtAt[sortBy]?.(a) ?? "";
      const bv = txtAt[sortBy]?.(b) ?? "";
      return dir * av.localeCompare(bv);
    });
  }, [txns, sortBy, sortDir, acctById]);

  const toggleSel = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const allChecked = txns.length > 0 && txns.every(t => selected.has(t.id));

  const bulkApprove = () => {
    if (!selected.size) return;
    setPendingConfirm({
      title: `Approve ${selected.size} transaction${selected.size === 1 ? "" : "s"}?`,
      body: "Marks the selected rows as human-reviewed. You can still edit individual rows afterwards.",
      confirmLabel: `Approve ${selected.size}`,
      variant: "primary",
      exec: async () => {
        setBusy(true);
        try {
          await api.post(`/companies/${currentId}/transactions/bulk-approve`, [...selected]);
          toast.success(`Approved ${selected.size} transactions.`);
          setSelected(new Set());
          load();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Approve failed");
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const bulkCreateRules = async () => {
    if (!selected.size) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/companies/${currentId}/rules/suggest-from-txns`,
        { transaction_ids: [...selected] },
      );
      let proposals = r.data?.proposals || [];
      // Fetch supporting lists once so the modal has classes / tags / etc.
      const [cls, tg] = await Promise.all([
        api.get(`/companies/${currentId}/classes`).catch(() => ({ data: {} })),
        api.get(`/companies/${currentId}/tags`).catch(() => ({ data: {} })),
      ]);
      if (proposals.length === 0) {
        // Fallback proposal — the backend skipped everything (rows
        // uncategorized or already covered by an existing rule), but
        // we still want to open the CreateRule popup so the user can
        // hand-author a rule. Seed the fields from the first selected
        // row's client-side data.
        const seed = txns.find(t => selected.has(t.id)) || {};
        const dup = r.data?.duplicates_skipped || 0;
        if (dup > 0) {
          toast.info(
            `${dup} row${dup === 1 ? " is" : "s are"} already covered by an existing rule — opening a blank rule you can tweak.`,
          );
        }
        const seedAmt = Number(seed.amount || 0);
        const dirHint = seedAmt < 0 ? "out" : seedAmt > 0 ? "in" : "both";
        proposals = [{
          match_field:  seed.contact_id ? "contact" : "merchant",
          match_value:  seed.contact_id || (seed.merchant || ""),
          match_value_display: seed.contact_name || seed.merchant || "",
          account_id:   seed.category_account_id || null,
          account_code: seed.category_account_code || "",
          account_name: seed.category_account_name || "",
          contact_id:   seed.contact_id || null,
          contact_name: seed.contact_name || null,
          class_id:     null,
          class_name:   null,
          tag_ids:      [],
          posting_mode: "auto",
          priority:     seed.contact_id ? 10 : 0,
          covered_txn_count: selected.size,
          direction_hint:    dirHint,
        }];
      }
      setRuleQueue({
        proposals,
        index: 0,
        classes: (cls.data?.classes || cls.data?.items || []).map(
          (x) => ({ id: x.id, name: x.name })),
        tags:    (tg.data?.tags    || tg.data?.items    || []).map(
          (x) => ({ id: x.id, name: x.name })),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suggestion failed");
    } finally {
      setBusy(false);
    }
  };

  // Called by ReclassifyPicker when the user chooses a target. We close
  // the picker and route through a confirm modal — the actual API write
  // fires only after the second explicit tap.
  const bulkReclassify = (categoryAccountId, contactId = null) => {
    if (!selected.size) return;
    const acct = accts.find(a => a.id === categoryAccountId);
    const contact = contactId
      ? filterContactOptions.find(c => c.id === contactId) : null;
    const count = selected.size;
    setReclassOpen(false);
    setPendingConfirm({
      title: `Reclassify ${count} transaction${count === 1 ? "" : "s"}?`,
      body: `Category → ${acct?.name || "target"}`
            + (contact ? ` · Contact → ${contact.name}` : ""),
      confirmLabel: `Reclassify ${count}`,
      variant: "primary",
      exec: () => runBulkReclassify(categoryAccountId, contactId),
    });
  };

  const runBulkReclassify = async (categoryAccountId, contactId) => {
    setBusy(true);
    try {
      const r = await api.post(`/companies/${currentId}/transactions/bulk-reclassify`, {
        transaction_ids: [...selected],
        category_account_id: categoryAccountId,
        contact_id: contactId || null,
      });
      const acct = accts.find(a => a.id === categoryAccountId);
      const contactTag = r.data.contact_applied
        ? ` and contact → ${r.data.contact_applied}`
        : "";
      showUndoToast(
        `Reclassified ${r.data.updated} txn(s) → ${acct?.name || "category"}${contactTag}`
        + (r.data.skipped_closed?.length
            ? `. Skipped ${r.data.skipped_closed.length} (closed period).`
            : ""),
        r.data.undo_token,
      );
      setSelected(new Set());
      if (r.data.rule_suggestion) setRuleSuggestion(r.data.rule_suggestion);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reclassify failed");
    } finally {
      setBusy(false);
    }
  };

  // Called by ContactPickerModal on selection. Route through a confirm.
  const bulkSetContact = (contactId) => {
    if (!selected.size) return;
    const contact = contactId
      ? filterContactOptions.find(c => c.id === contactId) : null;
    const label = contact?.name || "no contact (cleared)";
    const count = selected.size;
    setContactPickerOpen(false);
    setPendingConfirm({
      title: `Set contact on ${count} transaction${count === 1 ? "" : "s"}?`,
      body: contact
        ? `Contact → ${label}`
        : "This will clear the contact on all selected rows.",
      confirmLabel: contact ? `Set → ${label}` : `Clear on ${count}`,
      variant: contact ? "primary" : "danger",
      exec: () => runBulkSetContact(contactId),
    });
  };

  const runBulkSetContact = async (contactId) => {
    setBusy(true);
    try {
      const r = await api.post(`/companies/${currentId}/transactions/bulk-set-contact`, {
        transaction_ids: [...selected],
        contact_id: contactId || null,
      });
      const contact = filterContactOptions.find(c => c.id === contactId);
      const label = contact?.name || (contactId ? "contact" : "no contact");
      showUndoToast(
        `Updated ${r.data.updated} txn(s) → ${label}`
        + (r.data.skipped_closed?.length
            ? `. Skipped ${r.data.skipped_closed.length} (closed period).`
            : ""),
        r.data.undo_token,
      );
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Set contact failed");
    } finally {
      setBusy(false);
    }
  };

  // Consolidated "Bulk Update" flow — routes through the same confirm
  // gate as Reclassify/Set-Contact and lets the user change Contact +
  // Category + Approve in one round-trip. Empty fields are skipped so
  // an "approve only" or "contact only" pass still works. After the
  // write, the existing `rule_suggestion` banner (from bulk-reclassify)
  // auto-prompts "Make a rule?" if a category was applied.
  const bulkUpdateApply = (patch /* { contact_id?, category_account_id?, approve? } */) => {
    if (!selected.size) return;
    const acct    = patch.category_account_id
      ? accts.find(a => a.id === patch.category_account_id) : null;
    const contact = patch.contact_id
      ? filterContactOptions.find(c => c.id === patch.contact_id) : null;
    const count = selected.size;
    // Snapshot the selected IDs at confirm-open time so a follow-up rule
    // suggestion still targets these rows even after `runBulkUpdate`
    // clears the selection.
    const idsSnapshot = [...selected];
    setBulkUpdateOpen(false);
    const bodyLines = [];
    if (acct)              bodyLines.push(`Category → ${acct.name}`);
    if (contact)           bodyLines.push(`Contact → ${contact.name}`);
    if (patch.approve)     bodyLines.push("Mark as human-reviewed");
    setPendingConfirm({
      title: `Bulk update ${count} transaction${count === 1 ? "" : "s"}?`,
      body: bodyLines.join(" · ") || "No changes selected.",
      confirmLabel: `Update ${count}`,
      variant: "primary",
      exec: () => runBulkUpdate(patch),
      confirmAndRuleLabel: `Update ${count} & Make rule`,
      execAndRule: async () => {
        await runBulkUpdate(patch);
        // Ask the backend for rule proposals against the just-updated rows.
        try {
          const r = await api.post(`/companies/${currentId}/rules/suggest-from-txns`, {
            transaction_ids: idsSnapshot,
          });
          let proposals = r.data?.proposals || [];
          const [cls, tg] = await Promise.all([
            api.get(`/companies/${currentId}/classes`).catch(() => ({ data: {} })),
            api.get(`/companies/${currentId}/tags`).catch(() => ({ data: {} })),
          ]);
          if (proposals.length === 0) {
            // Same fallback as `bulkCreateRules` — always open the
            // CreateRule popup after a bulk update, even when the
            // backend has nothing new to suggest.
            const seed = txns.find(t => idsSnapshot.includes(t.id)) || {};
            const seedAmt = Number(seed.amount || 0);
            const dirHint = seedAmt < 0 ? "out" : seedAmt > 0 ? "in" : "both";
            const usedContact = patch.contact_id || seed.contact_id;
            const usedCategory = patch.category_account_id;
            const usedAcct = usedCategory ? accts.find(a => a.id === usedCategory) : null;
            proposals = [{
              match_field:  usedContact ? "contact" : "merchant",
              match_value:  usedContact || (seed.merchant || ""),
              match_value_display: (usedContact ? (filterContactOptions.find(c => c.id === usedContact)?.name) : seed.merchant) || "",
              account_id:   usedCategory || seed.category_account_id || null,
              account_code: usedAcct?.code || seed.category_account_code || "",
              account_name: usedAcct?.name || seed.category_account_name || "",
              contact_id:   usedContact || null,
              contact_name: null,
              class_id:     null,
              class_name:   null,
              tag_ids:      [],
              posting_mode: "auto",
              priority:     usedContact ? 10 : 0,
              covered_txn_count: idsSnapshot.length,
              direction_hint:    dirHint,
            }];
          }
          setRuleQueue({
            proposals,
            index: 0,
            classes: (cls.data?.classes || cls.data?.items || []).map(x => ({ id: x.id, name: x.name })),
            tags:    (tg.data?.tags    || tg.data?.items    || []).map(x => ({ id: x.id, name: x.name })),
          });
        } catch (e) {
          toast.error(`Rule suggestion failed: ${e.response?.data?.detail || e.message}`);
        }
      },
    });
  };

  const runBulkUpdate = async (patch) => {
    const ids = [...selected];
    setBusy(true);
    try {
      let updated = 0;
      let ruleSug = null;
      let undoToken = null;
      // Category + optional contact go through bulk-reclassify — it
      // handles both in a single write and returns the rule-suggestion
      // banner if applicable.
      if (patch.category_account_id) {
        const r = await api.post(`/companies/${currentId}/transactions/bulk-reclassify`, {
          transaction_ids: ids,
          category_account_id: patch.category_account_id,
          contact_id: patch.contact_id || null,
        });
        updated  = r.data?.updated ?? ids.length;
        ruleSug  = r.data?.rule_suggestion || null;
        undoToken = r.data?.undo_token;
      } else if (patch.contact_id) {
        // Contact-only path.
        const r = await api.post(`/companies/${currentId}/transactions/bulk-set-contact`, {
          transaction_ids: ids,
          contact_id: patch.contact_id,
        });
        updated  = r.data?.updated ?? ids.length;
        undoToken = r.data?.undo_token;
      }
      // Approve as a separate call — order is intentional: categorize
      // first (so approve doesn't post an uncategorized row), then flip
      // the reviewed flag.
      if (patch.approve) {
        await api.post(`/companies/${currentId}/transactions/bulk-approve`, ids);
        updated = updated || ids.length;
      }
      const parts = [];
      if (patch.category_account_id) {
        const a = accts.find(x => x.id === patch.category_account_id);
        parts.push(`category → ${a?.name || "target"}`);
      }
      if (patch.contact_id) {
        const c = filterContactOptions.find(x => x.id === patch.contact_id);
        parts.push(`contact → ${c?.name || "contact"}`);
      }
      if (patch.approve) parts.push("approved");
      const msg = `Updated ${updated} txn(s)${parts.length ? ` — ${parts.join(", ")}` : ""}`;
      if (undoToken) showUndoToast(msg, undoToken);
      else           toast.success(msg);
      setSelected(new Set());
      if (ruleSug) setRuleSuggestion(ruleSug);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBusy(false);
    }
  };

  // Shared undo-toast helper used by every bulk endpoint that
  // returns an `undo_token`. Reads the backend `POST /bulk-actions/
  // {undo_id}/undo` route to restore the pre-image.
  const showUndoToast = (message, undoToken) => {
    if (!undoToken) {
      toast.success(message);
      return;
    }
    toast.success(message, {
      duration: 15000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const u = await api.post(
              `/companies/${currentId}/bulk-actions/${undoToken}/undo`
            );
            toast.success(`Undone · restored ${u.data.restored} txn(s)`);
            load();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Undo failed");
          }
        },
      },
    });
  };

  // Inline "create new contact" hook used by the ContactPickerModal.
  // Returns {id, name} on success so the modal can immediately apply the
  // fresh contact to the current bulk-selection. We also splice the new
  // row into `filterContactOptions` so subsequent picker opens (in the
  // same tab, before the next debounced reload) still see it.
  const createContactInline = async (name) => {
    try {
      const r = await api.post(`/companies/${currentId}/contacts`, {
        name,
        type: "vendor",       // sensible default for txn-tagging flows
      });
      const created = r?.data;
      if (created?.id) {
        setFilterContactOptions((prev) => {
          if (prev.some((c) => c.id === created.id)) return prev;
          return [...prev, { id: created.id, name: created.name || name }];
        });
        return { id: created.id, name: created.name || name };
      }
      toast.error("Failed to create contact");
      return null;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create contact");
      return null;
    }
  };

  const acceptRuleSuggestion = async () => {
    if (!ruleSuggestion) return;
    try {
      const r = await api.post(`/companies/${currentId}/rules`, {
        match_type: "merchant_contains",
        match_value: ruleSuggestion.merchant,
        account_code: ruleSuggestion.account_code,
        apply_to_existing: true,
      });
      toast.success(
        `Rule created: "${ruleSuggestion.merchant}" → ${ruleSuggestion.account_name}`
        + (r.data.applied ? ` (applied to ${r.data.applied} existing txns)` : "")
      );
      setRuleSuggestion(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create rule");
    }
  };

  const sampleMutate = (id, patch) => {
    sampleRef.current = patch === null
      ? sampleRef.current.filter((t) => t.id !== id)
      : sampleRef.current.map((t) => (t.id === id ? { ...t, ...patch } : t));
    toast.message("Sample data — nothing is saved", { duration: 1800 });
    load();
  };
  const approve = async (id, anchor = null) => {
    if (sampleMode) return sampleMutate(id, { human_reviewed: true, posted: true, needs_review: false });
    let r;
    try {
      r = await api.post(`/companies/${currentId}/transactions/${id}/approve-with-suggestion`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve that transaction.");
      return;
    }
    load();
    const { similar, rule_exists } = r.data || {};
    if (similar?.count) {
      setSimilarApprove({ similar, ruleExists: !!rule_exists, anchor });
      emitAction("ai-bulk-approve-prompt", { similar, rule_exists: !!rule_exists, ids: (similar.items || []).map(i => i.id) });
      emitAction("ai-open");
    }
  };
  const unapprove = async (id) => {
    if (sampleMode) return sampleMutate(id, { human_reviewed: false, posted: false });
    await api.post(`/companies/${currentId}/transactions/${id}/unapprove`);
    load();
  };
  const toggleApprove = (t, e) => {
    if (t.human_reviewed) return unapprove(t.id);
    if (isUncategorizedTxn(t)) {
      toast.error("Pick a category first — Uncategorized Expense/Income can't be approved.");
      emitAction("ai-tell-me-about", { txn: t });
      emitAction("ai-open");
      return;
    }
    return approve(t.id, e ? { x: e.clientX, y: e.clientY } : null);
  };
  const recategorize = async (id) => {
    if (sampleMode) return sampleMutate(id, { ai_source: "ai", ai_confidence: 0.93, ai_reasoning: "Re-categorized (sample)." });
    setBusy(true);
    await api.post(`/companies/${currentId}/ai/recategorize/${id}`);
    setBusy(false); toast.success("Re-categorized by AI"); load();
  };
  const updateCategory = async (id, acctId) => {
    if (sampleMode) {
      const a = accts.find((x) => x.id === acctId);
      return sampleMutate(id, { category_account_id: acctId, category_account_name: a?.name || null, category_account_code: a?.code || null });
    }
    await api.patch(`/companies/${currentId}/transactions/${id}`, { category_account_id: acctId });
    load();
  };
  const updateContact = async (id, contactId) => {
    if (sampleMode) return sampleMutate(id, { contact_id: contactId || null });
    try {
      await api.patch(`/companies/${currentId}/transactions/${id}`, { contact_id: contactId || "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update the contact.");
    }
  };
  const del = async (id) => {
    if (sampleMode) return sampleMutate(id, null);
    if (!confirm("Delete this transaction?")) return;
    await api.delete(`/companies/${currentId}/transactions/${id}`);
    load();
  };

  const [xferBusy, setXferBusy] = useState(false);
  const [xferPreview, setXferPreview] = useState(null);
  const [similarApprove, setSimilarApprove] = useState(null); // { similar, ruleExists }
  const detectTransfers = async () => {
    if (xferBusy || !currentId) return;
    setXferBusy(true);
    try {
      const r = await api.post(
        `/companies/${currentId}/transactions/detect-transfers`,
        { dry_run: true }
      );
      const pairs = r.data?.pairs || [];
      if (pairs.length === 0) {
        toast.success("No unresolved internal transfers found — you're clean.");
        return;
      }
      setXferPreview(pairs);
    } catch (e) {
      toast.error("Couldn't scan for transfers.");
    } finally { setXferBusy(false); }
  };
  const applyTransfers = async () => {
    if (xferBusy || !currentId) return;
    setXferBusy(true);
    try {
      const r = await api.post(
        `/companies/${currentId}/transactions/detect-transfers`,
        { dry_run: false }
      );
      const n = r.data?.updated || 0;
      const pairs = (r.data?.pairs || []).length;
      toast.success(`Booked ${pairs} internal transfer${pairs === 1 ? "" : "s"} (${n} rows moved to the transfer clearing account).`);
      setXferPreview(null);
      load(); loadRollup();
    } catch (e) {
      toast.error("Couldn't apply transfers.");
    } finally { setXferBusy(false); }
  };

  return (
    <div className="space-y-4">
      {txnTourOpen && (
        <ChatReviewTour beats={TXN_BEATS} chapters={TXN_CHAPTERS} finaleLabel="Let's go 🎉" onClose={closeTxnTour} layout="top-bar" barAnchor="txn-table-wrap" />
      )}
      {sampleMode && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="txn-sample-banner">
          <Sparkles size={13} className="text-amber-600 shrink-0" />
          <span><b>Sample data for the tour</b> — these rows aren't yours and nothing you click here is saved. Your real transactions come back when the tour ends.</span>
        </div>
      )}
      {tourInviteOpen && !txnTourOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px]" data-testid="txn-tour-invite">
          <div className="w-[min(460px,92vw)] rounded-2xl border border-slate-200 bg-white shadow-2xl p-6 relative overflow-hidden">
            {fromCheckout && (
              <div className="pointer-events-none absolute -top-24 -right-24 w-48 h-48 rounded-full bg-emerald-100/40 blur-3xl" aria-hidden />
            )}
            {fromCheckout ? (
              <>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-600 font-semibold" data-testid="txn-checkout-congrats">
                  <PartyPopper size={13} /> You're all set
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mt-1">Welcome aboard — your books are ready.</h3>
                <p className="text-[14px] text-slate-600 mt-2 leading-relaxed">
                  Your plan is active and this is your <b>Transactions</b> page — where everything lands, gets categorized, and gets approved. Want a one-minute tour of how it works?
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-600 font-semibold"><Lightbulb size={13} /> Welcome to Transactions</div>
                <h3 className="text-lg font-semibold text-slate-900 mt-1">Want a quick tour?</h3>
                <p className="text-[14px] text-slate-600 mt-2 leading-relaxed">
                  About a minute: how to find what needs you, approve with one click, and hand anything confusing to the assistant. You can replay it anytime from the <b>Tour</b> button.
                </p>
              </>
            )}
            <div className="mt-5 flex items-center gap-2">
              <button type="button" onClick={() => closeTourInvite(true)} data-testid="txn-tour-invite-yes"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-md">
                <Lightbulb size={14} /> {fromCheckout ? "Show me around" : "Show me"}
              </button>
              <button type="button" onClick={() => closeTourInvite(false)} data-testid="txn-tour-invite-no"
                      className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm text-slate-600 hover:text-slate-900 hover:border-slate-300">
                {fromCheckout ? "Skip for now" : "Not now"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Step2Tour open={step2TourOpen} onDone={closeStep2Tour} />
      <Step3BTour open={step3bTourOpen} onDone={closeStep3BTour} />
      <MonthCloseBreadcrumb />
      <LabV3ReviewBanner companyId={currentId} />
      {(params.get("from") === "gl" || params.get("from") === "payments") && (
        <nav
          aria-label="Breadcrumb"
          data-testid="transactions-source-breadcrumb"
          className="text-sm text-slate-500 flex items-center gap-2"
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:text-slate-900 hover:underline"
            data-testid="transactions-source-back-link"
          >
            ← {params.get("from") === "payments" ? "Payments" : "General Ledger"}
          </button>
          <span aria-hidden="true">/</span>
          <span className="text-slate-900 font-medium">Transaction</span>
        </nav>
      )}
      <CleanupCopilot
        currentId={currentId}
        sampleProgress={sampleMode ? sampleProgress(sampleRef.current) : null}
        autoTrigger={params.get("auto") === "1"}
        hideChips={true}
        forceStep={isLetsReview ? 2 : (isNoContactReview ? 3 : null)}
        onApplyAction={(a) => {
          // Filter the list first so the user sees exactly what the AI is about
          // to touch, then kick off a conversational inquiry in the AI panel.
          if (a.kind === "contact_in_uncat") {
            // Uncategorized tab removed — route to "Unapproved" (the closest
            // visible tab that shows AI-categorized-and-uncategorized alike),
            // then narrow via search.
            setFilter("unapproved");
            setSearch(a.contact_name || "");
          } else if (a.kind === "contact_split") {
            setFilter("all");
            setSearch(a.contact_name || "");
            setView("rollup");
          } else if (a.kind === "flagged_batch") {
            setFilter("review");
          } else if (a.kind === "filter_uncat") {
            // Fallback: no per-contact cluster, but uncategorized rows exist —
            // filter the table and let the CPA pick them off manually.
            setFilter("unapproved");
            setSearch("");
            setPage(1);
            return;
          } else if (a.kind === "filter_flagged") {
            // Fallback: no per-contact cluster, but flagged rows exist —
            // filter the table without starting the one-at-a-time chat.
            setFilter("review");
            setSearch("");
            setPage(1);
            return;
          }
          setPage(1);
          emitAction("cleanup-inquiry", { action: a });
        }}
        onStartSession={(flaggedCount) => {
          setFilter("review");
          setPage(1);
          emitAction("cleanup-inquiry", { action: { kind: "flagged_batch", count: flaggedCount || 0, label: "Flagged for review" } });
        }}
      />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className={isReviewMode ? "flex-1 min-w-0" : ""}>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {isLetsReview
              ? "AI Transaction Questions"
              : isNoContactReview
                ? "No-Contact Review"
                : "Transactions"}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isLetsReview
              ? "One vendor at a time. Answer the AI's questions and post them in bulk."
              : isNoContactReview
                ? "One group at a time. Bank-feed rows without a contact, grouped by description."
                : "AI has posted the confident ones. Review the flagged. Hover a row to give the assistant context."}
          </p>
        </div>
        {isLetsReview && lrContactName && (
          <div
            className="w-[420px] shrink-0 rounded-lg bg-white border border-cyan-400 ring-1 ring-cyan-100 shadow-sm px-4 py-3"
            data-testid="lets-review-info-box"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Contact {lrIdx} of {lrTotal}
              </span>
              {(lrCount > 0 || lrTotalAmount) ? (
                <span
                  className="text-[10px] text-slate-500 tabular-nums"
                  data-testid="lets-review-contact-totals"
                >
                  <span data-testid="lets-review-contact-count">
                    {lrCount.toLocaleString()} txn{lrCount === 1 ? "" : "s"}
                  </span>
                  <span className="mx-1">·</span>
                  <span data-testid="lets-review-contact-total-amount">
                    {fmtMoney(lrTotalAmount)}
                  </span>
                </span>
              ) : null}
            </div>
            <div
              className="mt-0.5 font-heading font-semibold text-base text-slate-900 truncate"
              title={lrContactName}
            >
              {lrContactName}
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                Bulk-categorize all {(txns || []).length} row{(txns || []).length === 1 ? "" : "s"}
              </label>
              <button
                data-testid="lets-review-bulk-approve"
                onClick={applyBulkCategoryPreview}
                disabled={!bulkPreviewAcctId || bulkCatBusy || (txns || []).length === 0}
                title={bulkPreviewAcctId ? "Save this category to every visible row for this contact." : "Pick a category below to enable Approve."}
                className="text-[11px] font-semibold rounded-md border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 hover:border-emerald-600 px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:border-slate-300"
              >
                {bulkCatBusy ? "Saving…" : "Approve"}
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 min-w-0" data-testid="lets-review-bulk-category">
                <AccountPicker
                  value={bulkPreviewAcctId}
                  accounts={(accts || []).filter((a) =>
                    // Full chart-of-accounts access — CPAs asked to be
                    // able to bulk-code to any account type (revenue,
                    // equity, liability, etc.) not just expenses. Only
                    // the system Uncategorized sinks (9999/6999/4999)
                    // are excluded so bulk actions can't loop rows
                    // back into the review queue they came from.
                    !["9999", "6999", "4999"].includes(a.code)
                  )}
                  onChange={(id) => previewBulkCategory(id)}
                  companyId={currentId}
                  testId="lets-review-bulk-category-picker"
                />
              </div>
              <button
                onClick={() => letsReviewNav.prev && letsReviewNav.prev()}
                disabled={!letsReviewNav.prev}
                data-testid="lets-review-prev"
                className="text-[11px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                ← Prev
              </button>
              <button
                onClick={() => letsReviewNav.next && letsReviewNav.next()}
                disabled={!letsReviewNav.next}
                data-testid="lets-review-next"
                className="text-[11px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                Next →
              </button>
            </div>
          </div>
        )}
        {isNoContactReview && ncrLabel && (
          <div
            className="w-[420px] shrink-0 rounded-lg bg-white border border-cyan-400 ring-1 ring-cyan-100 shadow-sm px-4 py-3"
            data-testid="no-contact-review-info-box"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {ncrIsListMode ? "All Groups View" : `Group ${lrIdx} of ${lrTotal}`}
              </span>
              {ncrIsListMode ? (
                <span
                  className="text-[10px] text-slate-500 tabular-nums"
                  data-testid="no-contact-review-all-groups-count"
                >
                  {lrTotal.toLocaleString()} group{lrTotal === 1 ? "" : "s"}
                </span>
              ) : (lrCount > 0 || lrTotalAmount) ? (
                <span
                  className="text-[10px] text-slate-500 tabular-nums"
                  data-testid="no-contact-review-group-totals"
                >
                  <span data-testid="no-contact-review-group-count">
                    {lrCount.toLocaleString()} txn{lrCount === 1 ? "" : "s"}
                  </span>
                  <span className="mx-1">·</span>
                  <span data-testid="no-contact-review-group-total-amount">
                    {fmtMoney(lrTotalAmount)}
                  </span>
                </span>
              ) : null}
            </div>
            <div
              className="mt-0.5 font-heading font-semibold text-base text-slate-900 truncate"
              title={ncrIsListMode ? "All no-contact groups" : ncrLabel}
            >
              {ncrIsListMode ? `All Groups View (${lrTotal})` : ncrLabel}
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                {ncrIsListMode
                  ? "Editing all groups in list view"
                  : `Bulk-categorize all ${(txns || []).length} row${(txns || []).length === 1 ? "" : "s"}`}
              </label>
              {!ncrIsListMode && (
                <button
                  data-testid="no-contact-review-bulk-approve"
                  onClick={applyBulkCategoryPreview}
                  disabled={!bulkPreviewAcctId || bulkCatBusy || (txns || []).length === 0}
                  title={bulkPreviewAcctId ? "Save this category to every visible row in this group." : "Pick a category below to enable Approve."}
                  className="text-[11px] font-semibold rounded-md border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 hover:border-emerald-600 px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:border-slate-300"
                >
                  {bulkCatBusy ? "Saving…" : "Approve"}
                </button>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2">
              {ncrIsListMode ? (
                // In list mode the picker + Prev/Next are hidden but we
                // keep a flex spacer of the same height so the box's
                // total footprint (2 lines under the label) stays
                // identical to stepper mode — the surrounding layout
                // doesn't reflow when toggling.
                <div className="flex-1 min-w-0 h-[26px]" aria-hidden="true" />
              ) : (
                <>
                  <div className="flex-1 min-w-0" data-testid="no-contact-review-bulk-category">
                    <AccountPicker
                      value={bulkPreviewAcctId}
                      accounts={(accts || []).filter((a) =>
                        !["9999", "6999", "4999"].includes(a.code)
                      )}
                      onChange={(id) => previewBulkCategory(id)}
                      companyId={currentId}
                      testId="no-contact-review-bulk-category-picker"
                    />
                  </div>
                  <button
                    onClick={() => noContactReviewNav.prev && noContactReviewNav.prev()}
                    disabled={!noContactReviewNav.prev}
                    data-testid="no-contact-review-prev"
                    className="text-[11px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={() => noContactReviewNav.next && noContactReviewNav.next()}
                    disabled={!noContactReviewNav.next}
                    data-testid="no-contact-review-next"
                    className="text-[11px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                  >
                    Next →
                  </button>
                </>
              )}
              <NoContactReviewListToggle />
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {!isReviewMode && (
            <div className="inline-flex rounded-md border bg-white overflow-hidden">
              {[
                { k: "all",           label: "All" },
                { k: "unapproved",    label: "To do" },
                { k: "reviewed",      label: "Approved" },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  data-testid={k === "review" ? TID.txnFilterReview : `txn-filter-${k}`}
                  onClick={() => { setFilter(k); setPage(1); }}
                  className={`px-3 py-1.5 text-xs font-medium border-r border-slate-200 last:border-r-0 ${filter === k ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                >
                  {label}
                  {filter === k && (
                    <span data-testid={`txn-filter-count-${k}`}
                          className="ml-1.5 px-1.5 py-0.5 rounded bg-white/20 font-mono-num">
                      {pagination.total}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {verifyCount > 0 && (
            <button
              type="button"
              onClick={() => setFilter(filter === "receipt_verify" ? "all" : "receipt_verify")}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${filter === "receipt_verify" ? "bg-amber-600 text-white border-amber-600" : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"}`}
              title="Receipts matched approximately — confirm or fix each one"
              data-testid="txn-receipts-verify-chip"
            >
              <Paperclip size={12} /> {verifyCount} receipt{verifyCount === 1 ? "" : "s"} to verify
            </button>
          )}
          {!isReviewMode && (
            <button
              type="button"
              data-testid="txn-tour-btn"
              onClick={startTxnTour}
              title="Show me how this page works"
              className="inline-flex items-center gap-1 px-2 h-7 rounded-full text-slate-600 hover:bg-amber-50 hover:text-amber-700 text-xs font-medium"
            >
              <Lightbulb size={14} /> Tour
            </button>
          )}
          {!isReviewMode && (
            <button
              type="button"
              data-testid="uncat-sweep-btn"
              onClick={() => { emitAction("ai-open"); emitAction("uncat-sweep-start"); }}
              title="Walk through every uncategorized transaction with the assistant"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-sky-300 bg-sky-50 text-sky-700 text-xs font-medium hover:bg-sky-100"
            >
              <Sparkles size={13} /> Sweep uncategorized
            </button>
          )}
          {!isReviewMode && (
            <NewTransactionMenu
              onQuick={() => setCreating(true)}
              advanced={isAdvancedMode}
            />
          )}
        </div>
      </div>
      {/* Entity-type chip strip — orthogonal filter to the status
          tabs above. Lets the CPA slice the ledger by QBO entity
          without leaving the page. Uses the same `txn_type` field
          that the /sales-receipts and /credit-memos pages filter on.
          Only shown in Advanced accounting mode — regular business
          owners never need to slice by QBO entity. */}
      {!isReviewMode && isAdvancedMode && (
        <div
          className="flex items-center gap-1.5 flex-wrap"
          data-testid="txn-type-chip-strip"
        >
          <span className="text-[11px] uppercase tracking-wide text-slate-400 mr-1">
            Entity
          </span>
          {[
            { k: "",              label: "All types" },
            { k: "Purchase",      label: "Expenses" },
            { k: "SalesReceipt",  label: "Sales Receipts" },
            { k: "Deposit",       label: "Deposits" },
            { k: "CreditMemo",    label: "Credit Memos" },
            { k: "RefundReceipt", label: "Refund Receipts" },
            { k: "Transfer",      label: "Transfers" },
          ].map(({ k, label }) => {
            const active = txnTypeFilter === k;
            const pending = k ? (pendingByType[k] || 0) : 0;
            return (
              <button
                key={k || "all"}
                onClick={() => { setTxnTypeFilter(k); setPage(1); }}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`txn-type-chip-${k || "all"}`}
              >
                {label}
                {/* Amber pending-review counter — only shown when
                    silent-matched pairs of this txn_type are still
                    awaiting the CPA's confirm-or-unlink decision. */}
                {pending > 0 && (
                  <span
                    className={`min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold tabular-nums grid place-items-center ${
                      active
                        ? "bg-amber-300/90 text-amber-950"
                        : "bg-amber-100 text-amber-800"
                    }`}
                    title={`${pending} pair${pending === 1 ? "" : "s"} awaiting bank match review`}
                    data-testid={`txn-type-chip-pending-${k}`}
                  >
                    {pending}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Filter toolbar: search + date range */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={TID.txnSearch}
            type="text"
            placeholder="Search merchant, description, or contact…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-8 py-1.5 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          {search && (
            <button
              data-testid={TID.txnSearchClear}
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label="Clear search"
            >
              <XCircle size={14} />
            </button>
          )}
        </div>
        <div className="inline-flex items-center rounded-md border border-slate-200 bg-white overflow-hidden" role="tablist" aria-label="Transactions view">
          <button
            data-testid="txn-view-list"
            title="List view"
            role="tab"
            aria-selected={view === "list"}
            onClick={() => setView("list")}
            className={`px-2 py-1.5 flex items-center ${view === "list" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <ListIcon size={14} />
          </button>
          <button
            data-testid="txn-view-rollup"
            title="Group by contact & category"
            role="tab"
            aria-selected={view === "rollup"}
            onClick={() => setView("rollup")}
            className={`px-2 py-1.5 flex items-center border-l border-slate-200 ${view === "rollup" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <LayoutGrid size={14} />
          </button>
        </div>
        <div className="inline-flex items-center gap-1 border rounded-md bg-white px-2 py-1">
          <Calendar size={13} className="text-slate-400" />
          <input
            data-testid={TID.txnDateFrom}
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="text-xs bg-transparent focus:outline-none font-mono-num text-slate-700"
            aria-label="From date"
          />
          <span className="text-slate-400 text-xs">–</span>
          <input
            data-testid={TID.txnDateTo}
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="text-xs bg-transparent focus:outline-none font-mono-num text-slate-700"
            aria-label="To date"
          />
        </div>
        <button
          data-testid="txn-filter-withdrawal"
          onClick={() => setDirectionFilter((d) => d === "out" ? "" : "out")}
          className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border ${
            directionFilter === "out"
              ? "border-rose-600 bg-rose-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
          }`}
          title="Toggle to show only withdrawals (money out)"
        >
          Withdrawal
        </button>
        <button
          data-testid="txn-filter-deposit"
          onClick={() => setDirectionFilter((d) => d === "in" ? "" : "in")}
          className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border ${
            directionFilter === "in"
              ? "border-emerald-600 bg-emerald-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
          }`}
          title="Toggle to show only deposits (money in)"
        >
          Deposit
        </button>
        <button
          data-testid="txn-advanced-toggle"
          onClick={() => setAdvancedOpen((v) => !v)}
          className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border ${
            advancedOpen || advancedActive
              ? "border-blue-600 bg-blue-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
          }`}
          title="Toggle advanced filters (accounts, categories, contacts, amount range)"
        >
          {advancedOpen ? "Hide advanced" : "Advanced filter"}
          {advancedActive && !advancedOpen && (
            <span className="ml-0.5 inline-block w-1.5 h-1.5 rounded-full bg-cyan-400" />
          )}
        </button>
        {filtersActive && (
          <button
            data-testid={TID.txnFiltersClear}
            onClick={clearFilters}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-slate-900 border border-transparent hover:border-slate-200 rounded"
          >
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      {similarView && (
        <div data-testid="similar-view-chip"
             className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 pl-3 pr-1.5 py-1 text-[12px] text-indigo-900">
          <Sparkles size={12} className="text-indigo-500" />
          <span>Showing <b>{similarView.label || "similar"}</b> · <b>{selected.size}</b> of {similarView.ids.length} ticked{similarView.count ? ` (focused + ${similarView.count} similar)` : ""} · unreviewed only</span>
          <button type="button" data-testid="similar-view-clear" onClick={clearSimilarView}
                  className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-indigo-700 hover:bg-indigo-100">
            <X size={11} /> Clear
          </button>
        </div>
      )}

      {advancedOpen && (
        <div
          data-testid="txn-advanced-panel"
          className="rounded-lg border border-slate-200 bg-white px-4 py-3"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Advanced filters
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Bank account
              </label>
              <select
                data-testid="txn-filter-bank"
                value={filterBankAccountId}
                onChange={(e) => setFilterBankAccountId(e.target.value)}
                className="w-full px-2 py-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-800"
              >
                <option value="">All accounts</option>
                {/* All Asset + Liability accounts from the COA — banks,
                    credit cards, loans, A/R, A/P, fixed assets, etc. —
                    grouped by type so the CPA can see which side of the
                    ledger each row belongs to. Backend's bank_account_id
                    filter already ORs on plaid_account_id so a single
                    COA id catches both Plaid-fed and manual entries. */}
                {(() => {
                  const list = (accts || []).filter(
                    (a) => ["asset", "liability"].includes(a.type),
                  );
                  const assets = list.filter((a) => a.type === "asset")
                    .sort((x, y) => String(x.code).localeCompare(String(y.code)));
                  const liabs = list.filter((a) => a.type === "liability")
                    .sort((x, y) => String(x.code).localeCompare(String(y.code)));
                  return (
                    <>
                      {assets.length > 0 && (
                        <optgroup label="Assets">
                          {assets.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} · {a.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {liabs.length > 0 && (
                        <optgroup label="Liabilities">
                          {liabs.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} · {a.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  );
                })()}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Category
              </label>
              <select
                data-testid="txn-filter-category"
                value={filterCategoryId}
                onChange={(e) => setFilterCategoryId(e.target.value)}
                className="w-full px-2 py-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-800"
              >
                <option value="">All categories</option>
                {(accts || [])
                  .filter((a) =>
                    ["expense", "income", "cost_of_goods_sold",
                     "other_income", "other_expense", "equity", "asset", "liability"].includes(a.type)
                  )
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} · {a.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Contact
              </label>
              <select
                data-testid="txn-filter-contact"
                value={filterContactId}
                onChange={(e) => setFilterContactId(e.target.value)}
                className="w-full px-2 py-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-800"
              >
                <option value="">All contacts</option>
                {filterContactOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Amount range
              </label>
              <div className="flex items-center gap-1">
                <input
                  data-testid="txn-filter-amount-min"
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filterAmountMin}
                  onChange={(e) => setFilterAmountMin(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-800 font-mono-num"
                />
                <span className="text-slate-400 text-xs">–</span>
                <input
                  data-testid="txn-filter-amount-max"
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filterAmountMax}
                  onChange={(e) => setFilterAmountMax(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-800 font-mono-num"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {ruleSuggestion && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 flex items-center gap-3"
          data-testid="txn-rule-suggestion-banner"
        >
          <Sparkles size={16} className="text-amber-700 flex-shrink-0" />
          <div className="flex-1 text-xs text-amber-900">
            You've reclassified <b>{ruleSuggestion.merchant}</b> to{" "}
            <b>{ruleSuggestion.account_name}</b> {ruleSuggestion.approvals} times.{" "}
            Turn this into an automatic rule?
          </div>
          <button
            onClick={acceptRuleSuggestion}
            data-testid="txn-rule-suggestion-accept"
            className="px-2.5 py-1 text-xs rounded-md bg-amber-700 text-white hover:bg-amber-800"
          >
            Create rule
          </button>
          <button
            onClick={() => setRuleSuggestion(null)}
            data-testid="txn-rule-suggestion-dismiss"
            className="px-2.5 py-1 text-xs rounded-md hover:bg-amber-100 text-amber-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {selected.size > 0 && (
        <div data-testid="txn-bulk-bar" className="rounded-md border border-indigo-200 bg-indigo-50 text-indigo-950 px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold">{selected.size} selected</span>
          <button data-testid={TID.txnBulkApprove} disabled={busy} onClick={bulkApprove}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
            <Check size={12} /> Approve all
          </button>
          <button
            data-testid="txn-bulk-update"
            disabled={busy}
            onClick={() => setBulkUpdateOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-1 rounded bg-white border border-indigo-300 text-indigo-800 text-xs font-medium hover:bg-indigo-100 disabled:opacity-50"
          >
            <SlidersHorizontal size={12} /> Bulk update
          </button>
          <button data-testid={TID.txnBulkCreateRules} disabled={busy} onClick={bulkCreateRules}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50">
            <Wand2 size={12} /> Make these rules
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-indigo-700 hover:text-indigo-950 hover:underline">Clear</button>
        </div>
      )}

      {reclassOpen && (
        <ReclassifyPicker
          accounts={accts}
          count={selected.size}
          onCancel={() => setReclassOpen(false)}
          onApply={bulkReclassify}
          contacts={filterContactOptions}
        />
      )}

      {receiptPopup && (
        <ReceiptPopup
          cid={currentId}
          rid={receiptPopup.rid}
          txn={receiptPopup.txn}
          onClose={() => setReceiptPopup(null)}
          onChanged={() => { load(); loadVerifyCount(); }}
          onSnapNew={() => { setReceiptPopup(null); setEditing(receiptPopup.txn); }}
        />
      )}
      {linkedDocPreview && (
        <LinkedDocPreview
          preview={linkedDocPreview}
          currentId={currentId}
          onClose={() => setLinkedDocPreview(null)}
        />
      )}

      {contactPickerOpen && (
        <ContactPickerModal
          contacts={filterContactOptions}
          count={selected.size}
          onCancel={() => setContactPickerOpen(false)}
          onApply={bulkSetContact}
          onCreateContact={createContactInline}
        />
      )}

      {bulkUpdateOpen && (
        <BulkUpdateModal
          currentId={currentId}
          count={selected.size}
          contacts={filterContactOptions}
          accounts={accts}
          onCancel={() => setBulkUpdateOpen(false)}
          onApply={bulkUpdateApply}
          onContactCreated={(c) => {
            setFilterContactOptions((prev) =>
              prev.some(x => x.id === c.id) ? prev : [...prev, { id: c.id, name: c.name }]);
          }}
          onAccountCreated={(a) => {
            setAccts((prev) => prev.some(x => x.id === a.id) ? prev : [...prev, a]);
          }}
        />
      )}

      {ruleQueue && !automationsGate.allowed && (() => { automationsGate.openUpgrade(); setRuleQueue(null); return null; })()}
      {ruleQueue && automationsGate.allowed && (
        <CreateRuleModal
          key={`rq-${ruleQueue.index}`}         /* remount → fresh state per proposal */
          currentId={currentId}
          accts={accts}
          contacts={filterContactOptions}
          classes={ruleQueue.classes}
          tags={ruleQueue.tags}
          setClasses={(fn) => setRuleQueue(q =>
            q ? { ...q, classes: typeof fn === "function" ? fn(q.classes) : fn } : q)}
          setTags={(fn) => setRuleQueue(q =>
            q ? { ...q, tags:    typeof fn === "function" ? fn(q.tags)    : fn } : q)}
          initialProposal={ruleQueue.proposals[ruleQueue.index]}
          queue={{
            current: ruleQueue.index + 1,
            total:   ruleQueue.proposals.length,
            onNext: (saved) => {
              const nextIdx = ruleQueue.index + 1;
              if (nextIdx >= ruleQueue.proposals.length) {
                setRuleQueue(null);
                setSelected(new Set());
                if (saved) load();
                toast.success("Done — all suggested rules processed.");
              } else {
                setRuleQueue({ ...ruleQueue, index: nextIdx });
              }
            },
            onSkip: () => {
              const nextIdx = ruleQueue.index + 1;
              if (nextIdx >= ruleQueue.proposals.length) {
                setRuleQueue(null);
              } else {
                setRuleQueue({ ...ruleQueue, index: nextIdx });
              }
            },
            onPrev: () => {
              const prevIdx = ruleQueue.index - 1;
              if (prevIdx >= 0) {
                setRuleQueue({ ...ruleQueue, index: prevIdx });
              }
            },
          }}
          onClose={() => {
            const remaining = ruleQueue.proposals.length - ruleQueue.index - 1;
            setRuleQueue(null);
            if (remaining > 0) {
              toast.info(`Skipped ${remaining} remaining suggestion${remaining === 1 ? "" : "s"}.`);
            }
          }}
        />
      )}

      {pendingConfirm && (
        <BulkConfirmModal
          title={pendingConfirm.title}
          body={pendingConfirm.body}
          confirmLabel={pendingConfirm.confirmLabel}
          variant={pendingConfirm.variant}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={async () => {
            const fn = pendingConfirm.exec;
            setPendingConfirm(null);
            if (fn) await fn();
          }}
          confirmAndRuleLabel={pendingConfirm.confirmAndRuleLabel}
          onConfirmAndRule={pendingConfirm.execAndRule ? async () => {
            const fn = pendingConfirm.execAndRule;
            setPendingConfirm(null);
            if (fn) await fn();
          } : undefined}
        />
      )}

      {isReviewMode && isReviewDone ? (
        // Step 2 / Step 3 fully cleared — replace the table area with the
        // NextStepCard so the copilot header + blue "Step N" card at the
        // top stay visible, and the CPA gets a clear handoff to the next
        // step (or Dashboard when the whole checklist is done). Matches
        // the Step 1 AI Cleanup Review empty-state UX.
        <div className="rounded-xl border bg-white p-6" data-testid="review-mode-done-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="text-emerald-500" size={18} />
            <div className="font-heading font-semibold text-slate-900">
              {isLetsReview ? "Nice — Step 2 is clean" : "Nice — Step 3 is clean"}
            </div>
          </div>
          <div className="text-sm text-slate-500 mb-4">
            {isLetsReview
              ? "No uncategorized vendor groups left for this company."
              : "No uncategorized no-contact transactions left for this company."}
          </div>
          <NextStepCard currentId={currentId} />
        </div>
      ) : (
      <div className="rounded-xl border bg-white overflow-hidden">
        {ncrIsListMode ? (
          // In-place list view — renders the same all-groups table the
          // dedicated /accounting/no-contact-review?view=list page uses,
          // but without the outer page chrome (title/breadcrumb/back-
          // to-dashboard) so it slots cleanly into this container.
          <ListModeView embedded />
        ) : view === "rollup" ? (
          <ContactRollup
            data={rollup}
            busy={rollupBusy}
            currentId={currentId}
            accts={accts}
            setAccts={setAccts}
            contactOptions={filterContactOptions}
            reload={() => loadRef.current?.()}
          />
        ) : (
        <>
        {isMobile ? (
          <MobileTxnCards
            txns={sortedTxns}
            accts={accts}
            updateCategory={updateCategory}
            currentId={currentId}
            onReload={() => loadRef.current?.()}
            onEdit={(t) => setEditing(t)}
            renderActions={(t) => (
              <>
                {(t.matched_receipt_id || t.receipt_id || t.veryfi_receipt_id) && (
                  <button type="button" onClick={() => setReceiptPopup({ rid: t.matched_receipt_id || t.receipt_id, txn: t })}
                    className={`mr-auto inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-medium ${t.receipt_match_status === "suggested" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-orange-50 text-orange-600 border-orange-200"}`}
                    title="View receipt" data-testid={`txn-receipt-badge-mobile-${t.id}`}>
                    <Paperclip size={12} /> {t.receipt_match_status === "suggested" ? "Verify receipt" : "Receipt"}
                  </button>
                )}
                <button
                  title={t.human_reviewed ? "Unapprove" : isUncategorizedTxn(t) ? "Pick a category before approving" : "Approve"}
                  data-testid="txn-approve-btn"
                  onClick={(e) => toggleApprove(t, e)}
                  className={`p-2 rounded-lg ${t.human_reviewed ? "bg-emerald-100 text-emerald-700" : isUncategorizedTxn(t) ? "text-slate-300" : "text-emerald-600 hover:bg-emerald-50"}`}
                >
                  <Check size={16} />
                </button>
                <button
                  title="Ask AI about this transaction"
                  data-testid={`txn-ai-${t.id}`}
                  onClick={() => {
                    setFocus({ id: t.id, merchant: t.merchant, amount: t.amount, date: t.date }, { pin: true });
                    emitAction("ai-open", { source: "row" });
                    emitAction("ai-tell-me-about", { txn: { id: t.id, merchant: t.merchant, description: t.description, contact_name: t.contact_name, amount: t.amount, date: t.date } });
                  }}
                  className="p-2 rounded-lg text-sky-500 hover:bg-sky-50"
                >
                  <Sparkles size={16} />
                </button>
                <RowMoreMenu
                  t={t}
                  onEdit={() => setEditing(t)}
                  onRecategorize={() => recategorize(t.id)}
                  onSplit={() => setSplitting(t)}
                  onLink={() => setLinking(t)}
                  onDelete={() => del(t.id)}
                  onAskClient={() => askClientRef.current?.(t)}
                />
              </>
            )}
          />
        ) : (
        <div ref={tableWrapRef} data-testid="txn-table-wrap" className={tableNarrow ? "" : "overflow-x-auto"}>
          {tableNarrow ? (
            <NarrowTxnCardList
              onViewReceipt={(t) => setReceiptPopup({ rid: t.matched_receipt_id || t.receipt_id, txn: t })}
              txns={txns}
              accts={accts}
              currentId={currentId}
              selected={selected}
              allChecked={allChecked}
              setSelected={setSelected}
              toggleSel={toggleSel}
              setFocus={setFocus}
              emitAction={emitAction}
              fmtDate={fmtDate}
              fmtMoney={fmtMoney}
              toggleApprove={toggleApprove}
              setEditing={setEditing}
              recategorize={recategorize}
              setSplitting={setSplitting}
              setLinking={setLinking}
              del={del}
              askClientRef={askClientRef}
              updateCategory={updateCategory}
              showProvenance={showProvenance}
              setLinkedDocPreview={setLinkedDocPreview}
            />
          ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b bg-slate-50">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" data-testid={TID.txnBulkCheckbox}
                    checked={allChecked}
                    onChange={(e) => setSelected(e.target.checked ? new Set(sortedTxns.map(t => t.id)) : new Set())} />
                </th>
                {[
                  { key: "date",     label: "Date",                   align: "left"  },
                  { key: "contact",  label: "Contact",                align: "left"  },
                  { key: "merchant", label: "Merchant / Description", align: "left"  },
                  { key: "category", label: "Category",               align: "left"  },
                  { key: "amount",   label: "Amount",                 align: "right" },
                ].map((col) => (
                  <th
                    key={col.key}
                    onClick={() => toggleSort(col.key)}
                    className={`px-3 py-2 cursor-pointer select-none hover:bg-slate-100 text-${col.align}`}
                    data-testid={`txn-th-${col.key}`}
                    aria-sort={sortBy === col.key ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""}`}>
                      {col.label}
                      <span className={`text-[10px] ${sortBy === col.key ? "text-slate-700" : "text-slate-300"}`}>
                        {sortBy === col.key ? (sortDir === "asc" ? "▲" : "▼") : "▲"}
                      </span>
                    </span>
                  </th>
                ))}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sortedTxns.map(t => {
                const rowActions = (
                  <div className="flex items-center gap-1 justify-end">
                    {(t.matched_receipt_id || t.receipt_id || t.veryfi_receipt_id) && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setReceiptPopup({ rid: t.matched_receipt_id || t.receipt_id, txn: t }); }}
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${t.receipt_match_status === "suggested" ? "bg-amber-50 text-amber-700 border-amber-300 ring-2 ring-amber-200" : "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"}`}
                        title={t.receipt_match_status === "suggested" ? "Receipt matched approximately — verify" : "View receipt"}
                        data-testid={`txn-receipt-badge-${t.id}`}
                      >
                        <Paperclip size={12} />
                      </button>
                    )}
                    <button
                      title={t.human_reviewed ? "Unapprove" : isUncategorizedTxn(t) ? "Pick a category before approving" : "Approve"}
                      data-testid={TID.txnApprove}
                      onClick={(e) => toggleApprove(t, e)}
                      className={
                        t.human_reviewed
                          ? "p-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          : isUncategorizedTxn(t)
                            ? "p-1 rounded text-slate-300 hover:bg-slate-100 cursor-not-allowed"
                            : "p-1 rounded hover:bg-emerald-100 text-emerald-600"
                      }
                    >
                      <Check size={14} />
                    </button>
                    <button
                      title="Ask AI about this transaction"
                      data-testid={`txn-ai-${t.id}`}
                      onClick={() => {
                        setFocus(
                          { id: t.id, merchant: t.merchant, amount: t.amount, date: t.date },
                          { pin: true }
                        );
                        emitAction("ai-open");
                        emitAction("ai-tell-me-about", {
                          txn: {
                            id: t.id,
                            merchant: t.merchant,
                            description: t.description,
                            contact_name: t.contact_name,
                            amount: t.amount,
                            date: t.date,
                          },
                        });
                      }}
                      className="p-1 rounded hover:bg-sky-100 text-sky-500"
                    >
                      <Sparkles size={14} />
                    </button>
                    <RowMoreMenu
                      t={t}
                      onEdit={() => setEditing(t)}
                      onRecategorize={() => recategorize(t.id)}
                      onSplit={() => setSplitting(t)}
                      onLink={() => setLinking(t)}
                      onDelete={() => del(t.id)}
                      onAskClient={() => askClientRef.current?.(t)}
                    />
                  </div>
                );
                return (
                <Fragment key={t.id}>
                <tr data-testid={TID.txnRow} data-txn-id={t.id}
                    onMouseEnter={() => setFocus({ id: t.id, merchant: t.merchant, amount: t.amount, date: t.date })}
                    onMouseLeave={() => setFocus(null)}
                    onClick={(e) => {
                      // Row-click opens the full Edit-transaction modal so the
                      // desktop table matches the mobile / narrow card views.
                      // Skip if the click landed on any interactive control
                      // inside the row (checkbox, category picker, split
                      // pill, more-menu, sparkle, linked-doc chip, etc.) so
                      // those keep working as before.
                      if (e.target.closest(
                        "button, input, select, textarea, a, label, [role='menu'], [role='menuitem'], [role='combobox'], [role='listbox']"
                      )) return;
                      setEditing(t);
                    }}
                    className="border-b hover:bg-slate-50 transition-colors cursor-pointer">
                  <td className="px-3 py-2">
                    <input type="checkbox" data-testid={TID.txnRowCheckbox}
                      checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600 font-mono-num">{fmtDate(t.date)}</td>
                  <td className="px-3 py-2 text-slate-700 max-w-[200px]">
                    <ContactPicker
                      value={t.contact_id || null}
                      name={t.contact_name}
                      logoUrl={t.contact_logo_url}
                      contacts={filterContactOptions}
                      onChange={(cid) => updateContact(t.id, cid)}
                      onCreate={createContactInline}
                      testId={`txn-contact-picker-${t.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 align-top max-w-[420px]">
                    {/* Merchant + description. Cap the column at ~420px
                        and wrap onto multiple lines with `break-words`
                        + `whitespace-normal` for long bank-feed strings
                        (e.g. "NV ENERGY NORTH DES:SPPC PYMT ID:…") so
                        the table never forces a horizontal scrollbar.
                        Long single tokens (no spaces) still break via
                        `break-all` so they can't overflow the cell. */}
                    <div className="flex items-start gap-2 min-w-0">
                      <div className="font-medium break-words break-all whitespace-normal leading-snug flex-1 min-w-0">
                        {t.merchant || t.description}
                      </div>
                      {/* Reconciliation indicator — only shown on rows
                          that were authored via a full-page editor
                          (Sales Receipt, Deposit, etc.) since bank-
                          feed rows are their own source of truth. */}
                      {["SalesReceipt", "Deposit", "Purchase",
                          "CreditMemo", "RefundReceipt"].includes(t.txn_type) && (
                        <MatchDot row={t} mode="compact" />
                      )}
                    </div>
                    {t.splits?.length > 0 && <div className="text-[10px] text-indigo-600 mt-0.5">Split into {t.splits.length}</div>}
                    {(t.linked_invoice_id || t.linked_bill_id) && (
                      <LinkedDocChip t={t}
                                     onOpen={setLinkedDocPreview}
                                     data-testid={`txn-${t.id}-linked-chip`} />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="inline-flex items-center gap-1 max-w-[220px]">
                      {showProvenance && (
                        <ProvenanceDot
                          source={t.categorization_source || t.ai_source}
                          matched={t.rule_matched}
                          semantic={t.rule_semantic}
                          confidence={t.rule_confidence ?? t.ai_confidence}
                          bucket={t.bucket}
                        />
                      )}
                      {t.splits?.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setEditing(t)}
                          data-testid={`txn-cat-split-pill-${t.id}`}
                          title={`Split across ${t.splits.length} accounts — click to view`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100"
                        >
                          <Split size={12} />
                          — Split ({t.splits.length}) —
                        </button>
                      ) : (
                        <>
                          <div className="min-w-0 flex-1" data-testid={TID.txnEditCategory}>
                            <AccountPicker
                              value={t.category_account_id || ""}
                              accounts={accts}
                              onChange={(id) => updateCategory(t.id, id)}
                              companyId={currentId}
                              testId={`txn-cat-picker-${t.id}`}
                            />
                          </div>
                          <AccountInfoTooltip
                            account={accts.find(a => a.id === t.category_account_id)}
                          />
                        </>
                      )}
                    </div>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono-num ${t.amount < 0 ? "text-slate-800" : "text-emerald-700 font-semibold"}`}>
                    {fmtMoney(t.amount)}
                  </td>
                  <td className="px-3 py-2">{rowActions}</td>
                </tr>
                </Fragment>
                );
              })}
              {!txns.length && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No transactions.</td></tr>
              )}
            </tbody>
          </table>
          )}
        </div>
        )}
        <PaginationBar
          pagination={pagination}
          pageSize={pageSize}
          setPageSize={setPageSize}
          page={page}
          setPage={setPage}
          visibleCount={txns.length}
          filtersActive={filtersActive}
          onClearFilters={clearFilters}
        />
        <AskClientLauncher registerRef={askClientRef} onAsked={load} />
        </>
        )}
      </div>
      )}

      {creating && <ManualTxnModal accts={accts} currentId={currentId} contactOptions={filterContactOptions} invoices={invoices} bills={bills} onClose={() => { setCreating(false); load(); }} />}
      {editing && <ManualTxnModal accts={accts} currentId={currentId} contactOptions={filterContactOptions} invoices={invoices} bills={bills} initialTxn={editing} onClose={() => { setEditing(null); load(); }} onOpenMultiLink={() => setLinking(editing)} />}
      {splitting && <SplitModal txn={splitting} accts={accts} currentId={currentId} onClose={() => { setSplitting(null); load(); }} />}
      {linking && <LinkModal txn={linking} invoices={invoices} bills={bills} currentId={currentId} sampleDocs={sampleMode ? { invoices: SAMPLE_INVOICES, bills: SAMPLE_BILLS } : null} onClose={() => { setLinking(null); load(); }} />}
      {similarApprove && (
        <SimilarApproveModal currentId={currentId} similar={similarApprove.similar} ruleExists={similarApprove.ruleExists}
                             anchor={similarApprove.anchor} onClose={() => setSimilarApprove(null)} />
      )}
      {xferPreview && (
        <Modal title={`Found ${xferPreview.length} internal-transfer pair${xferPreview.length === 1 ? "" : "s"}`}
               onClose={() => setXferPreview(null)}>
          <div className="text-xs text-slate-600 mb-3">
            Both legs of each pair will be booked to the <b>Inter-Account Transfer</b> equity account so neither leg hits your P&amp;L.
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2 mb-4" data-testid="detect-transfers-preview">
            {xferPreview.map((p, i) => (
              <div key={i} className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                <div className="flex justify-between items-center text-rose-700 font-medium">
                  <span>{fmtDate(p.debit_leg.date)} · {p.debit_leg.bank_account_name}</span>
                  <span className="font-mono-num">{fmtMoney(p.debit_leg.amount)}</span>
                </div>
                <div className="text-slate-500 truncate italic">{p.debit_leg.description}</div>
                <div className="flex justify-between items-center text-emerald-700 font-medium mt-1">
                  <span>{fmtDate(p.credit_leg.date)} · {p.credit_leg.bank_account_name}</span>
                  <span className="font-mono-num">+{fmtMoney(p.credit_leg.amount)}</span>
                </div>
                <div className="text-slate-500 truncate italic">{p.credit_leg.description}</div>
                {p.date_delta_days > 0 && (
                  <div className="text-[10px] text-slate-400 mt-1">Δ{p.date_delta_days} day{p.date_delta_days === 1 ? "" : "s"}</div>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="detect-transfers-apply"
              disabled={xferBusy}
              onClick={applyTransfers}
              className="flex-1 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {xferBusy ? "Applying…" : `Book all ${xferPreview.length} pair${xferPreview.length === 1 ? "" : "s"}`}
            </button>
            <button
              onClick={() => setXferPreview(null)}
              disabled={xferBusy}
              className="px-4 py-2 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PaginationBar({ pagination, pageSize, setPageSize, page, setPage, visibleCount, filtersActive, onClearFilters }) {
  const total = pagination?.total || 0;
  const pages = Math.max(1, pagination?.pages || 1);
  const currentPage = Math.min(pages, Math.max(1, pagination?.page || page));
  const startIdx = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIdx = total === 0 ? 0 : (currentPage - 1) * pageSize + visibleCount;

  const canPrev = currentPage > 1;
  const canNext = currentPage < pages;

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap border-t bg-slate-50/60 px-4 py-2.5">
      <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
        <span data-testid={TID.txnPageIndicator}>
          {total === 0
            ? (filtersActive ? "No transactions match these filters" : "No transactions")
            : <>Showing <span className="font-mono-num font-medium text-slate-900">{startIdx.toLocaleString()}</span>–<span className="font-mono-num font-medium text-slate-900">{endIdx.toLocaleString()}</span> of <span className="font-mono-num font-medium text-slate-900">{total.toLocaleString()}</span></>
          }
        </span>
        {filtersActive && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium">
            filtered
            {onClearFilters && total === 0 && (
              <button onClick={onClearFilters} className="ml-1 underline">clear</button>
            )}
          </span>
        )}
        <span className="text-slate-300">·</span>
        <label className="inline-flex items-center gap-1.5">
          <span className="text-slate-500">Rows</span>
          <select
            data-testid={TID.txnPageSize}
            value={pageSize}
            onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            className="border rounded px-1.5 py-0.5 bg-white text-xs font-mono-num"
          >
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-1">
        <button
          data-testid={TID.txnPagePrev}
          disabled={!canPrev}
          onClick={() => setPage(currentPage - 1)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-white text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100"
        >
          <ChevronLeft size={14} /> Prev
        </button>
        <span className="px-2 text-xs text-slate-600 font-mono-num">
          Page {currentPage} of {pages}
        </span>
        <button
          data-testid={TID.txnPageNext}
          disabled={!canNext}
          onClick={() => setPage(currentPage + 1)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-white text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export function Modal({ title, children, onClose, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    // Lock page scroll while modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid="modal-overlay"
    >
      <div
        className={`rounded-xl bg-white shadow-2xl w-full my-auto max-h-[90vh] flex flex-col ${wide ? "max-w-2xl" : "max-w-md"}`}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="modal-panel"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white rounded-t-xl z-10">
          <h3 className="font-heading font-semibold">{title}</h3>
          <button data-testid={TID.cancelBtn} onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

// Contact-rollup: alphabetized cards, one per contact, listing every category
// their transactions fall under with count + amount range. Perfect for
// spotting split categorizations (e.g. AT&T mostly in Utilities but 1 stray
// row in Inter-Account Transfer). Clicking a category row expands inline
// to show the underlying transactions — no navigation, no page change.
function ContactRollup({ data, busy, currentId, accts = [], setAccts = () => {}, contactOptions = [], reload }) {
  const fmtMoney = useMoneyFmt();
  const { classesEnabled, projectsEnabled } = useCompany();
  const contacts = data?.contacts || [];
  // Cache expanded rows: key = `${contactKey}||${categoryKey}` → txn list.
  const [expanded, setExpanded] = useState({});   // key → true/false
  const [cache, setCache] = useState({});          // key → txn[] (or "loading")
  // Classes list — only fetched when the flag is on so a company that
  // doesn't use Classes pays nothing.
  const [classOptions, setClassOptions] = useState([]);
  useEffect(() => {
    if (!classesEnabled || !currentId) return;
    api.get(`/companies/${currentId}/classes`)
      .then(r => setClassOptions(r.data?.classes || []))
      .catch(() => setClassOptions([]));
  }, [classesEnabled, currentId]);
  // Projects list — same lazy-fetch pattern.
  const [projectOptions, setProjectOptions] = useState([]);
  useEffect(() => {
    if (!projectsEnabled || !currentId) return;
    api.get(`/companies/${currentId}/projects`)
      .then(r => setProjectOptions(r.data?.projects || []))
      .catch(() => setProjectOptions([]));
  }, [projectsEnabled, currentId]);
  // Per-project phase cache — keyed by project_id. Loaded lazily
  // the first time we render a txn whose project has phases so the
  // dropdown feels instant on subsequent rows.
  const [phasesByProject, setPhasesByProject] = useState({});
  const ensurePhases = async (projectId) => {
    if (!projectId || phasesByProject[projectId] !== undefined) return;
    // Mark loading (empty array) so we don't re-fetch on every render.
    setPhasesByProject(p => ({ ...p, [projectId]: [] }));
    try {
      const r = await api.get(
        `/companies/${currentId}/projects/${projectId}/phases`);
      setPhasesByProject(p => ({ ...p, [projectId]: r.data?.phases || [] }));
    } catch { /* leave empty — will retry on next render */ }
  };
  // Track which (contact, category) cells and which contacts as a whole
  // have been approved this session. Purely visual state — the actual
  // approval lives on the txn's human_reviewed field via bulk-approve.
  const [approved, setApproved] = useState({});    // key → true
  const [approvingBusy, setApprovingBusy] = useState({});  // key → true
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);
  // Per-txn state — busy flags for the individual approve/save buttons
  // in the expanded drawer.
  const [txnBusy, setTxnBusy] = useState({}); // txnId → true
  // Category options for the dropdown — all revenue/expense/cogs
  // accounts on the CoA. Same shape as the Transactions list-mode
  // category picker uses.
  const categoryOptions = (accts || [])
    .filter(a => ["revenue", "expense", "cogs", "income"].includes((a.type || "").toLowerCase()))
    .sort((a, b) => (a.code || "").localeCompare(b.code || ""));

  // Update a single txn's cached copy after an API mutation so the UI
  // reflects the new state without a full rollup refetch.
  const patchTxnInCache = (txnId, patch) => {
    setCache(c => {
      const next = { ...c };
      for (const k of Object.keys(next)) {
        if (Array.isArray(next[k])) {
          next[k] = next[k].map(t => (t.id === txnId ? { ...t, ...patch } : t));
        }
      }
      return next;
    });
  };

  const setTxnCategory = async (t, newAccountId) => {
    if (!newAccountId || newAccountId === (t.category_account_id || "")) return;
    setTxnBusy(b => ({ ...b, [t.id]: true }));
    try {
      const acct = categoryOptions.find(a => a.id === newAccountId);
      await api.patch(`/companies/${currentId}/transactions/${t.id}`, {
        category_account_id: newAccountId,
      });
      patchTxnInCache(t.id, {
        category_account_id: newAccountId,
        category_account_code: acct?.code,
        category_account_name: acct?.name,
      });
      toast.success(`Moved to ${acct?.name || "new category"}`);
      // Rollup counts / groupings need a full refresh since the txn
      // just moved to a different (contact, category) bucket.
      reload?.();
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTxnBusy(b => ({ ...b, [t.id]: false }));
    }
  };

  const toggleTxnApprove = async (t) => {
    setTxnBusy(b => ({ ...b, [t.id]: true }));
    try {
      const endpoint = t.human_reviewed ? "bulk-unapprove" : "bulk-approve";
      await api.post(`/companies/${currentId}/transactions/${endpoint}`, [t.id]);
      patchTxnInCache(t.id, { human_reviewed: !t.human_reviewed, needs_review: false });
      toast.success(t.human_reviewed ? "Unapproved" : "Approved");
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTxnBusy(b => ({ ...b, [t.id]: false }));
    }
  };

  // Change the contact on ONE transaction. Pass `""` to clear (No contact).
  const setTxnContact = async (t, newContactId) => {
    if ((newContactId || "") === (t.contact_id || "")) return;
    setTxnBusy(b => ({ ...b, [t.id]: true }));
    try {
      const contact = contactOptions.find(c => c.id === newContactId);
      await api.patch(`/companies/${currentId}/transactions/${t.id}`, {
        contact_id: newContactId || null,
      });
      patchTxnInCache(t.id, {
        contact_id: newContactId || null,
        contact_name: contact?.name || null,
      });
      toast.success(newContactId ? `Moved to ${contact?.name || "new contact"}` : "Contact cleared");
      reload?.();  // rollup groups by contact — refresh so the row jumps
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTxnBusy(b => ({ ...b, [t.id]: false }));
    }
  };

  // Per-txn class assignment (Phase 2 advanced features). Rollup
  // doesn't currently group by class, so no reload is needed —
  // patch the cache in place and let the class filter/chip (Phase 3
  // work) pick it up on the next query.
  const setTxnClassId = async (t, newClassId) => {
    if ((newClassId || "") === (t.class_id || "")) return;
    setTxnBusy(b => ({ ...b, [t.id]: true }));
    try {
      const cls = classOptions.find(c => c.id === newClassId);
      // Empty string clears (backend maps "" → null on FK fields).
      await api.patch(`/companies/${currentId}/transactions/${t.id}`, {
        class_id: newClassId === "" ? "" : newClassId,
      });
      patchTxnInCache(t.id, {
        class_id: newClassId || null,
        class_name: cls?.name || null,
      });
      toast.success(newClassId ? `Class set to ${cls?.name || "new class"}` : "Class cleared");
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTxnBusy(b => ({ ...b, [t.id]: false }));
    }
  };

  // Per-txn project assignment. Same shape as class assignment —
  // just patch the txn and update the cache in place. Rollup doesn't
  // currently regroup by project so no reload needed here.
  const setTxnProjectId = async (t, newProjectId) => {
    if ((newProjectId || "") === (t.project_id || "")) return;
    setTxnBusy(b => ({ ...b, [t.id]: true }));
    try {
      const proj = projectOptions.find(p => p.id === newProjectId);
      await api.patch(`/companies/${currentId}/transactions/${t.id}`, {
        project_id: newProjectId === "" ? "" : newProjectId,
        // Changing project also clears any phase — a phase belongs
        // to exactly one project, so a stale phase_id on the new
        // project would violate that invariant.
        phase_id: "",
      });
      patchTxnInCache(t.id, {
        project_id: newProjectId || null,
        project_name: proj?.name || null,
        phase_id: null,
      });
      toast.success(newProjectId ? `Project set to ${proj?.name || "new project"}` : "Project cleared");
      // Pre-warm the phase list for the new project so the phase
      // dropdown renders instantly on the next paint.
      if (newProjectId) ensurePhases(newProjectId);
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTxnBusy(b => ({ ...b, [t.id]: false }));
    }
  };

  // Per-txn phase assignment. Only meaningful when the txn already
  // carries a project_id — phase belongs to a project.
  const setTxnPhaseId = async (t, newPhaseId) => {
    if ((newPhaseId || "") === (t.phase_id || "")) return;
    setTxnBusy(b => ({ ...b, [t.id]: true }));
    try {
      const phList = phasesByProject[t.project_id] || [];
      const ph = phList.find(p => p.id === newPhaseId);
      await api.patch(`/companies/${currentId}/transactions/${t.id}`, {
        phase_id: newPhaseId === "" ? "" : newPhaseId,
      });
      patchTxnInCache(t.id, {
        phase_id: newPhaseId || null,
        phase_name: ph?.name || null,
      });
      toast.success(newPhaseId ? `Phase set to ${ph?.name || "new phase"}` : "Phase cleared");
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTxnBusy(b => ({ ...b, [t.id]: false }));
    }
  };

  // Bulk-move every transaction under a whole rollup contact to a new
  // contact — one PATCH per row (kept simple; the txn list per contact
  // is small in practice). Fires reload once at the end so the rollup
  // regroups.
  const [reassigningContact, setReassigningContact] = useState({}); // ck → true
  const bulkReassignContact = async (contact, newContactId) => {
    const ck = contact.contact_id || `_nocontact_${contact.contact_name}`;
    if ((newContactId || "") === (contact.contact_id || "")) return;
    setReassigningContact(b => ({ ...b, [ck]: true }));
    try {
      // Fetch every txn id under this contact via the same query the
      // expand handler uses. Cheaper than pre-loading them all.
      const perCat = await Promise.all(
        contact.categories.map(cat => {
          const ak = cat.category_account_id || "_uncat_";
          const cellKey = `${ck}||${ak}`;
          return idsForCell(contact, cat, cellKey);
        }),
      );
      const allIds = Array.from(new Set(perCat.flat()));
      if (!allIds.length) {
        toast.info("No transactions to move");
        return;
      }
      // Sequential PATCH — keeps the backend gentle and lets us report
      // partial failures cleanly. In practice each contact rollup is
      // < 50 rows so latency stays reasonable.
      let ok = 0;
      for (const id of allIds) {
        try {
          await api.patch(`/companies/${currentId}/transactions/${id}`, {
            contact_id: newContactId || null,
          });
          ok += 1;
        } catch { /* per-row failure — keep going */ }
      }
      const dest = contactOptions.find(c => c.id === newContactId);
      toast.success(
        newContactId
          ? `Moved ${ok} transactions to ${dest?.name || "new contact"}`
          : `Cleared contact on ${ok} transactions`,
      );
      reload?.();
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setReassigningContact(b => ({ ...b, [ck]: false }));
    }
  };

  if (busy && contacts.length === 0) {
    return <div className="p-8 text-center text-slate-500 text-sm">Grouping transactions…</div>;
  }
  if (contacts.length === 0) {
    return <div className="p-8 text-center text-slate-500 text-sm">No transactions to group.</div>;
  }

  const toggle = async (contact, category) => {
    const ck = contact.contact_id || `_nocontact_${contact.contact_name}`;
    const ak = category.category_account_id || "_uncat_";
    const key = `${ck}||${ak}`;
    const nextOpen = !expanded[key];
    setExpanded(e => ({ ...e, [key]: nextOpen }));
    if (nextOpen && !cache[key]) {
      setCache(c => ({ ...c, [key]: "loading" }));
      try {
        const p = new URLSearchParams({ limit: "500" });
        if (contact.contact_id) p.set("contact_id", contact.contact_id);
        if (category.category_account_id) p.set("category_account_id", category.category_account_id);
        const r = await api.get(`/companies/${currentId}/transactions?${p.toString()}`);
        // Post-filter for (No contact) and Uncategorized cells since those
        // aren't representable as query params.
        let rows = r.data.transactions || [];
        if (!contact.contact_id) rows = rows.filter(t => !t.contact_id);
        if (!category.category_account_id) rows = rows.filter(t => !t.category_account_id);
        // Also match by name for the (No contact) fallback bucket.
        if (!contact.contact_id) rows = rows.filter(t => (t.contact_name || "") === contact.contact_name || !t.contact_name);
        setCache(c => ({ ...c, [key]: rows }));
      } catch {
        setCache(c => ({ ...c, [key]: [] }));
      }
    }
  };

  // Fetch the txn ids underlying a (contact, category) cell. Reuses the
  // expand cache when available; otherwise fires the same query the
  // expand handler does. Returns array of ids (may be empty).
  const idsForCell = async (contact, category, cellKey) => {
    const cached = cache[cellKey];
    if (Array.isArray(cached)) return cached.map(t => t.id);
    const p = new URLSearchParams({ limit: "500" });
    if (contact.contact_id) p.set("contact_id", contact.contact_id);
    if (category.category_account_id) p.set("category_account_id", category.category_account_id);
    const r = await api.get(`/companies/${currentId}/transactions?${p.toString()}`);
    let rows = r.data.transactions || [];
    if (!contact.contact_id) rows = rows.filter(t => !t.contact_id);
    if (!category.category_account_id) rows = rows.filter(t => !t.category_account_id);
    if (!contact.contact_id) rows = rows.filter(t => (t.contact_name || "") === contact.contact_name || !t.contact_name);
    // Warm the cache so a later expand doesn't re-fetch.
    setCache(c => ({ ...c, [cellKey]: rows }));
    return rows.map(t => t.id);
  };

  const approveCategory = async (contact, category, e) => {
    e?.stopPropagation();  // don't toggle the row's expand/collapse
    const ck = contact.contact_id || `_nocontact_${contact.contact_name}`;
    const ak = category.category_account_id || "_uncat_";
    const key = `${ck}||${ak}`;
    if (approvingBusy[key]) return;
    const isCurrentlyApproved = !!approved[key];
    setApprovingBusy(b => ({ ...b, [key]: true }));
    try {
      const ids = await idsForCell(contact, category, key);
      if (ids.length) {
        const endpoint = isCurrentlyApproved ? "bulk-unapprove" : "bulk-approve";
        await api.post(`/companies/${currentId}/transactions/${endpoint}`, ids);
      }
      setApproved(a => ({ ...a, [key]: !isCurrentlyApproved }));
      toast.success(
        isCurrentlyApproved
          ? `Unapproved ${ids.length} txns in "${category.category_name}"`
          : `Approved ${ids.length} txns in "${category.category_name}"`,
      );
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setApprovingBusy(b => ({ ...b, [key]: false }));
    }
  };

  const approveContact = async (contact, e) => {
    e?.stopPropagation();
    const ck = contact.contact_id || `_nocontact_${contact.contact_name}`;
    if (approvingBusy[ck]) return;
    const isCurrentlyApproved = !!approved[ck];
    setApprovingBusy(b => ({ ...b, [ck]: true }));
    try {
      // Gather ids across every category on this contact in parallel.
      const perCat = await Promise.all(
        contact.categories.map(cat => {
          const ak = cat.category_account_id || "_uncat_";
          const key = `${ck}||${ak}`;
          return idsForCell(contact, cat, key).then(ids => ({ key, ids }));
        }),
      );
      const allIds = perCat.flatMap(x => x.ids);
      if (allIds.length) {
        const endpoint = isCurrentlyApproved ? "bulk-unapprove" : "bulk-approve";
        await api.post(`/companies/${currentId}/transactions/${endpoint}`, allIds);
      }
      // Cascade the visual state across the contact + all categories.
      setApproved(a => {
        const next = { ...a, [ck]: !isCurrentlyApproved };
        for (const { key } of perCat) next[key] = !isCurrentlyApproved;
        return next;
      });
      toast.success(
        isCurrentlyApproved
          ? `Unapproved ${allIds.length} txns for ${contact.contact_name}`
          : `Approved ${allIds.length} txns for ${contact.contact_name}`,
      );
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setApprovingBusy(b => ({ ...b, [ck]: false }));
    }
  };

  // One-click bulk approve of every AI-confident row on the company —
  // reuses the existing `bulk-approve-ai-ready` endpoint which flips
  // AI-categorized-unreviewed rows (needs_review=false, posted, categorized,
  // not yet human_reviewed) into human_reviewed. Marks every visible
  // rollup card as approved in one sweep.
  const bulkApproveAiReady = async () => {
    if (bulkBusy || bulkDone) return;
    setBulkBusy(true);
    try {
      const r = await api.post(
        `/companies/${currentId}/transactions/bulk-approve-ai-ready`, {},
      );
      const count = r.data?.approved || r.data?.count || 0;
      toast.success(`Approved ${count} AI-confident transactions`, { duration: 6000 });
      setBulkDone(true);
      // Mark every contact + category currently on-screen as approved
      // so the buttons visually reflect the bulk action. The next data
      // refresh from the server will confirm.
      const stamp = {};
      for (const c of contacts) {
        const ck = c.contact_id || `_nocontact_${c.contact_name}`;
        stamp[ck] = true;
        for (const cat of c.categories) {
          const ak = cat.category_account_id || "_uncat_";
          stamp[`${ck}||${ak}`] = true;
        }
      }
      setApproved(a => ({ ...a, ...stamp }));
    } catch (err) {
      toast.error(`Bulk approve failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  // Shared styling — filled green when actionable, outlined green once done.
  const approveBtnClasses = (isApproved, size = "sm") => {
    const base = size === "sm"
      ? "text-[11px] px-2 py-0.5"
      : "text-xs px-2.5 py-1";
    if (isApproved) {
      return `${base} rounded-md border border-emerald-600 bg-white text-emerald-700 font-medium hover:bg-emerald-50`;
    }
    return `${base} rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700`;
  };

  return (
    <div data-testid="txn-rollup-grid" className="p-4 flex flex-col gap-4">
      {/* Header bulk-action bar — one-tap approve every AI-confident row.
          Kept above the cards so it's discoverable but doesn't fight for
          screen with the toolbar. */}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50/40">
        <div className="text-xs text-slate-700">
          <span className="font-semibold text-slate-900">Bulk approve.</span>{" "}
          Approve every transaction the AI got confident on across all contacts.
          Skips rows still flagged for review, keeps your custom rules and manual overrides intact.
        </div>
        <button
          type="button"
          onClick={bulkApproveAiReady}
          disabled={bulkBusy || bulkDone}
          data-testid="rollup-bulk-approve-ai-ready"
          className={`text-xs px-3 py-1.5 rounded-md font-medium shrink-0 ${
            bulkDone
              ? "border border-emerald-600 bg-white text-emerald-700 cursor-default"
              : "bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-70"
          }`}
        >
          {bulkBusy ? "Approving all…" : bulkDone ? "Approved all confident" : "Approve all AI-confident"}
        </button>
      </div>

      {contacts.map((c) => {
        const multi = c.categories.length > 1;
        const ck = c.contact_id || `_nocontact_${c.contact_name}`;
        const contactApproved = !!approved[ck];
        const contactBusy = !!approvingBusy[ck];
        return (
          <div
            key={c.contact_id || c.contact_name}
            data-testid={`rollup-card-${(c.contact_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`rounded-lg border ${multi ? "border-amber-200" : "border-slate-200"} bg-white overflow-hidden`}
          >
            <div className="px-3 py-2 grid grid-cols-12 gap-2 items-center bg-slate-50 border-b">
              <div className="col-span-5 flex items-center gap-2 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{c.contact_name}</div>
                {multi && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 shrink-0">
                    {c.categories.length} categories
                  </span>
                )}
              </div>
              {/* Bulk reassign contact — moves every txn under this
                  rollup to a different contact in one click. */}
              <div className="col-span-3 shrink-0">
                <select
                  value=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__clear__") bulkReassignContact(c, "");
                    else if (v) bulkReassignContact(c, v);
                    e.target.value = "";
                  }}
                  disabled={!!reassigningContact[ck]}
                  data-testid={`rollup-bulk-reassign-contact-${ck}`}
                  className="w-full border border-slate-200 rounded px-1.5 py-1 text-[11px] bg-white text-slate-600 disabled:opacity-50"
                  title="Move every transaction on this contact to a different contact"
                >
                  <option value="">{reassigningContact[ck] ? "Moving…" : "Move all to…"}</option>
                  {c.contact_id && <option value="__clear__">— Clear contact —</option>}
                  {contactOptions
                    .filter(opt => opt.id !== c.contact_id)
                    .map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                </select>
              </div>
              <span className="col-span-3 text-right text-sm text-slate-500 font-mono-num">{c.total_count} txns</span>
              <div className="col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={(e) => approveContact(c, e)}
                  disabled={contactBusy}
                  data-testid={`rollup-approve-contact-${ck}`}
                  className={approveBtnClasses(contactApproved, "md")}
                  title={contactApproved ? "Click to unapprove" : "Approve all categories for this contact"}
                >
                  {contactBusy ? "Working…" : contactApproved ? "Approved" : "Approve"}
                </button>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {c.categories.map((cat) => {
                const ak = cat.category_account_id || "_uncat_";
                const key = `${ck}||${ak}`;
                const isOpen = !!expanded[key];
                const cached = cache[key];
                const cellApproved = !!approved[key];
                const cellBusy = !!approvingBusy[key];
                const rangeStr = cat.min_amount === cat.max_amount
                  ? fmtMoney(cat.min_amount)
                  : `${fmtMoney(cat.min_amount)} – ${fmtMoney(cat.max_amount)}`;
                return (
                  <div key={cat.category_account_id || cat.category_name}>
                    <div className={`w-full grid grid-cols-12 gap-2 px-3 py-2 items-center text-xs ${isOpen ? "bg-slate-50" : "hover:bg-slate-50"}`}>
                      <button
                        type="button"
                        onClick={() => toggle(c, cat)}
                        aria-expanded={isOpen}
                        className="col-span-11 grid grid-cols-11 gap-2 items-center text-left"
                      >
                        <span className="col-span-1 flex items-center gap-1 font-mono-num text-slate-400">
                          <ChevronRight
                            size={12}
                            className={`text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                          />
                          {cat.category_code || "—"}
                        </span>
                        <span className="col-span-6 text-slate-800 truncate">{cat.category_name}</span>
                        <span className="col-span-1 text-right text-slate-500 font-mono-num text-sm">{cat.count}×</span>
                        <span className="col-span-3 text-right font-mono-num text-slate-700 text-sm">{rangeStr}</span>
                      </button>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={(e) => approveCategory(c, cat, e)}
                          disabled={cellBusy}
                          data-testid={`rollup-approve-cat-${key}`}
                          className={approveBtnClasses(cellApproved, "sm")}
                          title={cellApproved ? "Click to unapprove" : "Approve this category"}
                        >
                          {cellBusy ? "…" : cellApproved ? "Approved" : "Approve"}
                        </button>
                      </div>
                    </div>
                    {isOpen && (
                      <div data-testid={`rollup-expand-${key}`} className="bg-slate-50/40 border-t border-slate-100">
                        {cached === "loading" && (
                          <div className="px-4 py-3 text-[11px] text-slate-500">Loading transactions…</div>
                        )}
                        {Array.isArray(cached) && cached.length === 0 && (
                          <div className="px-4 py-3 text-[11px] text-slate-500">No transactions matched.</div>
                        )}
                        {Array.isArray(cached) && cached.length > 0 && (
                          <div className="divide-y divide-slate-100 text-[13px]">
                            {cached.map(t => {
                              const busy = !!txnBusy[t.id];
                              const desc = (t.description || "").trim();
                              const showDesc = desc && desc !== (t.merchant || "").trim();
                              return (
                              <div key={t.id} className="grid grid-cols-12 gap-2 px-4 py-2 items-center hover:bg-white">
                                <span className="col-span-2 font-mono-num text-slate-500 text-sm">{t.date}</span>
                                <div className="col-span-3 min-w-0" title={t.description || t.merchant}>
                                  <div className="truncate text-slate-800 text-sm">
                                    {t.merchant || t.description || <span className="italic text-slate-400">—</span>}
                                    {t.needs_review && <span className="ml-2 text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">review</span>}
                                    {t.human_reviewed && <span className="ml-2 text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">approved</span>}
                                  </div>
                                  {showDesc && (
                                    <div className="truncate text-[11px] text-slate-500 mt-0.5" title={t.description}>
                                      {t.description}
                                    </div>
                                  )}
                                  {classesEnabled && (
                                    <select
                                      value={t.class_id || ""}
                                      onChange={(e) => setTxnClassId(t, e.target.value)}
                                      disabled={busy}
                                      data-testid={`rollup-txn-class-${t.id}`}
                                      className="mt-1 border border-slate-200 rounded px-1.5 py-0.5 text-[10px] bg-white text-slate-600 disabled:opacity-50 max-w-full truncate"
                                      title="Change class"
                                    >
                                      <option value="">— No class —</option>
                                      {classOptions.filter(c => c.active !== false).map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                      ))}
                                    </select>
                                  )}
                                  {projectsEnabled && (
                                    <select
                                      value={t.project_id || ""}
                                      onChange={(e) => setTxnProjectId(t, e.target.value)}
                                      disabled={busy}
                                      data-testid={`rollup-txn-project-${t.id}`}
                                      className="mt-1 ml-1 border border-slate-200 rounded px-1.5 py-0.5 text-[10px] bg-white text-slate-600 disabled:opacity-50 max-w-full truncate"
                                      title="Change project"
                                    >
                                      <option value="">— No project —</option>
                                      {projectOptions.filter(p => p.status !== "cancelled").map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                      ))}
                                    </select>
                                  )}
                                  {projectsEnabled && t.project_id && (() => {
                                    // Ensure phase list is loaded for this project.
                                    // Idempotent — cached after first fetch.
                                    ensurePhases(t.project_id);
                                    const phList = phasesByProject[t.project_id] || [];
                                    if (phList.length === 0) return null;
                                    return (
                                      <select
                                        value={t.phase_id || ""}
                                        onChange={(e) => setTxnPhaseId(t, e.target.value)}
                                        disabled={busy}
                                        data-testid={`rollup-txn-phase-${t.id}`}
                                        className="mt-1 ml-1 border border-slate-200 rounded px-1.5 py-0.5 text-[10px] bg-white text-slate-600 disabled:opacity-50 max-w-full truncate"
                                        title="Change phase"
                                      >
                                        <option value="">— No phase —</option>
                                        {phList
                                          .filter(p => p.status !== "cancelled")
                                          .map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                          ))}
                                      </select>
                                    );
                                  })()}
                                </div>
                                <select
                                  value={t.contact_id || ""}
                                  onChange={(e) => setTxnContact(t, e.target.value)}
                                  disabled={busy}
                                  data-testid={`rollup-txn-contact-${t.id}`}
                                  className="col-span-2 border border-slate-200 rounded px-1.5 py-1 text-[12px] bg-white text-slate-700 disabled:opacity-50 truncate min-w-0"
                                  title="Change contact"
                                >
                                  <option value="">— No contact —</option>
                                  {contactOptions.map(opt => (
                                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                                  ))}
                                </select>
                                <div className="col-span-2 min-w-0">
                                  <SearchableAccountPicker
                                    value={t.category_account_id || null}
                                    onChange={(id) => setTxnCategory(t, id)}
                                    accounts={categoryOptions}
                                    allAccounts={accts}
                                    placeholder="— Uncategorized —"
                                    kindLabel="category"
                                    newDefaults={{ type: "expense" }}
                                    currentId={currentId}
                                    onCreated={(a) => {
                                      if (!a?.id) return;
                                      setAccts(prev => prev.some(x => x.id === a.id) ? prev : [...prev, a]);
                                      setTxnCategory(t, a.id);
                                    }}
                                    disabled={busy}
                                    testId={`rollup-txn-category-${t.id}`}
                                  />
                                </div>
                                <span className={`col-span-2 text-right font-mono-num text-sm ${(t.amount || 0) < 0 ? "text-slate-800" : "text-emerald-700"}`}>
                                  {fmtMoney(t.amount)}
                                </span>
                                <div className="col-span-1 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => toggleTxnApprove(t)}
                                    disabled={busy}
                                    data-testid={`rollup-txn-approve-${t.id}`}
                                    className={`text-[11px] px-2 py-1 rounded-md font-medium whitespace-nowrap ${
                                      t.human_reviewed
                                        ? "border border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-50"
                                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                                    } disabled:opacity-50`}
                                    title={t.human_reviewed ? "Click to unapprove" : "Approve this transaction"}
                                  >
                                    {busy ? "…" : t.human_reviewed ? "Approved" : "Approve"}
                                  </button>
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ManualTxnModal({ accts, currentId, contactOptions = [], invoices = [], bills = [], initialTxn = null, onClose, onOpenMultiLink = null }) {
  const fmtMoney = useMoneyFmt();
  const isEdit = Boolean(initialTxn);
  const [date, setDate] = useState(initialTxn?.date || new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState(initialTxn?.description || "");
  const [merchant, setMerchant] = useState(initialTxn?.merchant || "");
  const [amount, setAmount] = useState(
    // Prefer `txn_amount` when the caller passed one — the account-
    // detail row's `amount` field is the SPLIT-LINE amount (e.g. -$19
    // for the Fees line of a $2,145 mortgage payment), whereas the
    // modal always edits the WHOLE parent transaction. Non-split
    // callers omit `txn_amount`, so we fall back to `amount`.
    initialTxn?.txn_amount != null
      ? String(initialTxn.txn_amount)
      : (initialTxn?.amount != null ? String(initialTxn.amount) : "")
  );
  const [categoryId, setCategoryId] = useState(initialTxn?.category_account_id || "");
  // Source Account the transaction hit. Pulled from all Asset + Liability
  // rows on the Chart of Accounts (banks & savings live on the asset side,
  // credit cards & lines of credit on the liability side — we don't hard-
  // code type=bank/credit_card because the seed uses type=asset/liability
  // with subtype indicating current_asset / current_liability / etc).
  const [bankAccountId, setBankAccountId] = useState(initialTxn?.bank_account_id || "");
  const bankOptions = (accts || [])
    .filter((a) => ["asset", "liability"].includes(a.type))
    .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
  const bankAssets = bankOptions.filter((a) => a.type === "asset");
  const bankLiabilities = bankOptions.filter((a) => a.type === "liability");
  const [busy, setBusy] = useState(false);
  // Contact link — CPA can pick an existing contact from the search
  // combo or type a brand-new name to auto-create one on save.
  const [contactId, setContactId] = useState(initialTxn?.contact_id || "");
  const [contactQuery, setContactQuery] = useState("");
  const [contactMenuOpen, setContactMenuOpen] = useState(false);
  // Auto-resolve the contact from the merchant when the modal opens if
  // the row doesn't already carry a `contact_id`. Semantic lookup goes
  // through the backend `contacts/resolve` endpoint (normalized name
  // match → semantic AI fallback → auto-create). Runs at most once per
  // modal-open so a CPA can clear the field manually and it won't
  // re-populate itself.
  const [contactResolving, setContactResolving] = useState(false);
  const [contactAutoFilled, setContactAutoFilled] = useState(false);
  const [resolvedContactName, setResolvedContactName] = useState(initialTxn?.contact_name || "");
  const contactResolveDone = useRef(false);
  useEffect(() => {
    if (contactResolveDone.current) return;
    if (!currentId) return;
    if (contactId) { contactResolveDone.current = true; return; }
    const m = (merchant || "").trim();
    if (!m) return;
    contactResolveDone.current = true;
    setContactResolving(true);
    api.post(`/companies/${currentId}/contacts/resolve`, {
      merchant: m,
      description: description || "",
      amount: parseFloat(amount || 0) || 0,
      auto_create: true,
    }).then(async (r) => {
      const cid = r.data?.contact_id;
      const cname = r.data?.contact_name;
      if (cid) {
        setContactId(cid);
        setResolvedContactName(cname || "");
        setContactAutoFilled(true);
        if (r.data?.created) {
          toast.success(`Contact created: ${cname}`);
        }
        // Persist the auto-fill to the underlying transaction so the list
        // reflects it immediately, even if the CPA closes the modal
        // without hitting Save. Only fires in EDIT mode — the CREATE
        // flow has no txn_id yet, so the save() handler carries it.
        if (isEdit && initialTxn?.id) {
          try {
            await api.patch(
              `/companies/${currentId}/transactions/${initialTxn.id}`,
              { contact_id: cid, contact_name: cname || "" },
            );
          } catch (e) {
            // Silent — the field is set locally; save() will retry on submit.
          }
        }
      }
    }).catch(() => {
      // Silent — user can still type manually.
    }).finally(() => setContactResolving(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, merchant, contactId]);

  const filteredContacts = (() => {
    const q = contactQuery.trim().toLowerCase();
    if (!q) return contactOptions.slice(0, 50);
    return contactOptions.filter((c) => (c.name || "").toLowerCase().includes(q)).slice(0, 50);
  })();
  const canCreateNewContact = contactQuery.trim().length > 1 && !contactOptions.some(
    (c) => (c.name || "").trim().toLowerCase() === contactQuery.trim().toLowerCase()
  );
  // Splits are optional — CPA toggles "Split into multiple categories"
  // to break the header amount across N lines instead of picking a
  // single category. Mirrors the SplitModal UX but lives inline in the
  // create flow so a CPA can post a proper multi-line JE without a
  // second click. Empty array = simple single-category mode.
  const initialSplits = Array.isArray(initialTxn?.splits) ? initialTxn.splits : [];
  const [splitsOn, setSplitsOn] = useState(initialSplits.length > 0);
  const [splitRows, setSplitRows] = useState(
    initialSplits.length > 0
      ? initialSplits.map((s) => ({
          amount: s.amount != null ? String(s.amount) : "",
          category_account_id: s.category_account_id || "",
          description: s.description || "",
        }))
      : [
          { amount: "", category_account_id: "", description: "" },
          { amount: "", category_account_id: "", description: "" },
        ]
  );
  const amtNum = parseFloat(amount || 0) || 0;
  const splitTotal = splitRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const splitsBalance = Math.abs(splitTotal - amtNum) < 0.01;
  // Link to invoice / bill — the backend already exposes
  // /transactions/{id}/link (see routes/transactions.py). Positive
  // amounts default to invoice; negative default to bill. We surface
  // both dropdowns anyway so a refund-style txn can be re-linked
  // easily.
  const [linkKind, setLinkKind] = useState(
    initialTxn?.linked_bill_id ? "bill"
    : initialTxn?.linked_invoice_id ? "invoice"
    : (parseFloat(initialTxn?.amount || 0) >= 0 ? "invoice" : "bill")
  );
  const [linkId, setLinkId] = useState(
    initialTxn?.linked_invoice_id || initialTxn?.linked_bill_id || ""
  );
  const linkOptions = linkKind === "invoice" ? invoices : bills;

  // ── Attachments (receipts, supporting docs) ────────────────
  // Rendered as inline thumbnails in the Edit modal. Uploads go
  // straight into `transactions.attachments[]` via the pro-scoped
  // endpoint added Feb 2026 — no need to round-trip through the
  // client-review flow. Base64 stays inline for MVP; large accounts
  // should move this to Emergent Object Storage.
  const [attachments, setAttachments] = useState(
    Array.isArray(initialTxn?.attachments) ? initialTxn.attachments : []
  );
  const [attaching, setAttaching] = useState(false);
  const attachInputRef = useRef(null);
  const [receiptPickerOpen, setReceiptPickerOpen] = useState(false);
  const [linkedReceiptId, setLinkedReceiptId] = useState(initialTxn?.matched_receipt_id || null);
  const onPickAttachment = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Attachment too large (max 8 MB).");
      return;
    }
    if (!isEdit || !initialTxn?.id) {
      toast.error("Save the transaction first, then attach a receipt.");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setAttaching(true);
      try {
        const r = await api.post(
          `/companies/${currentId}/transactions/${initialTxn.id}/attachments`,
          {
            data_url: reader.result,
            filename: file.name,
            mime:     file.type,
            size:     file.size,
          },
        );
        setAttachments((prev) => [...prev, r.data.attachment]);
        toast.success("Receipt attached.");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed.");
      } finally {
        setAttaching(false);
      }
    };
    reader.readAsDataURL(file);
  };
  const removeAttachment = async (aid) => {
    if (!isEdit || !initialTxn?.id) return;
    try {
      const r = await api.delete(
        `/companies/${currentId}/transactions/${initialTxn.id}/attachments/${aid}`,
      );
      setAttachments((prev) => prev.filter((a) => a.id !== aid));
      if (r.data?.receipt_unlinked) {
        // Receipt gone → the transaction is uncategorized again.
        setLinkedReceiptId(null);
        setSplitsOn(false);
        setSplitRows([]);
        setCategoryId("");
        toast.success("Receipt removed — transaction is back to uncategorized.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed.");
    }
  };
  const openAttachment = async (aid) => {
    if (!isEdit || !initialTxn?.id) return;
    try {
      const r = await api.get(
        `/companies/${currentId}/transactions/${initialTxn.id}/attachments/${aid}`,
      );
      const url = r.data?.attachment?.data_url;
      if (!url) return;
      // Open in new tab. Data URLs work directly in Chromium; some
      // browsers block huge PDFs — user can right-click → save-as if needed.
      const win = window.open(url, "_blank", "noopener");
      if (!win) toast.error("Popup blocked — allow popups for this site.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open attachment.");
    }
  };


  // Auto-suggest a match once, on first render, if the user hasn't
  // manually picked a link yet. We look for a single OPEN doc with
  // (a) contact_name matching merchant (case-insensitive substring)
  // and (b) total within a penny of |amount|. Exactly-one match =
  // pre-select; ambiguous = leave blank and let the CPA decide.
  const [suggestedId, setSuggestedId] = useState(null);
  const [suggestApplied, setSuggestApplied] = useState(false);
  useEffect(() => {
    if (linkId || suggestApplied) return;
    const amt = Math.abs(parseFloat(amount || 0) || 0);
    if (!amt) return;
    const kind = parseFloat(amount || 0) >= 0 ? "invoice" : "bill";
    const pool = (kind === "invoice" ? invoices : bills).filter(d => {
      const st = (d.status || "").toLowerCase();
      if (st === "paid" || st === "void") return false;
      const t = parseFloat(d.total || 0);
      return Math.abs(t - amt) < 0.01;
    });
    // Prefer merchant match, but if nothing matches by merchant, don't
    // trigger — one same-amount doc isn't enough on its own. Require
    // the shorter side of the pair to be ≥ 4 chars so we don't
    // over-match generic tokens like "Inc" or "LLC".
    const m = (merchant || "").trim().toLowerCase();
    const matches = m && m.length >= 4
      ? pool.filter(d => {
          const cn = (d.contact_name || "").toLowerCase();
          if (!cn || cn.length < 4) return false;
          return cn.includes(m) || m.includes(cn);
        })
      : [];
    if (matches.length === 1) {
      setLinkKind(kind);
      setLinkId(matches[0].id);
      setSuggestedId(matches[0].id);
      setSuggestApplied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, merchant, invoices, bills]);

  const save = async () => {
    if (splitsOn) {
      if (!amtNum) { toast.error("Enter the total amount first"); return; }
      if (!splitsBalance) { toast.error(`Split total ${splitTotal.toFixed(2)} must equal ${amtNum.toFixed(2)}`); return; }
      if (splitRows.some((r) => !r.category_account_id)) { toast.error("Every split needs a category"); return; }
    }
    setBusy(true);
    try {
      // Resolve the contact link. If the CPA typed a new name that
      // doesn't match anything in contactOptions, create the contact
      // first and use the returned id. Otherwise honor whatever
      // contactId is selected (may be empty for "no contact").
      let finalContactId = contactId;
      let finalContactName = "";
      if (finalContactId) {
        finalContactName = (contactOptions.find((c) => c.id === finalContactId) || {}).name || "";
      } else if (contactQuery.trim() && canCreateNewContact) {
        try {
          const cr = await api.post(`/companies/${currentId}/contacts`, {
            name: contactQuery.trim(),
            kind: (parseFloat(amount || 0) || 0) >= 0 ? "customer" : "vendor",
          });
          finalContactId = cr.data?.id || "";
          finalContactName = contactQuery.trim();
        } catch (e) {
          toast.error("Couldn't create the new contact — try again?");
          setBusy(false);
          return;
        }
      }
      const payload = {
        date, description, merchant, amount: amtNum,
        bank_account_id: bankAccountId || null,
        category_account_id: splitsOn ? null : (categoryId || null),
        splits: splitsOn ? splitRows.map((r) => ({
          amount: parseFloat(r.amount) || 0,
          category_account_id: r.category_account_id,
          description: r.description,
        })) : (isEdit ? [] : null),
        contact_id: finalContactId || null,
        contact_name: finalContactName || null,
      };
      if (isEdit) {
        await api.patch(`/companies/${currentId}/transactions/${initialTxn.id}`, payload);
        toast.success(splitsOn ? "Split transaction updated" : "Transaction updated");
      } else {
        const created = await api.post(`/companies/${currentId}/transactions`, {
          ...payload,
          auto_categorize: !splitsOn && !categoryId,
        });
        toast.success(splitsOn ? "Split transaction created" : "Transaction created");
        // For a brand-new manual txn we still want to honour any picked
        // link — use the returned id from the POST response.
        if (linkId) {
          const newId = created.data?.id;
          if (newId) {
            const q = linkKind === "invoice" ? `invoice_id=${linkId}` : `bill_id=${linkId}`;
            await api.post(`/companies/${currentId}/transactions/${newId}/link?${q}`);
          }
        }
      }
      // On edit, always push the current link selection so unlinking
      // (setting to empty) works too. Empty string clears both sides.
      if (isEdit) {
        const currentLinked = initialTxn?.linked_invoice_id || initialTxn?.linked_bill_id || "";
        if (linkId !== currentLinked || (initialTxn?.linked_bill_id && linkKind === "invoice") || (initialTxn?.linked_invoice_id && linkKind === "bill")) {
          const params = new URLSearchParams();
          if (linkKind === "invoice") {
            params.set("invoice_id", linkId || "");
            // Clear the other side explicitly.
            if (initialTxn?.linked_bill_id) params.set("bill_id", "");
          } else {
            params.set("bill_id", linkId || "");
            if (initialTxn?.linked_invoice_id) params.set("invoice_id", "");
          }
          await api.post(`/companies/${currentId}/transactions/${initialTxn.id}/link?${params.toString()}`);
        }
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={isEdit ? "Edit transaction" : "Add manual transaction"} onClose={onClose} wide={splitsOn}>
      <div className="space-y-3 text-sm">
        <div><label className="text-xs text-slate-600">Date</label>
          <input data-testid="manual-txn-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border rounded px-2 py-1.5" /></div>
        <div>
          <label className="text-xs text-slate-600">Account</label>
          <select
            data-testid="manual-txn-bank-account"
            value={bankAccountId}
            onChange={(e) => setBankAccountId(e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm bg-white"
          >
            <option value="">— Default (Business Checking) —</option>
            {bankAssets.length > 0 && (
              <optgroup label="Assets (bank, cash, receivable…)">
                {bankAssets.map((a) => (
                  <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                ))}
              </optgroup>
            )}
            {bankLiabilities.length > 0 && (
              <optgroup label="Liabilities (credit cards, loans, payable…)">
                {bankLiabilities.map((a) => (
                  <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
        <div className="relative" onBlur={(e) => {
          // Close the contact menu on blur unless focus went to a child.
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setTimeout(() => setContactMenuOpen(false), 150);
          }
        }}>
          <label className="text-xs text-slate-600 inline-flex items-center gap-2">
            Contact
            {contactResolving && (
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-500" data-testid="contact-resolving">
                <Loader2 size={10} className="animate-spin" /> matching…
              </span>
            )}
            {contactAutoFilled && !contactResolving && contactId && (
              <span
                className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800"
                data-testid="contact-auto-filled"
                title="Auto-matched from merchant"
              >Auto-matched</span>
            )}
          </label>
          <input
            data-testid="manual-txn-contact-input"
            type="text"
            placeholder="Search or type a new name…"
            value={contactId
              ? ((contactOptions.find((c) => c.id === contactId) || {}).name || resolvedContactName || initialTxn?.contact_name || "")
              : contactQuery}
            onFocus={() => setContactMenuOpen(true)}
            onChange={(e) => {
              setContactId("");
              setContactAutoFilled(false);
              setResolvedContactName("");
              setContactQuery(e.target.value);
              setContactMenuOpen(true);
            }}
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          {contactMenuOpen && (filteredContacts.length > 0 || canCreateNewContact) && (
            <div className="absolute z-30 left-0 right-0 top-[calc(100%+2px)] max-h-[240px] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-xl">
              {filteredContacts.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`manual-txn-contact-opt-${c.id}`}
                  onClick={() => {
                    setContactId(c.id);
                    setContactQuery("");
                    setContactMenuOpen(false);
                  }}
                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                >
                  {c.name}
                </button>
              ))}
              {canCreateNewContact && (
                <button
                  type="button"
                  data-testid="manual-txn-contact-add-new"
                  onClick={() => setContactMenuOpen(false)}
                  className="w-full text-left px-2 py-1.5 text-xs text-cyan-700 font-semibold hover:bg-cyan-50 border-t border-slate-100"
                >
                  + Use new contact "{contactQuery.trim()}"
                </button>
              )}
            </div>
          )}
        </div>
        <div className="hidden"><label className="text-xs text-slate-600">Merchant</label>
          <input data-testid="manual-txn-merchant" value={merchant} onChange={(e) => setMerchant(e.target.value)} className="w-full border rounded px-2 py-1.5" /></div>
        <div><label className="text-xs text-slate-600">Description</label>
          <input data-testid="manual-txn-description" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border rounded px-2 py-1.5" /></div>
        <div><label className="text-xs text-slate-600">Amount (negative = expense)</label>
          <input data-testid="manual-txn-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border rounded px-2 py-1.5 font-mono-num" /></div>
        <div className="flex items-center gap-2 pt-1">
          <input
            type="checkbox"
            id="manual-txn-splits-on"
            data-testid="manual-txn-splits-toggle"
            checked={splitsOn}
            onChange={(e) => setSplitsOn(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="manual-txn-splits-on" className="text-xs text-slate-700 font-medium cursor-pointer">
            Split into multiple categories
          </label>
        </div>
        {splitsOn ? (
          <div className="space-y-2 border-t pt-3" data-testid="manual-txn-splits-panel">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Splits — must sum to {fmtMoney(amtNum)}
            </div>
            {splitRows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  value={r.amount}
                  onChange={(e) => setSplitRows(splitRows.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                  className="col-span-3 border rounded px-2 py-1.5 font-mono-num text-xs"
                />
                <div className="col-span-6 min-w-0">
                  <AccountPicker
                    value={r.category_account_id}
                    accounts={accts}
                    onChange={(id) => setSplitRows(splitRows.map((x, j) => j === i ? { ...x, category_account_id: id } : x))}
                    companyId={currentId}
                    testId={`manual-txn-split-cat-${i}`}
                  />
                </div>
                <input
                  placeholder="Note"
                  value={r.description}
                  onChange={(e) => setSplitRows(splitRows.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                  className="col-span-2 border rounded px-2 py-1.5 text-xs"
                />
                <button
                  onClick={() => splitRows.length > 1 && setSplitRows(splitRows.filter((_, j) => j !== i))}
                  disabled={splitRows.length <= 1}
                  className="col-span-1 text-red-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove split line"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              <button
                onClick={() => setSplitRows([...splitRows, { amount: "", category_account_id: "", description: "" }])}
                data-testid="manual-txn-split-add"
                className="text-xs text-slate-600 border border-dashed border-slate-300 rounded px-2 py-1 hover:bg-slate-50"
              >
                + Add split line
              </button>
              <div className={`text-xs ${splitsBalance && amtNum ? "text-emerald-600" : "text-red-600"}`}>
                Total: <span className="font-mono-num font-semibold">{fmtMoney(splitTotal)}</span>
                {" · Target: "}
                <span className="font-mono-num">{fmtMoney(amtNum)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <label className="text-xs text-slate-600">Category (leave blank for AI)</label>
            <div className="mt-1">
              <AccountPicker
                value={categoryId}
                accounts={accts}
                onChange={(id) => setCategoryId(id)}
                companyId={currentId}
                testId="manual-txn-category-picker"
              />
            </div>
          </div>
        )}
        <div className="border-t pt-3 space-y-2" data-testid="txn-link-section">
          <div className="flex items-center justify-between">
            <label className="text-xs text-slate-600 font-medium inline-flex items-center gap-2">
              Link to invoice or bill
              {suggestedId && suggestedId === linkId && (
                <span
                  data-testid="txn-link-suggested"
                  className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                  title="Auto-matched by merchant + amount"
                >Auto-matched</span>
              )}
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => { onClose(); onOpenMultiLink && onOpenMultiLink(); }}
                className="text-[10px] text-emerald-700 hover:underline"
                data-testid="txn-link-multi"
                title="Split this receipt across multiple invoices"
              >Apply to multiple invoices…</button>
              {linkId && (
                <button
                  type="button"
                  onClick={() => { setLinkId(""); setSuggestedId(null); }}
                  className="text-[10px] text-rose-600 hover:underline"
                  data-testid="txn-link-clear"
                >Unlink</button>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <div className="inline-flex rounded-md border bg-slate-50 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => { setLinkKind("invoice"); setLinkId(""); }}
                data-testid="txn-link-kind-invoice"
                className={`px-2.5 py-1 rounded ${linkKind === "invoice" ? "bg-emerald-600 text-white" : "text-slate-600"}`}
              >Invoice</button>
              <button
                type="button"
                onClick={() => { setLinkKind("bill"); setLinkId(""); }}
                data-testid="txn-link-kind-bill"
                className={`px-2.5 py-1 rounded ${linkKind === "bill" ? "bg-rose-600 text-white" : "text-slate-600"}`}
              >Bill</button>
            </div>
            <select
              value={linkId}
              onChange={(e) => setLinkId(e.target.value)}
              className="flex-1 border rounded px-2 py-1.5 text-sm bg-white"
              data-testid="txn-link-select"
            >
              <option value="">— None (not linked) —</option>
              {linkOptions.map(x => (
                <option key={x.id} value={x.id}>
                  {x.number} · {x.contact_name || "no contact"} · {fmtMoney(x.total)}{x.status ? ` · ${x.status}` : ""}
                </option>
              ))}
            </select>
          </div>
          <p className="text-[10px] text-slate-400">
            Linking marks this transaction as the payment/receipt for the picked {linkKind}. Leave blank to un-link.
          </p>
        </div>
        {/* Attachments (receipts, supporting docs) */}
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <label className="text-xs text-slate-600 font-medium inline-flex items-center gap-2">
              Attachments
              {attachments.length > 0 && (
                <span
                  className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700"
                  data-testid="txn-attachments-count"
                >
                  {attachments.length} on file
                </span>
              )}
            </label>
            {isEdit && (
              <>
                <input
                  ref={attachInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onPickAttachment(f);
                    e.target.value = "";
                  }}
                  data-testid="txn-attachment-input"
                />
                <button
                  type="button"
                  onClick={() => attachInputRef.current?.click()}
                  disabled={attaching}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="txn-attachment-add"
                  title="Attach a receipt or supporting document"
                >
                  {attaching
                    ? <><Loader2 size={11} className="animate-spin" /> Uploading…</>
                    : <><Paperclip size={11} /> Add receipt</>}
                </button>
                {!linkedReceiptId ? (
                  <button
                    type="button"
                    onClick={() => setReceiptPickerOpen(true)}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    data-testid="txn-attach-existing-receipt"
                    title="Link a receipt you already uploaded on the Receipts page"
                  >
                    <Link2 size={11} /> Attach existing receipt
                  </button>
                ) : (
                  <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid="txn-receipt-linked">
                    <Link2 size={11} /> Receipt linked
                  </span>
                )}
                {receiptPickerOpen && (
                  <ReceiptMatchPicker
                    companyId={currentId}
                    mode="transaction"
                    id={initialTxn.id}
                    onClose={() => setReceiptPickerOpen(false)}
                    onDone={(r) => setLinkedReceiptId(r.receipt_id)}
                  />
                )}
              </>
            )}
          </div>
          {!isEdit && (
            <p className="text-[10px] text-slate-400">
              Save this transaction first — you'll be able to attach a receipt right after.
            </p>
          )}
          {isEdit && attachments.length === 0 && (
            <p className="text-[10px] text-slate-400">
              No receipts on file. Drop a photo, scan, or PDF above and it'll live with this transaction forever.
            </p>
          )}
          {attachments.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="txn-attachments-list">
              {attachments.map((a) => {
                const isImg = (a.mime || "").startsWith("image/");
                const kb = a.size ? (a.size / 1024).toFixed(0) : "?";
                return (
                  <div
                    key={a.id}
                    className="group relative flex flex-col rounded-md border border-slate-200 overflow-hidden bg-white hover:border-slate-300 hover:shadow-sm transition"
                    data-testid={`txn-attachment-${a.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => openAttachment(a.id)}
                      className="h-20 flex items-center justify-center bg-slate-50 text-slate-400 hover:bg-slate-100"
                      title="Open in new tab"
                      data-testid={`txn-attachment-open-${a.id}`}
                    >
                      {isImg
                        ? <Eye size={18} />
                        : <FileText size={18} />}
                    </button>
                    <div className="px-1.5 py-1 text-[10px] leading-tight">
                      <div className="truncate font-medium text-slate-800" title={a.filename}>
                        {a.filename}
                      </div>
                      <div className="flex items-center justify-between text-slate-400">
                        <span>{kb} KB</span>
                        {a.source && <span className="uppercase">{a.source}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.id)}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 rounded bg-white/90 text-rose-600 hover:bg-rose-50 shadow-sm transition"
                      title="Remove"
                      data-testid={`txn-attachment-remove-${a.id}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <button data-testid={TID.saveBtn} onClick={save} disabled={busy}
                className="w-full py-2 rounded-md bg-slate-900 text-white text-sm disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

export function SplitModal({ txn, accts, currentId, onClose }) {
  const fmtMoney = useMoneyFmt();
  const [rows, setRows] = useState([
    { amount: (txn.amount / 2).toFixed(2), category_account_id: txn.category_account_id, description: "" },
    { amount: (txn.amount / 2).toFixed(2), category_account_id: "", description: "" },
  ]);
  // Local copy of accounts so a "+ Add new" inside any row picker
  // immediately shows up on the next row without a page reload.
  const [localAccts, setLocalAccts] = useState(accts || []);
  useEffect(() => { setLocalAccts(accts || []); }, [accts]);
  const total = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const save = async () => {
    if (Math.abs(total - txn.amount) > 0.01) { toast.error(`Must total ${txn.amount}`); return; }
    await api.post(`/companies/${currentId}/transactions/${txn.id}/split`, { splits: rows });
    toast.success("Transaction split"); onClose();
  };
  return (
    <Modal title={`Split ${fmtMoney(txn.amount)} · ${txn.merchant}`} onClose={onClose} wide>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input type="number" step="0.01" value={r.amount}
                   onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                   className="col-span-3 border rounded px-2 py-1.5 font-mono-num text-sm" />
            <div className="col-span-5">
              <SearchableAccountPicker
                value={r.category_account_id || null}
                onChange={(id) => setRows(rows.map((x, j) => j === i ? { ...x, category_account_id: id || "" } : x))}
                accounts={localAccts}
                allAccounts={localAccts}
                placeholder="Category…"
                kindLabel="category"
                newDefaults={{ type: "expense" }}
                currentId={currentId}
                onCreated={(a) => {
                  if (!a?.id) return;
                  setLocalAccts(prev => prev.some(x => x.id === a.id) ? prev : [...prev, a]);
                  setRows(rows.map((x, j) => j === i ? { ...x, category_account_id: a.id } : x));
                }}
                testId={`split-row-${i}-category`}
              />
            </div>
            <input placeholder="Description" value={r.description}
                   onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                   className="col-span-3 border rounded px-2 py-1.5 text-sm" />
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="col-span-1 text-red-500"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => setRows([...rows, { amount: "0", category_account_id: "", description: "" }])}
                className="text-xs text-slate-600 border border-dashed rounded px-2 py-1">+ Add split</button>
        <div className="flex items-center justify-between border-t pt-3 mt-3">
          <div className={`text-sm ${Math.abs(total - txn.amount) < 0.01 ? "text-emerald-600" : "text-red-600"}`}>
            Split total: <span className="font-mono-num font-semibold">{fmtMoney(total)}</span> · Target: <span className="font-mono-num">{fmtMoney(txn.amount)}</span>
          </div>
          <button data-testid={TID.saveBtn} onClick={save} className="px-4 py-1.5 rounded-md bg-slate-900 text-white text-sm">Save split</button>
        </div>
      </div>
    </Modal>
  );
}

export function LinkModal({ txn, invoices, bills, currentId, onClose, token, itemId, onApplied, sampleDocs = null }) {
  const fmtMoney = useMoneyFmt();
  // Token-mode: swap the JWT-authed `api` client for a plain axios so
  // the same UI works inside a client-facing magic-link session.
  const clientAxios = React.useMemo(
    () => (token ? axios.create() : null),
    [token],
  );
  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";
  const _get = React.useCallback(
    (path) => token
      ? clientAxios.get(apiBase + path.replace(
          `/companies/${currentId}`,
          `/client-review/${token}`,
        ))
      : api.get(path),
    [token, clientAxios, apiBase, currentId],
  );
  const _post = React.useCallback(
    (path, body) => token
      ? clientAxios.post(
          apiBase + path.replace(
            `/companies/${currentId}`,
            `/client-review/${token}`,
          ) + (itemId ? (path.includes("?") ? "&" : "?") + `item_id=${encodeURIComponent(itemId)}` : ""),
          body,
        )
      : api.post(path, body),
    [token, clientAxios, apiBase, currentId, itemId],
  );
  // Unified list mode (Mar 2026 UX overhaul): one modal shows ALL open
  // invoices with a Customer column + quick-filter chips + search.
  // Pros pick 1..N invoices in a single view — no more "pick a
  // customer" step, no more "single vs multi" tab choice.
  const isDeposit = Number(txn.amount || 0) > 0;
  const [kind, setKind] = useState(isDeposit ? "invoice" : "bill");
  const [openDocs, setOpenDocs] = useState([]);
  const [apps, setApps] = useState({}); // { docId: appliedAmount }
  const [search, setSearch] = useState("");
  const [contactFilter, setContactFilter] = useState(""); // "" = all
  const [loading, setLoading] = useState(false);

  const txnAmt = Math.abs(Number(txn.amount || 0));

  // Fetch every open doc for the picked kind (oldest first).
  useEffect(() => {
    setOpenDocs([]); setApps({}); setSearch(""); setContactFilter("");
    const url = kind === "invoice"
      ? `/companies/${currentId}/invoices/open`
      : `/companies/${currentId}/bills/open`;
    const fetchDocs = sampleDocs
      ? Promise.resolve({ data: kind === "invoice" ? { invoices: sampleDocs.invoices } : { bills: sampleDocs.bills } })
      : _get(url);
    fetchDocs
      .then(r => {
        const rows = r.data.invoices || r.data.bills || [];
        setOpenDocs(rows);
        // Auto-pre-select: if the txn already knows its counterparty,
        // check that party's open docs FIFO up to the deposit total
        // so single-invoice / same-customer flows are one-click.
        if (txn.contact_id) {
          const mine = rows.filter(x => x.contact_id === txn.contact_id);
          const seed = {};
          let remaining = txnAmt;
          for (const d of mine) {
            if (remaining <= 0.005) break;
            const applied = Math.min(Number(d.balance_due || 0), remaining);
            if (applied > 0.005) {
              seed[d.id] = +applied.toFixed(2);
              remaining -= applied;
            }
          }
          setApps(seed);
        }
      })
      .catch(() => setOpenDocs([]));
  }, [kind, currentId, txn.contact_id, txnAmt]);

  // Customer/vendor buckets for the quick-filter chip strip.
  const contactBuckets = useMemo(() => {
    const map = new Map();
    openDocs.forEach(d => {
      const key = d.contact_id || "";
      const name = d.contact_name || "—";
      if (!map.has(key)) map.set(key, { id: key, name, count: 0 });
      map.get(key).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [openDocs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return openDocs.filter(d => {
      if (contactFilter && d.contact_id !== contactFilter) return false;
      if (!q) return true;
      const hay = `${d.number} ${d.contact_name || ""} ${d.balance_due} ${d.total}`.toLowerCase();
      return hay.includes(q);
    });
  }, [openDocs, search, contactFilter]);

  const totalApplied = useMemo(
    () => Object.values(apps).reduce((s, v) => s + Number(v || 0), 0),
    [apps],
  );
  const remaining = +(txnAmt - totalApplied).toFixed(2);

  // Zeroing out auto-unchecks; otherwise clamp to open balance + room left.
  const setAppAmount = (d, value) => {
    const wanted = Math.max(0, Number(value || 0));
    if (wanted <= 0.005) {
      setApps(prev => { const n = { ...prev }; delete n[d.id]; return n; });
      return;
    }
    setApps(prev => {
      const otherUsed = Object.entries(prev).filter(([k]) => k !== d.id).reduce((s, [, v]) => s + Number(v || 0), 0);
      const roomLeft = Math.max(0, txnAmt - otherUsed);
      return { ...prev, [d.id]: +Math.min(Number(d.balance_due || 0), roomLeft, wanted).toFixed(2) };
    });
  };

  const toggleDoc = (d, checked) => {
    setApps(prev => {
      const next = { ...prev };
      if (checked) {
        const openBal = Number(d.balance_due || 0);
        const currentRemaining = +(txnAmt - Object.values(prev).reduce((s, v) => s + Number(v || 0), 0)).toFixed(2);
        // No room left → refuse the check-on. Frontend also disables
        // the checkbox when this happens (see fullyConsumed below),
        // but this second guard protects against keyboard toggles.
        if (currentRemaining <= 0.005) return prev;
        next[d.id] = +Math.min(openBal, Math.max(0, currentRemaining)).toFixed(2);
      } else {
        delete next[d.id];
      }
      return next;
    });
  };

  const applyPayment = async () => {
    if (Math.abs(remaining) > 0.02) {
      toast.error(`Applied ${fmtMoney(totalApplied)} but ${kind === "invoice" ? "deposit" : "withdrawal"} is ${fmtMoney(txnAmt)}. Adjust so they match.`);
      return;
    }
    const applications = Object.entries(apps)
      .filter(([, amt]) => Number(amt || 0) > 0.005)
      .map(([doc_id, amount]) => ({ [kind === "invoice" ? "invoice_id" : "bill_id"]: doc_id, amount: Number(amount) }));
    if (!applications.length) {
      toast.error(`Pick at least one ${kind}.`);
      return;
    }
    if (sampleDocs) {
      toast.message("Sample data — nothing is saved", { duration: 1800 });
      onClose();
      return;
    }
    setLoading(true);
    try {
      // Mar 2026 — unified multi-doc receive/pay. Both invoice
      // (deposit) and bill (withdrawal) applications go through
      // the same endpoint; the server auto-detects kind from the
      // application shape.
      const r = await _post(
        `/companies/${currentId}/transactions/${txn.id}/receive-payment`,
        { applications },
      );
      toast.success(`Applied to ${applications.length} ${kind}${applications.length > 1 ? "s" : ""}`);
      onApplied?.({ applications, ...(r?.data || {}) });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply");
    } finally { setLoading(false); }
  };

  return (
    <Modal title="Link transaction to invoice or bill" onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        <div className="flex gap-1 border-b pb-2">
          <button onClick={() => setKind("invoice")}
                   className={`px-3 py-1.5 rounded text-xs ${kind === "invoice" ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600"}`}
                   data-testid="link-modal-tab-invoice">Invoice</button>
          <button onClick={() => setKind("bill")}
                   className={`px-3 py-1.5 rounded text-xs ${kind === "bill" ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600"}`}
                   data-testid="link-modal-tab-bill">Bill</button>
        </div>

        {/* Search + customer quick-filter chips */}
        <div className="space-y-2">
          <input
            type="text" placeholder={`Search ${kind} #, ${kind === "invoice" ? "customer" : "vendor"}, amount…`}
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full border rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
            data-testid="link-modal-search"
          />
          {contactBuckets.length > 1 && (
            <div className="flex flex-wrap gap-1">
              <button onClick={() => setContactFilter("")}
                       className={`px-2 py-0.5 rounded-full text-[11px] border ${
                         !contactFilter
                           ? "bg-slate-900 text-white border-slate-900"
                           : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                       data-testid="link-modal-filter-all">
                All open ({openDocs.length})
              </button>
              {contactBuckets.slice(0, 6).map(b => (
                <button key={b.id || "none"} onClick={() => setContactFilter(b.id)}
                         className={`px-2 py-0.5 rounded-full text-[11px] border ${
                           contactFilter === b.id
                             ? "bg-slate-900 text-white border-slate-900"
                             : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                         data-testid={`link-modal-filter-${b.id || "none"}`}>
                  {b.name} ({b.count})
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Unified table — Invoice · Date · Customer · Original · Open · Apply */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-slate-400 italic">
            Sorted oldest first by issue date (FIFO) — matches standard AR aging & QBO
          </p>
          {Object.keys(apps).length > 0 && (
            <button
              onClick={() => setApps({})}
              className="text-[10px] text-slate-500 hover:text-slate-700 hover:underline"
              data-testid="link-modal-clear-all"
            >Clear auto-selection</button>
          )}
        </div>
        <div className="border rounded-md overflow-hidden max-h-80 overflow-y-auto">
          {/* Mobile: stacked cards — no horizontal scroll, amount box full-width */}
          <div className="md:hidden divide-y" data-testid="link-modal-cards">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-slate-400 text-xs italic">
                {search || contactFilter ? "No matches." : `No open ${kind}s to apply this deposit against.`}
              </div>
            )}
            {filtered.map(d => {
              const checked = d.id in apps;
              const fullyConsumed = remaining <= 0.005 && !checked;
              return (
                <div key={d.id} className={`px-3 py-3 ${checked ? "bg-emerald-50" : fullyConsumed ? "opacity-40" : ""}`} data-testid={`link-modal-card-${d.id}`}>
                  <label className="flex items-start gap-3">
                    <input type="checkbox" checked={checked} disabled={fullyConsumed}
                           onChange={(e) => toggleDoc(d, e.target.checked)}
                           className="mt-1 w-5 h-5" data-testid={`link-modal-check-m-${d.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono font-semibold text-sm">{d.number}</span>
                        <span className="text-xs text-slate-500 tabular-nums">{d.issue_date || "—"}</span>
                      </div>
                      <div className="text-sm text-slate-700 truncate">{d.contact_name || "—"}</div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 tabular-nums">
                        <span>Original <b className="text-slate-700 font-mono">{fmtMoney(d.total)}</b></span>
                        <span>Open <b className="text-slate-700 font-mono">{fmtMoney(d.balance_due)}</b></span>
                      </div>
                    </div>
                  </label>
                  {checked && (
                    <div className="mt-2 pl-8 flex items-center gap-2">
                      <label className="text-[11px] uppercase tracking-wide text-slate-500 shrink-0">Apply</label>
                      <input type="number" step="0.01" min="0" max={d.balance_due} inputMode="decimal"
                             value={apps[d.id]}
                             onChange={(e) => setAppAmount(d, e.target.value)}
                             className="flex-1 min-w-0 h-11 border rounded-lg px-3 text-right font-mono tabular-nums text-base"
                             data-testid={`link-modal-amt-m-${d.id}`} />
                      <button type="button" onClick={() => setAppAmount(d, d.balance_due)}
                              className="h-11 px-3 rounded-lg border text-xs font-semibold text-slate-700 shrink-0"
                              data-testid={`link-modal-full-m-${d.id}`}>Full</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5 w-8"></th>
                <th className="text-left px-2 py-1.5">{kind === "invoice" ? "Invoice" : "Bill"}</th>
                <th className="text-left px-2 py-1.5">Date</th>
                <th className="text-left px-2 py-1.5">{kind === "invoice" ? "Customer" : "Vendor"}</th>
                <th className="text-right px-2 py-1.5">Original</th>
                <th className="text-right px-2 py-1.5">Open</th>
                <th className="text-right px-2 py-1.5 w-28">Apply</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400 text-xs italic">
                  {search || contactFilter ? "No matches." : `No open ${kind}s to apply this deposit against.`}
                </td></tr>
              )}
              {filtered.map(d => {
                const checked = d.id in apps;
                const fullyConsumed = remaining <= 0.005 && !checked;
                return (
                  <tr key={d.id} className={
                    checked ? "bg-emerald-50"
                    : fullyConsumed ? "opacity-40" : ""}>
                    <td className="px-2 py-1.5">
                      <input type="checkbox" checked={checked}
                              disabled={fullyConsumed}
                              onChange={(e) => toggleDoc(d, e.target.checked)}
                              title={fullyConsumed ? "Deposit fully applied — no funds remaining" : ""}
                              data-testid={`link-modal-check-${d.id}`} />
                    </td>
                    <td className="px-2 py-1.5 font-mono">{d.number}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap tabular-nums"
                        title="FIFO order — oldest issue date is applied first">
                      {d.issue_date || "—"}
                    </td>
                    <td className="px-2 py-1.5 text-slate-700">{d.contact_name || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMoney(d.total)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{fmtMoney(d.balance_due)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" step="0.01" min="0" max={d.balance_due}
                              value={checked ? apps[d.id] : ""}
                              disabled={!checked}
                              onChange={(e) => setAppAmount(d, e.target.value)}
                              className="w-24 border rounded px-1.5 py-0.5 text-right font-mono tabular-nums disabled:bg-slate-50 disabled:text-slate-400"
                              data-testid={`link-modal-amt-${d.id}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-3 gap-3 items-center py-2 border-y bg-slate-50 rounded px-3">
          <div className="text-xs text-slate-500">{kind === "invoice" ? "Deposit" : "Withdrawal"} amount</div>
          <div className="text-xs text-slate-500 text-right">Applied</div>
          <div className="text-xs text-slate-500 text-right">Unapplied</div>
          <div className="font-mono tabular-nums font-semibold text-sm">{fmtMoney(txnAmt)}</div>
          <div className="font-mono tabular-nums font-semibold text-sm text-right">{fmtMoney(totalApplied)}</div>
          <div className={`font-mono tabular-nums font-semibold text-sm text-right ${
            Math.abs(remaining) < 0.02 ? "text-emerald-700" : "text-rose-600"
          }`}>{fmtMoney(remaining)}</div>
        </div>

        <button
          disabled={loading || Math.abs(remaining) > 0.02 || Object.values(apps).filter(v => Number(v || 0) > 0.005).length === 0}
          onClick={applyPayment}
          className="w-full py-2 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-50 hover:bg-emerald-700"
          data-testid={TID.saveBtn}
        >
          {loading ? "Applying…"
            : Math.abs(remaining) < 0.02
              ? `Apply to ${Object.values(apps).filter(v => Number(v || 0) > 0.005).length} ${kind}${Object.values(apps).filter(v => Number(v || 0) > 0.005).length > 1 ? "s" : ""}`
              : `Balance ${fmtMoney(remaining)} — adjust to match ${kind === "invoice" ? "deposit" : "withdrawal"}`}
        </button>
      </div>
    </Modal>
  );
}
